import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import * as React from 'react';
import { Button as MuiButton, createMuiTheme, ThemeProvider, IconButton as MuiIconButton, useTheme, Tooltip } from '@material-ui/core';
import deepmerge from 'deepmerge';
import Icon from '../Icon';
import { baseTheme, emphasisStyles } from '../Theme';
import { designTokens } from '../Theme.design-tokens';
var darkContainedPrimaryTheme = createMuiTheme(deepmerge(baseTheme, {
  palette: {
    primary: {
      main: designTokens.colors.blue500
    }
  }
}));
var darkOutlinedPrimaryTheme = createMuiTheme(deepmerge(baseTheme, {
  palette: {
    primary: {
      main: designTokens.colors.white
    }
  }
}));

var Button = function Button(_ref) {
  var a11yControls = _ref.a11yControls,
      a11yDescribedby = _ref.a11yDescribedby,
      a11yHaspopup = _ref.a11yHaspopup,
      a11yLabel = _ref.a11yLabel,
      body = _ref.body,
      buttonRef = _ref.buttonRef,
      children = _ref.children,
      dsOnClick = _ref.dsOnClick,
      e2e = _ref.e2e,
      getBodySlot = _ref.getBodySlot,
      disabled = _ref.disabled,
      href = _ref.href,
      startIcon = _ref.startIcon,
      endIcon = _ref.endIcon,
      icon = _ref.icon,
      size = _ref.size,
      _ref$tooltip = _ref.tooltip,
      tooltip = _ref$tooltip === void 0 ? '' : _ref$tooltip,
      type = _ref.type,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'contained' : _ref$variant;
  var bodyCb = React.useCallback(function (node) {
    getBodySlot && getBodySlot(node);
  }, [getBodySlot]);
  var theme = useTheme();
  var emphasis = emphasisStyles();
  var defaultButton = /*#__PURE__*/React.createElement(MuiButton, _extends({
    "aria-controls": a11yControls,
    "aria-describedby": a11yDescribedby,
    "aria-haspopup": a11yHaspopup,
    variant: variant,
    color: "primary",
    disabled: disabled
  }, e2e && {
    'data-e2e': e2e
  }, {
    onClick: dsOnClick,
    href: href,
    size: size,
    type: type,
    focusRipple: true,
    startIcon: startIcon && /*#__PURE__*/React.createElement(Icon, {
      color: "inherit",
      body: startIcon
    }),
    endIcon: endIcon && /*#__PURE__*/React.createElement(Icon, {
      color: "inherit",
      body: endIcon
    }),
    buttonRef: buttonRef
  }), /*#__PURE__*/React.createElement("span", _extends({
    ref: bodyCb
  }, e2e && {
    'data-e2e': "".concat(e2e, "-body")
  }), body, children));
  var iconButton = /*#__PURE__*/React.createElement(Tooltip, {
    arrow: true,
    title: tooltip,
    PopperProps: _objectSpread(_objectSpread({}, e2e && {
      'data-e2e': "".concat(e2e, "-tooltip")
    }), {}, {
      modifiers: {
        offset: {
          enabled: true,
          offset: "0, -".concat(theme.spacing(1))
        }
      }
    })
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(MuiIconButton, _extends({
    "aria-label": a11yLabel,
    className: emphasis.low,
    disabled: disabled
  }, e2e && {
    'data-e2e': e2e
  }, {
    onClick: dsOnClick
  }, href && {
    href: href
  }, {
    size: size,
    buttonRef: buttonRef
  }), /*#__PURE__*/React.createElement(Icon, {
    color: "inherit",
    body: icon
  }))));
  var button = icon ? iconButton : defaultButton;
  var isDark = theme.palette.type === 'dark';

  if (isDark && !disabled) {
    switch (variant) {
      case 'contained':
        {
          return /*#__PURE__*/React.createElement(ThemeProvider, {
            theme: darkContainedPrimaryTheme
          }, button);
        }

      default:
        {
          return /*#__PURE__*/React.createElement(ThemeProvider, {
            theme: darkOutlinedPrimaryTheme
          }, button);
        }
    }
  }

  return button;
};

Button.defaultProps = {
  variant: 'contained'
};
Button.displayName = 'Button';
export default Button;

try {
  Button.__docgenInfo = {
    description: '',
    displayName: 'Button',
    methods: [],
    props: {
      a11yControls: {
        defaultValue: null,
        description: 'If button invokes a dropdown, set to id of dropdown DOM node.',
        name: 'a11yControls',
        required: false,
        type: {
          name: 'string'
        }
      },
      a11yDescribedby: {
        defaultValue: null,
        description: 'If button invokes a popover, set to id of popover DOM node.',
        name: 'a11yDescribedby',
        required: false,
        type: {
          name: 'string'
        }
      },
      a11yHaspopup: {
        defaultValue: null,
        description: "if button invokes a dropdown, set to 'true'.",
        name: 'a11yHaspopup',
        required: false,
        type: {
          name: 'boolean | "dialog" | "menu" | "true" | "false" | "listbox" | "tree" | "grid"'
        }
      },
      a11yLabel: {
        defaultValue: null,
        description: 'Accessibility label (needed for Icon Button only)',
        name: 'a11yLabel',
        required: false,
        type: {
          name: 'string'
        }
      },
      body: {
        defaultValue: null,
        description: '',
        name: 'body',
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      dsOnClick: {
        defaultValue: null,
        description: '',
        name: 'dsOnClick',
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      e2e: {
        defaultValue: null,
        description: '',
        name: 'e2e',
        required: false,
        type: {
          name: 'string'
        }
      },
      getBodySlot: {
        defaultValue: null,
        description: '',
        name: 'getBodySlot',
        required: false,
        type: {
          name: '(elem: HTMLElement) => any'
        }
      },
      startIcon: {
        defaultValue: null,
        description: '',
        name: 'startIcon',
        required: false,
        type: {
          name: 'enum',
          raw: 'AllIconsType',
          value: [{
            value: '"style"'
          }, {
            value: '"title"'
          }, {
            value: '"translate"'
          }, {
            value: '"security"'
          }, {
            value: '"message"'
          }, {
            value: '"info"'
          }, {
            value: '"warning"'
          }, {
            value: '"error"'
          }, {
            value: '"label"'
          }, {
            value: '"article"'
          }, {
            value: '"code"'
          }, {
            value: '"details"'
          }, {
            value: '"input"'
          }, {
            value: '"link"'
          }, {
            value: '"map"'
          }, {
            value: '"menu"'
          }, {
            value: '"source"'
          }, {
            value: '"table"'
          }, {
            value: '"template"'
          }, {
            value: '"circle"'
          }, {
            value: '"filter"'
          }, {
            value: '"image"'
          }, {
            value: '"stop"'
          }, {
            value: '"email"'
          }, {
            value: '"search"'
          }, {
            value: '"drag_corner"'
          }, {
            value: '"exclude"'
          }, {
            value: '"export"'
          }, {
            value: '"filter_filled"'
          }, {
            value: '"import"'
          }, {
            value: '"list_remove"'
          }, {
            value: '"pin"'
          }, {
            value: '"pin_left"'
          }, {
            value: '"pin_right"'
          }, {
            value: '"unpin"'
          }, {
            value: '"bar_chart"'
          }, {
            value: '"donut_chart"'
          }, {
            value: '"line_chart"'
          }, {
            value: '"stacked_chart"'
          }, {
            value: '"file_chart"'
          }, {
            value: '"file_code"'
          }, {
            value: '"file_pdf"'
          }, {
            value: '"file_table"'
          }, {
            value: '"tree_view"'
          }, {
            value: '"advanced_search"'
          }, {
            value: '"asterisk"'
          }, {
            value: '"currency_exchange"'
          }, {
            value: '"import_export"'
          }, {
            value: '"knowledgebase"'
          }, {
            value: '"move_down"'
          }, {
            value: '"move_up"'
          }, {
            value: '"collapse_column"'
          }, {
            value: '"collapse_down"'
          }, {
            value: '"collapse_horiz"'
          }, {
            value: '"collapse_left"'
          }, {
            value: '"collapse_right"'
          }, {
            value: '"collapse_up"'
          }, {
            value: '"collapse_vert"'
          }, {
            value: '"expand_row"'
          }, {
            value: '"move"'
          }, {
            value: '"analytics"'
          }, {
            value: '"billing"'
          }, {
            value: '"collect"'
          }, {
            value: '"commerce"'
          }, {
            value: '"customers"'
          }, {
            value: '"finance"'
          }, {
            value: '"home"'
          }, {
            value: '"insights"'
          }, {
            value: '"marketplace"'
          }, {
            value: '"payments"'
          }, {
            value: '"platform"'
          }, {
            value: '"products"'
          }, {
            value: '"profile"'
          }, {
            value: '"reporting"'
          }, {
            value: '"revpro"'
          }, {
            value: '"workflow"'
          }, {
            value: '"column_group"'
          }, {
            value: '"fields"'
          }, {
            value: '"insert_row"'
          }, {
            value: '"remove_row"'
          }, {
            value: '"row_group"'
          }, {
            value: '"1k_plus"'
          }, {
            value: '"19mp"'
          }, {
            value: '"18mp"'
          }, {
            value: '"17mp"'
          }, {
            value: '"16mp"'
          }, {
            value: '"15mp"'
          }, {
            value: '"14mp"'
          }, {
            value: '"13mp"'
          }, {
            value: '"12mp"'
          }, {
            value: '"11mp"'
          }, {
            value: '"10mp"'
          }, {
            value: '"10k"'
          }, {
            value: '"1k"'
          }, {
            value: '"2k_plus"'
          }, {
            value: '"24mp"'
          }, {
            value: '"23mp"'
          }, {
            value: '"22mp"'
          }, {
            value: '"21mp"'
          }, {
            value: '"20mp"'
          }, {
            value: '"2mp"'
          }, {
            value: '"2k"'
          }, {
            value: '"3d_rotation"'
          }, {
            value: '"3k_plus"'
          }, {
            value: '"3mp"'
          }, {
            value: '"360"'
          }, {
            value: '"3k"'
          }, {
            value: '"4k_plus"'
          }, {
            value: '"4mp"'
          }, {
            value: '"4k"'
          }, {
            value: '"5mp"'
          }, {
            value: '"5k_plus"'
          }, {
            value: '"5k"'
          }, {
            value: '"5g"'
          }, {
            value: '"6mp"'
          }, {
            value: '"6k_plus"'
          }, {
            value: '"6k"'
          }, {
            value: '"6_ft_apart"'
          }, {
            value: '"7k_plus"'
          }, {
            value: '"7mp"'
          }, {
            value: '"7k"'
          }, {
            value: '"8k_plus"'
          }, {
            value: '"8mp"'
          }, {
            value: '"8k"'
          }, {
            value: '"9k_plus"'
          }, {
            value: '"9mp"'
          }, {
            value: '"9k"'
          }, {
            value: '"av_timer"'
          }, {
            value: '"autorenew"'
          }, {
            value: '"auto_stories"'
          }, {
            value: '"auto_fix_off"'
          }, {
            value: '"auto_fix_normal"'
          }, {
            value: '"auto_fix_high"'
          }, {
            value: '"auto_delete"'
          }, {
            value: '"auto_awesome_motion"'
          }, {
            value: '"auto_awesome_mosaic"'
          }, {
            value: '"auto_awesome"'
          }, {
            value: '"audiotrack"'
          }, {
            value: '"attractions"'
          }, {
            value: '"attachment"'
          }, {
            value: '"attach_money"'
          }, {
            value: '"attach_file"'
          }, {
            value: '"attach_email"'
          }, {
            value: '"atm"'
          }, {
            value: '"assistant_photo"'
          }, {
            value: '"assistant_navigation"'
          }, {
            value: '"assistant_direction"'
          }, {
            value: '"assistant"'
          }, {
            value: '"assignment_turned_in"'
          }, {
            value: '"assignment_returned"'
          }, {
            value: '"assignment_return"'
          }, {
            value: '"assignment_late"'
          }, {
            value: '"assignment_ind"'
          }, {
            value: '"assignment"'
          }, {
            value: '"assessment"'
          }, {
            value: '"aspect_ratio"'
          }, {
            value: '"art_track"'
          }, {
            value: '"arrow_upward"'
          }, {
            value: '"arrow_right_alt"'
          }, {
            value: '"arrow_right"'
          }, {
            value: '"arrow_left"'
          }, {
            value: '"arrow_forward_ios"'
          }, {
            value: '"arrow_forward"'
          }, {
            value: '"arrow_drop_up"'
          }, {
            value: '"arrow_drop_down_circle"'
          }, {
            value: '"arrow_drop_down"'
          }, {
            value: '"arrow_downward"'
          }, {
            value: '"arrow_circle_up"'
          }, {
            value: '"arrow_circle_down"'
          }, {
            value: '"arrow_back_ios"'
          }, {
            value: '"arrow_back"'
          }, {
            value: '"archive"'
          }, {
            value: '"architecture"'
          }, {
            value: '"apps"'
          }, {
            value: '"approval"'
          }, {
            value: '"app_settings_alt"'
          }, {
            value: '"app_registration"'
          }, {
            value: '"app_blocking"'
          }, {
            value: '"api"'
          }, {
            value: '"apartment"'
          }, {
            value: '"announcement"'
          }, {
            value: '"animation"'
          }, {
            value: '"android"'
          }, {
            value: '"anchor"'
          }, {
            value: '"amp_stories"'
          }, {
            value: '"alternate_email"'
          }, {
            value: '"alt_route"'
          }, {
            value: '"all_out"'
          }, {
            value: '"all_inclusive"'
          }, {
            value: '"all_inbox"'
          }, {
            value: '"album"'
          }, {
            value: '"alarm_on"'
          }, {
            value: '"alarm_off"'
          }, {
            value: '"alarm_add"'
          }, {
            value: '"alarm"'
          }, {
            value: '"airport_shuttle"'
          }, {
            value: '"airplay"'
          }, {
            value: '"airplanemode_on"'
          }, {
            value: '"airplanemode_off"'
          }, {
            value: '"airplanemode_inactive"'
          }, {
            value: '"airplanemode_active"'
          }, {
            value: '"airline_seat_recline_normal"'
          }, {
            value: '"airline_seat_recline_extra"'
          }, {
            value: '"airline_seat_legroom_reduced"'
          }, {
            value: '"airline_seat_legroom_normal"'
          }, {
            value: '"airline_seat_legroom_extra"'
          }, {
            value: '"airline_seat_individual_suite"'
          }, {
            value: '"airline_seat_flat_angled"'
          }, {
            value: '"airline_seat_flat"'
          }, {
            value: '"agriculture"'
          }, {
            value: '"admin_panel_settings"'
          }, {
            value: '"adjust"'
          }, {
            value: '"addchart"'
          }, {
            value: '"add_to_queue"'
          }, {
            value: '"add_to_photos"'
          }, {
            value: '"add_to_home_screen"'
          }, {
            value: '"add_to_drive"'
          }, {
            value: '"add_task"'
          }, {
            value: '"add_shopping_cart"'
          }, {
            value: '"add_road"'
          }, {
            value: '"add_photo_alternate"'
          }, {
            value: '"add_moderator"'
          }, {
            value: '"add_location_alt"'
          }, {
            value: '"add_location"'
          }, {
            value: '"add_link"'
          }, {
            value: '"add_ic_call"'
          }, {
            value: '"add_comment"'
          }, {
            value: '"add_circle_outline"'
          }, {
            value: '"add_circle"'
          }, {
            value: '"add_chart"'
          }, {
            value: '"add_call"'
          }, {
            value: '"add_business"'
          }, {
            value: '"add_box"'
          }, {
            value: '"add_alert"'
          }, {
            value: '"add_alarm"'
          }, {
            value: '"add_a_photo"'
          }, {
            value: '"add"'
          }, {
            value: '"adb"'
          }, {
            value: '"ad_units"'
          }, {
            value: '"account_tree"'
          }, {
            value: '"account_circle"'
          }, {
            value: '"account_box"'
          }, {
            value: '"account_balance_wallet"'
          }, {
            value: '"account_balance"'
          }, {
            value: '"accessible_forward"'
          }, {
            value: '"accessible"'
          }, {
            value: '"accessibility_new"'
          }, {
            value: '"accessibility"'
          }, {
            value: '"access_time"'
          }, {
            value: '"access_alarms"'
          }, {
            value: '"access_alarm"'
          }, {
            value: '"ac_unit"'
          }, {
            value: '"business_center"'
          }, {
            value: '"business"'
          }, {
            value: '"bus_alert"'
          }, {
            value: '"burst_mode"'
          }, {
            value: '"build_circle"'
          }, {
            value: '"build"'
          }, {
            value: '"bug_report"'
          }, {
            value: '"bubble_chart"'
          }, {
            value: '"brush"'
          }, {
            value: '"brunch_dining"'
          }, {
            value: '"browser_not_supported"'
          }, {
            value: '"broken_image"'
          }, {
            value: '"brightness_medium"'
          }, {
            value: '"brightness_low"'
          }, {
            value: '"brightness_high"'
          }, {
            value: '"brightness_auto"'
          }, {
            value: '"brightness_7"'
          }, {
            value: '"brightness_6"'
          }, {
            value: '"brightness_5"'
          }, {
            value: '"brightness_4"'
          }, {
            value: '"brightness_3"'
          }, {
            value: '"brightness_2"'
          }, {
            value: '"brightness_1"'
          }, {
            value: '"breakfast_dining"'
          }, {
            value: '"branding_watermark"'
          }, {
            value: '"border_vertical"'
          }, {
            value: '"border_top"'
          }, {
            value: '"border_style"'
          }, {
            value: '"border_right"'
          }, {
            value: '"border_outer"'
          }, {
            value: '"border_left"'
          }, {
            value: '"border_inner"'
          }, {
            value: '"border_horizontal"'
          }, {
            value: '"border_color"'
          }, {
            value: '"border_clear"'
          }, {
            value: '"border_bottom"'
          }, {
            value: '"border_all"'
          }, {
            value: '"bookmarks"'
          }, {
            value: '"bookmark_outline"'
          }, {
            value: '"bookmark_border"'
          }, {
            value: '"bookmark"'
          }, {
            value: '"book_online"'
          }, {
            value: '"book"'
          }, {
            value: '"bolt"'
          }, {
            value: '"blur_on"'
          }, {
            value: '"blur_off"'
          }, {
            value: '"blur_linear"'
          }, {
            value: '"blur_circular"'
          }, {
            value: '"bluetooth_searching"'
          }, {
            value: '"bluetooth_disabled"'
          }, {
            value: '"bluetooth_connected"'
          }, {
            value: '"bluetooth_audio"'
          }, {
            value: '"bluetooth"'
          }, {
            value: '"block_flipped"'
          }, {
            value: '"block"'
          }, {
            value: '"biotech"'
          }, {
            value: '"bike_scooter"'
          }, {
            value: '"bento"'
          }, {
            value: '"beenhere"'
          }, {
            value: '"bedtime"'
          }, {
            value: '"beach_access"'
          }, {
            value: '"battery_unknown"'
          }, {
            value: '"battery_std"'
          }, {
            value: '"battery_full"'
          }, {
            value: '"battery_charging_full"'
          }, {
            value: '"battery_alert"'
          }, {
            value: '"bathtub"'
          }, {
            value: '"batch_prediction"'
          }, {
            value: '"ballot"'
          }, {
            value: '"bakery_dining"'
          }, {
            value: '"badge"'
          }, {
            value: '"backup_table"'
          }, {
            value: '"backup"'
          }, {
            value: '"backspace"'
          }, {
            value: '"backpack"'
          }, {
            value: '"baby_changing_station"'
          }, {
            value: '"crop_square"'
          }, {
            value: '"crop_rotate"'
          }, {
            value: '"crop_portrait"'
          }, {
            value: '"crop_original"'
          }, {
            value: '"crop_landscape"'
          }, {
            value: '"crop_free"'
          }, {
            value: '"crop_din"'
          }, {
            value: '"crop_7_5"'
          }, {
            value: '"crop_5_4"'
          }, {
            value: '"crop_3_2"'
          }, {
            value: '"crop_16_9"'
          }, {
            value: '"crop"'
          }, {
            value: '"credit_card"'
          }, {
            value: '"create_new_folder"'
          }, {
            value: '"create"'
          }, {
            value: '"countertops"'
          }, {
            value: '"corporate_fare"'
          }, {
            value: '"coronavirus"'
          }, {
            value: '"copyright"'
          }, {
            value: '"control_point_duplicate"'
          }, {
            value: '"control_point"'
          }, {
            value: '"control_camera"'
          }, {
            value: '"content_paste"'
          }, {
            value: '"content_cut"'
          }, {
            value: '"content_copy"'
          }, {
            value: '"contacts"'
          }, {
            value: '"contactless"'
          }, {
            value: '"contact_support"'
          }, {
            value: '"contact_phone"'
          }, {
            value: '"contact_page"'
          }, {
            value: '"contact_mail"'
          }, {
            value: '"construction"'
          }, {
            value: '"connected_tv"'
          }, {
            value: '"connect_without_contact"'
          }, {
            value: '"confirmation_number"'
          }, {
            value: '"confirmation_num"'
          }, {
            value: '"computer"'
          }, {
            value: '"compress"'
          }, {
            value: '"compass_calibration"'
          }, {
            value: '"compare_arrows"'
          }, {
            value: '"compare"'
          }, {
            value: '"commute"'
          }, {
            value: '"comment_bank"'
          }, {
            value: '"comment"'
          }, {
            value: '"colorize"'
          }, {
            value: '"color_lens"'
          }, {
            value: '"collections_bookmark"'
          }, {
            value: '"collections"'
          }, {
            value: '"cloud_upload"'
          }, {
            value: '"cloud_queue"'
          }, {
            value: '"cloud_off"'
          }, {
            value: '"cloud_download"'
          }, {
            value: '"cloud_done"'
          }, {
            value: '"cloud_circle"'
          }, {
            value: '"cloud"'
          }, {
            value: '"closed_caption_off"'
          }, {
            value: '"closed_caption_disabled"'
          }, {
            value: '"closed_caption"'
          }, {
            value: '"close_fullscreen"'
          }, {
            value: '"close"'
          }, {
            value: '"clear_all"'
          }, {
            value: '"clear"'
          }, {
            value: '"cleaning_services"'
          }, {
            value: '"clean_hands"'
          }, {
            value: '"class"'
          }, {
            value: '"circle_notifications"'
          }, {
            value: '"chrome_reader_mode"'
          }, {
            value: '"child_friendly"'
          }, {
            value: '"child_care"'
          }, {
            value: '"chevron_right"'
          }, {
            value: '"chevron_left"'
          }, {
            value: '"checkroom"'
          }, {
            value: '"check_circle_outline"'
          }, {
            value: '"check_circle"'
          }, {
            value: '"check_box_outline_blank"'
          }, {
            value: '"check_box"'
          }, {
            value: '"check"'
          }, {
            value: '"chat_bubble_outline"'
          }, {
            value: '"chat_bubble"'
          }, {
            value: '"chat"'
          }, {
            value: '"charging_station"'
          }, {
            value: '"change_history"'
          }, {
            value: '"center_focus_weak"'
          }, {
            value: '"center_focus_strong"'
          }, {
            value: '"cell_wifi"'
          }, {
            value: '"celebration"'
          }, {
            value: '"category"'
          }, {
            value: '"cast_for_education"'
          }, {
            value: '"cast_connected"'
          }, {
            value: '"cast"'
          }, {
            value: '"casino"'
          }, {
            value: '"cases"'
          }, {
            value: '"carpenter"'
          }, {
            value: '"card_travel"'
          }, {
            value: '"card_membership"'
          }, {
            value: '"card_giftcard"'
          }, {
            value: '"car_repair"'
          }, {
            value: '"car_rental"'
          }, {
            value: '"cancel_schedule_send"'
          }, {
            value: '"cancel_presentation"'
          }, {
            value: '"cancel"'
          }, {
            value: '"campaign"'
          }, {
            value: '"camera_roll"'
          }, {
            value: '"camera_rear"'
          }, {
            value: '"camera_front"'
          }, {
            value: '"camera_enhance"'
          }, {
            value: '"camera_alt"'
          }, {
            value: '"camera"'
          }, {
            value: '"call_to_action"'
          }, {
            value: '"call_split"'
          }, {
            value: '"call_received"'
          }, {
            value: '"call_missed_outgoing"'
          }, {
            value: '"call_missed"'
          }, {
            value: '"call_merge"'
          }, {
            value: '"call_made"'
          }, {
            value: '"call_end"'
          }, {
            value: '"call"'
          }, {
            value: '"calendar_view_day"'
          }, {
            value: '"calendar_today"'
          }, {
            value: '"calculate"'
          }, {
            value: '"cake"'
          }, {
            value: '"cached"'
          }, {
            value: '"dynamic_form"'
          }, {
            value: '"dynamic_feed"'
          }, {
            value: '"dvr"'
          }, {
            value: '"duo"'
          }, {
            value: '"dry_cleaning"'
          }, {
            value: '"dry"'
          }, {
            value: '"drive_folder_upload"'
          }, {
            value: '"drive_file_rename_outline"'
          }, {
            value: '"drive_file_move_outline"'
          }, {
            value: '"drive_file_move"'
          }, {
            value: '"drive_eta"'
          }, {
            value: '"drag_indicator"'
          }, {
            value: '"drag_handle"'
          }, {
            value: '"drafts"'
          }, {
            value: '"double_arrow"'
          }, {
            value: '"donut_small"'
          }, {
            value: '"donut_large"'
          }, {
            value: '"done_outline"'
          }, {
            value: '"done_all"'
          }, {
            value: '"done"'
          }, {
            value: '"domain_verification"'
          }, {
            value: '"domain_disabled"'
          }, {
            value: '"domain"'
          }, {
            value: '"dock"'
          }, {
            value: '"do_not_touch"'
          }, {
            value: '"do_not_step"'
          }, {
            value: '"do_not_disturb_on"'
          }, {
            value: '"do_not_disturb_off"'
          }, {
            value: '"do_not_disturb_alt"'
          }, {
            value: '"do_not_disturb"'
          }, {
            value: '"dns"'
          }, {
            value: '"dnd_forwardslash"'
          }, {
            value: '"disc_full"'
          }, {
            value: '"disabled_by_default"'
          }, {
            value: '"dirty_lens"'
          }, {
            value: '"directions_walk"'
          }, {
            value: '"directions_transit"'
          }, {
            value: '"directions_train"'
          }, {
            value: '"directions_subway"'
          }, {
            value: '"directions_run"'
          }, {
            value: '"directions_railway"'
          }, {
            value: '"directions_off"'
          }, {
            value: '"directions_ferry"'
          }, {
            value: '"directions_car"'
          }, {
            value: '"directions_bus"'
          }, {
            value: '"directions_boat"'
          }, {
            value: '"directions_bike"'
          }, {
            value: '"directions"'
          }, {
            value: '"dinner_dining"'
          }, {
            value: '"dialpad"'
          }, {
            value: '"dialer_sip"'
          }, {
            value: '"devices_other"'
          }, {
            value: '"devices"'
          }, {
            value: '"device_unknown"'
          }, {
            value: '"device_thermostat"'
          }, {
            value: '"device_hub"'
          }, {
            value: '"developer_mode"'
          }, {
            value: '"developer_board"'
          }, {
            value: '"desktop_windows"'
          }, {
            value: '"desktop_mac"'
          }, {
            value: '"desktop_access_disabled"'
          }, {
            value: '"design_services"'
          }, {
            value: '"description"'
          }, {
            value: '"departure_board"'
          }, {
            value: '"delivery_dining"'
          }, {
            value: '"delete_sweep"'
          }, {
            value: '"delete_outline"'
          }, {
            value: '"delete_forever"'
          }, {
            value: '"delete"'
          }, {
            value: '"dehaze"'
          }, {
            value: '"deck"'
          }, {
            value: '"date_range"'
          }, {
            value: '"data_usage"'
          }, {
            value: '"dashboard_customize"'
          }, {
            value: '"dashboard"'
          }, {
            value: '"dangerous"'
          }, {
            value: '"extension"'
          }, {
            value: '"exposure_zero"'
          }, {
            value: '"exposure_plus_2"'
          }, {
            value: '"exposure_plus_1"'
          }, {
            value: '"exposure_neg_2"'
          }, {
            value: '"exposure_neg_1"'
          }, {
            value: '"exposure_minus_2"'
          }, {
            value: '"exposure_minus_1"'
          }, {
            value: '"exposure"'
          }, {
            value: '"explore_off"'
          }, {
            value: '"explore"'
          }, {
            value: '"explicit"'
          }, {
            value: '"expand_more"'
          }, {
            value: '"expand_less"'
          }, {
            value: '"expand"'
          }, {
            value: '"exit_to_app"'
          }, {
            value: '"event_seat"'
          }, {
            value: '"event_note"'
          }, {
            value: '"event_busy"'
          }, {
            value: '"event_available"'
          }, {
            value: '"event"'
          }, {
            value: '"ev_station"'
          }, {
            value: '"euro_symbol"'
          }, {
            value: '"euro"'
          }, {
            value: '"escalator_warning"'
          }, {
            value: '"escalator"'
          }, {
            value: '"error_outline"'
          }, {
            value: '"equalizer"'
          }, {
            value: '"enhanced_encryption"'
          }, {
            value: '"enhance_photo_translate"'
          }, {
            value: '"engineering"'
          }, {
            value: '"emoji_transportation"'
          }, {
            value: '"emoji_symbols"'
          }, {
            value: '"emoji_people"'
          }, {
            value: '"emoji_objects"'
          }, {
            value: '"emoji_nature"'
          }, {
            value: '"emoji_food_beverage"'
          }, {
            value: '"emoji_flags"'
          }, {
            value: '"emoji_events"'
          }, {
            value: '"emoji_emotions"'
          }, {
            value: '"elevator"'
          }, {
            value: '"electrical_services"'
          }, {
            value: '"electric_scooter"'
          }, {
            value: '"electric_rickshaw"'
          }, {
            value: '"electric_moped"'
          }, {
            value: '"electric_car"'
          }, {
            value: '"electric_bike"'
          }, {
            value: '"elderly"'
          }, {
            value: '"eject"'
          }, {
            value: '"edit_road"'
          }, {
            value: '"edit_off"'
          }, {
            value: '"edit_location"'
          }, {
            value: '"edit_attributes"'
          }, {
            value: '"edit"'
          }, {
            value: '"eco"'
          }, {
            value: '"east"'
          }, {
            value: '"functions"'
          }, {
            value: '"fullscreen_exit"'
          }, {
            value: '"fullscreen"'
          }, {
            value: '"free_breakfast"'
          }, {
            value: '"foundation"'
          }, {
            value: '"forward_to_inbox"'
          }, {
            value: '"forward_5"'
          }, {
            value: '"forward_30"'
          }, {
            value: '"forward_10"'
          }, {
            value: '"forward"'
          }, {
            value: '"forum"'
          }, {
            value: '"format_underlined"'
          }, {
            value: '"format_underline"'
          }, {
            value: '"format_textdirection_r_to_l"'
          }, {
            value: '"format_textdirection_l_to_r"'
          }, {
            value: '"format_strikethrough"'
          }, {
            value: '"format_size"'
          }, {
            value: '"format_shapes"'
          }, {
            value: '"format_quote"'
          }, {
            value: '"format_paint"'
          }, {
            value: '"format_list_numbered_rtl"'
          }, {
            value: '"format_list_numbered"'
          }, {
            value: '"format_list_bulleted"'
          }, {
            value: '"format_line_spacing"'
          }, {
            value: '"format_italic"'
          }, {
            value: '"format_indent_increase"'
          }, {
            value: '"format_indent_decrease"'
          }, {
            value: '"format_color_text"'
          }, {
            value: '"format_color_reset"'
          }, {
            value: '"format_color_fill"'
          }, {
            value: '"format_clear"'
          }, {
            value: '"format_bold"'
          }, {
            value: '"format_align_right"'
          }, {
            value: '"format_align_left"'
          }, {
            value: '"format_align_justify"'
          }, {
            value: '"format_align_center"'
          }, {
            value: '"food_bank"'
          }, {
            value: '"font_download"'
          }, {
            value: '"follow_the_signs"'
          }, {
            value: '"folder_special"'
          }, {
            value: '"folder_shared"'
          }, {
            value: '"folder_open"'
          }, {
            value: '"folder"'
          }, {
            value: '"flip_to_front"'
          }, {
            value: '"flip_to_back"'
          }, {
            value: '"flip_camera_ios"'
          }, {
            value: '"flip_camera_android"'
          }, {
            value: '"flip"'
          }, {
            value: '"flight_takeoff"'
          }, {
            value: '"flight_land"'
          }, {
            value: '"flight"'
          }, {
            value: '"flash_on"'
          }, {
            value: '"flash_off"'
          }, {
            value: '"flash_auto"'
          }, {
            value: '"flare"'
          }, {
            value: '"flaky"'
          }, {
            value: '"flag"'
          }, {
            value: '"fitness_center"'
          }, {
            value: '"fit_screen"'
          }, {
            value: '"first_page"'
          }, {
            value: '"fireplace"'
          }, {
            value: '"fire_hydrant"'
          }, {
            value: '"fire_extinguisher"'
          }, {
            value: '"fingerprint"'
          }, {
            value: '"find_replace"'
          }, {
            value: '"find_in_page"'
          }, {
            value: '"filter_vintage"'
          }, {
            value: '"filter_tilt_shift"'
          }, {
            value: '"filter_none"'
          }, {
            value: '"filter_list_alt"'
          }, {
            value: '"filter_list"'
          }, {
            value: '"filter_hdr"'
          }, {
            value: '"filter_frames"'
          }, {
            value: '"filter_drama"'
          }, {
            value: '"filter_center_focus"'
          }, {
            value: '"filter_b_and_w"'
          }, {
            value: '"filter_alt"'
          }, {
            value: '"filter_9_plus"'
          }, {
            value: '"filter_9"'
          }, {
            value: '"filter_8"'
          }, {
            value: '"filter_7"'
          }, {
            value: '"filter_6"'
          }, {
            value: '"filter_5"'
          }, {
            value: '"filter_4"'
          }, {
            value: '"filter_3"'
          }, {
            value: '"filter_2"'
          }, {
            value: '"filter_1"'
          }, {
            value: '"file_upload"'
          }, {
            value: '"file_present"'
          }, {
            value: '"file_download_done"'
          }, {
            value: '"file_download"'
          }, {
            value: '"file_copy"'
          }, {
            value: '"fiber_smart_record"'
          }, {
            value: '"fiber_pin"'
          }, {
            value: '"fiber_new"'
          }, {
            value: '"fiber_manual_record"'
          }, {
            value: '"fiber_dvr"'
          }, {
            value: '"festival"'
          }, {
            value: '"fence"'
          }, {
            value: '"feedback"'
          }, {
            value: '"featured_video"'
          }, {
            value: '"featured_play_list"'
          }, {
            value: '"favorite_outline"'
          }, {
            value: '"favorite_border"'
          }, {
            value: '"favorite"'
          }, {
            value: '"fastfood"'
          }, {
            value: '"fast_rewind"'
          }, {
            value: '"fast_forward"'
          }, {
            value: '"family_restroom"'
          }, {
            value: '"fact_check"'
          }, {
            value: '"facebook"'
          }, {
            value: '"face_retouching_natural"'
          }, {
            value: '"face"'
          }, {
            value: '"groups"'
          }, {
            value: '"group_work"'
          }, {
            value: '"group_add"'
          }, {
            value: '"group"'
          }, {
            value: '"grid_view"'
          }, {
            value: '"grid_on"'
          }, {
            value: '"grid_off"'
          }, {
            value: '"grass"'
          }, {
            value: '"graphic_eq"'
          }, {
            value: '"grain"'
          }, {
            value: '"grading"'
          }, {
            value: '"gradient"'
          }, {
            value: '"grade"'
          }, {
            value: '"gps_off"'
          }, {
            value: '"gps_not_fixed"'
          }, {
            value: '"gps_fixed"'
          }, {
            value: '"golf_course"'
          }, {
            value: '"goat"'
          }, {
            value: '"gif"'
          }, {
            value: '"get_app"'
          }, {
            value: '"gesture"'
          }, {
            value: '"gavel"'
          }, {
            value: '"games"'
          }, {
            value: '"gamepad"'
          }, {
            value: '"g_translate"'
          }, {
            value: '"hvac"'
          }, {
            value: '"https"'
          }, {
            value: '"http"'
          }, {
            value: '"how_to_vote"'
          }, {
            value: '"how_to_reg"'
          }, {
            value: '"house_siding"'
          }, {
            value: '"house"'
          }, {
            value: '"hourglass_top"'
          }, {
            value: '"hourglass_full"'
          }, {
            value: '"hourglass_empty"'
          }, {
            value: '"hourglass_disabled"'
          }, {
            value: '"hourglass_bottom"'
          }, {
            value: '"hotel"'
          }, {
            value: '"hot_tub"'
          }, {
            value: '"horizontal_split"'
          }, {
            value: '"horizontal_rule"'
          }, {
            value: '"home_work"'
          }, {
            value: '"home_repair_service"'
          }, {
            value: '"home_filled"'
          }, {
            value: '"history_toggle_off"'
          }, {
            value: '"history_edu"'
          }, {
            value: '"history"'
          }, {
            value: '"highlight_remove"'
          }, {
            value: '"highlight_off"'
          }, {
            value: '"highlight_alt"'
          }, {
            value: '"highlight"'
          }, {
            value: '"high_quality"'
          }, {
            value: '"help_outline"'
          }, {
            value: '"help_center"'
          }, {
            value: '"help"'
          }, {
            value: '"height"'
          }, {
            value: '"hearing_disabled"'
          }, {
            value: '"hearing"'
          }, {
            value: '"healing"'
          }, {
            value: '"headset_off"'
          }, {
            value: '"headset_mic"'
          }, {
            value: '"headset"'
          }, {
            value: '"hdr_weak"'
          }, {
            value: '"hdr_strong"'
          }, {
            value: '"hdr_on"'
          }, {
            value: '"hdr_off"'
          }, {
            value: '"hdr_enhanced_select"'
          }, {
            value: '"hd"'
          }, {
            value: '"hardware"'
          }, {
            value: '"handyman"'
          }, {
            value: '"hail"'
          }, {
            value: '"iso"'
          }, {
            value: '"ios_share"'
          }, {
            value: '"invert_colors_on"'
          }, {
            value: '"invert_colors_off"'
          }, {
            value: '"invert_colors"'
          }, {
            value: '"inventory"'
          }, {
            value: '"integration_instructions"'
          }, {
            value: '"insert_photo"'
          }, {
            value: '"insert_link"'
          }, {
            value: '"insert_invitation"'
          }, {
            value: '"insert_emoticon"'
          }, {
            value: '"insert_drive_file"'
          }, {
            value: '"insert_comment"'
          }, {
            value: '"insert_chart_outlined"'
          }, {
            value: '"insert_chart"'
          }, {
            value: '"info_outline"'
          }, {
            value: '"indeterminate_check_box"'
          }, {
            value: '"inbox"'
          }, {
            value: '"important_devices"'
          }, {
            value: '"import_contacts"'
          }, {
            value: '"imagesearch_roller"'
          }, {
            value: '"image_search"'
          }, {
            value: '"image_not_supported"'
          }, {
            value: '"image_aspect_ratio"'
          }, {
            value: '"icecream"'
          }, {
            value: '"keyboard_arrow_right"'
          }, {
            value: '"keyboard_arrow_left"'
          }, {
            value: '"keyboard_arrow_down"'
          }, {
            value: '"keyboard_backspace"'
          }, {
            value: '"keyboard_arrow_up"'
          }, {
            value: '"keyboard_capslock"'
          }, {
            value: '"keyboard_control"'
          }, {
            value: '"keyboard_return"'
          }, {
            value: '"keyboard_voice"'
          }, {
            value: '"keyboard_hide"'
          }, {
            value: '"keyboard_tab"'
          }, {
            value: '"king_bed"'
          }, {
            value: '"keyboard"'
          }, {
            value: '"kitchen"'
          }, {
            value: '"lunch_dining"'
          }, {
            value: '"luggage"'
          }, {
            value: '"loyalty"'
          }, {
            value: '"low_priority"'
          }, {
            value: '"loupe"'
          }, {
            value: '"loop"'
          }, {
            value: '"looks_two"'
          }, {
            value: '"looks_one"'
          }, {
            value: '"looks_6"'
          }, {
            value: '"looks_5"'
          }, {
            value: '"looks_4"'
          }, {
            value: '"looks_3"'
          }, {
            value: '"looks"'
          }, {
            value: '"logout"'
          }, {
            value: '"login"'
          }, {
            value: '"lock_outline"'
          }, {
            value: '"lock_open"'
          }, {
            value: '"lock_clock"'
          }, {
            value: '"lock"'
          }, {
            value: '"location_searching"'
          }, {
            value: '"location_pin"'
          }, {
            value: '"location_on"'
          }, {
            value: '"location_off"'
          }, {
            value: '"location_history"'
          }, {
            value: '"location_disabled"'
          }, {
            value: '"location_city"'
          }, {
            value: '"local_taxi"'
          }, {
            value: '"local_shipping"'
          }, {
            value: '"local_see"'
          }, {
            value: '"local_restaurant"'
          }, {
            value: '"local_printshop"'
          }, {
            value: '"local_print_shop"'
          }, {
            value: '"local_post_office"'
          }, {
            value: '"local_police"'
          }, {
            value: '"local_play"'
          }, {
            value: '"local_pizza"'
          }, {
            value: '"local_phone"'
          }, {
            value: '"local_pharmacy"'
          }, {
            value: '"local_parking"'
          }, {
            value: '"local_offer"'
          }, {
            value: '"local_movies"'
          }, {
            value: '"local_mall"'
          }, {
            value: '"local_library"'
          }, {
            value: '"local_laundry_service"'
          }, {
            value: '"local_hotel"'
          }, {
            value: '"local_hospital"'
          }, {
            value: '"local_grocery_store"'
          }, {
            value: '"local_gas_station"'
          }, {
            value: '"local_florist"'
          }, {
            value: '"local_fire_department"'
          }, {
            value: '"local_drink"'
          }, {
            value: '"local_dining"'
          }, {
            value: '"local_convenience_store"'
          }, {
            value: '"local_car_wash"'
          }, {
            value: '"local_cafe"'
          }, {
            value: '"local_bar"'
          }, {
            value: '"local_attraction"'
          }, {
            value: '"local_atm"'
          }, {
            value: '"local_airport"'
          }, {
            value: '"local_activity"'
          }, {
            value: '"live_tv"'
          }, {
            value: '"live_help"'
          }, {
            value: '"list_alt"'
          }, {
            value: '"list"'
          }, {
            value: '"liquor"'
          }, {
            value: '"linked_camera"'
          }, {
            value: '"link_off"'
          }, {
            value: '"linear_scale"'
          }, {
            value: '"line_weight"'
          }, {
            value: '"line_style"'
          }, {
            value: '"lightbulb_outline"'
          }, {
            value: '"lightbulb"'
          }, {
            value: '"library_music"'
          }, {
            value: '"library_books"'
          }, {
            value: '"library_add_check"'
          }, {
            value: '"library_add"'
          }, {
            value: '"lens"'
          }, {
            value: '"legend_toggle"'
          }, {
            value: '"leave_bags_at_home"'
          }, {
            value: '"leak_remove"'
          }, {
            value: '"leak_add"'
          }, {
            value: '"leaderboard"'
          }, {
            value: '"layers_clear"'
          }, {
            value: '"layers"'
          }, {
            value: '"launch"'
          }, {
            value: '"last_page"'
          }, {
            value: '"laptop_windows"'
          }, {
            value: '"laptop_mac"'
          }, {
            value: '"laptop_chromebook"'
          }, {
            value: '"laptop"'
          }, {
            value: '"language"'
          }, {
            value: '"landscape"'
          }, {
            value: '"label_outline"'
          }, {
            value: '"label_off"'
          }, {
            value: '"label_important_outline"'
          }, {
            value: '"label_important"'
          }, {
            value: '"my_location"'
          }, {
            value: '"my_library_music"'
          }, {
            value: '"my_library_books"'
          }, {
            value: '"my_library_add"'
          }, {
            value: '"music_video"'
          }, {
            value: '"music_off"'
          }, {
            value: '"music_note"'
          }, {
            value: '"museum"'
          }, {
            value: '"multitrack_audio"'
          }, {
            value: '"multiple_stop"'
          }, {
            value: '"multiline_chart"'
          }, {
            value: '"mp"'
          }, {
            value: '"movie_filter"'
          }, {
            value: '"movie_creation"'
          }, {
            value: '"movie"'
          }, {
            value: '"move_to_inbox"'
          }, {
            value: '"mouse"'
          }, {
            value: '"motorcycle"'
          }, {
            value: '"motion_photos_paused"'
          }, {
            value: '"motion_photos_pause"'
          }, {
            value: '"motion_photos_on"'
          }, {
            value: '"motion_photos_off"'
          }, {
            value: '"more_vert"'
          }, {
            value: '"more_time"'
          }, {
            value: '"more_horiz"'
          }, {
            value: '"more"'
          }, {
            value: '"moped"'
          }, {
            value: '"mood_bad"'
          }, {
            value: '"mood"'
          }, {
            value: '"monochrome_photos"'
          }, {
            value: '"monitor"'
          }, {
            value: '"money_off"'
          }, {
            value: '"money"'
          }, {
            value: '"monetization_on"'
          }, {
            value: '"model_training"'
          }, {
            value: '"mode_edit"'
          }, {
            value: '"mode_comment"'
          }, {
            value: '"mobile_screen_share"'
          }, {
            value: '"mobile_off"'
          }, {
            value: '"mobile_friendly"'
          }, {
            value: '"mms"'
          }, {
            value: '"missed_video_call"'
          }, {
            value: '"miscellaneous_services"'
          }, {
            value: '"minimize"'
          }, {
            value: '"military_tech"'
          }, {
            value: '"microwave"'
          }, {
            value: '"mic_off"'
          }, {
            value: '"mic_none"'
          }, {
            value: '"mic_external_on"'
          }, {
            value: '"mic_external_off"'
          }, {
            value: '"mic"'
          }, {
            value: '"messenger_outline"'
          }, {
            value: '"messenger"'
          }, {
            value: '"merge_type"'
          }, {
            value: '"menu_open"'
          }, {
            value: '"menu_book"'
          }, {
            value: '"memory"'
          }, {
            value: '"meeting_room"'
          }, {
            value: '"medical_services"'
          }, {
            value: '"mediation"'
          }, {
            value: '"maximize"'
          }, {
            value: '"masks"'
          }, {
            value: '"markunread_mailbox"'
          }, {
            value: '"markunread"'
          }, {
            value: '"mark_email_unread"'
          }, {
            value: '"mark_email_read"'
          }, {
            value: '"mark_chat_unread"'
          }, {
            value: '"mark_chat_read"'
          }, {
            value: '"mark_as_unread"'
          }, {
            value: '"margin"'
          }, {
            value: '"maps_ugc"'
          }, {
            value: '"mail_outline"'
          }, {
            value: '"mail"'
          }, {
            value: '"now_widgets"'
          }, {
            value: '"now_wallpaper"'
          }, {
            value: '"notifications_paused"'
          }, {
            value: '"notifications_on"'
          }, {
            value: '"notifications_off"'
          }, {
            value: '"notifications_none"'
          }, {
            value: '"notifications_active"'
          }, {
            value: '"notifications"'
          }, {
            value: '"notification_important"'
          }, {
            value: '"notes"'
          }, {
            value: '"note_add"'
          }, {
            value: '"note"'
          }, {
            value: '"not_started"'
          }, {
            value: '"not_listed_location"'
          }, {
            value: '"not_interested"'
          }, {
            value: '"not_accessible"'
          }, {
            value: '"north_west"'
          }, {
            value: '"north_east"'
          }, {
            value: '"north"'
          }, {
            value: '"no_transfer"'
          }, {
            value: '"no_stroller"'
          }, {
            value: '"no_sim"'
          }, {
            value: '"no_photography"'
          }, {
            value: '"no_meeting_room"'
          }, {
            value: '"no_meals_ouline"'
          }, {
            value: '"no_meals"'
          }, {
            value: '"no_luggage"'
          }, {
            value: '"no_food"'
          }, {
            value: '"no_flash"'
          }, {
            value: '"no_encryption"'
          }, {
            value: '"no_drinks"'
          }, {
            value: '"no_cell"'
          }, {
            value: '"no_backpack"'
          }, {
            value: '"nights_stay"'
          }, {
            value: '"nightlight_round"'
          }, {
            value: '"nightlife"'
          }, {
            value: '"night_shelter"'
          }, {
            value: '"nfc"'
          }, {
            value: '"next_week"'
          }, {
            value: '"next_plan"'
          }, {
            value: '"new_releases"'
          }, {
            value: '"network_wifi"'
          }, {
            value: '"network_locked"'
          }, {
            value: '"network_check"'
          }, {
            value: '"network_cell"'
          }, {
            value: '"near_me_disabled"'
          }, {
            value: '"near_me"'
          }, {
            value: '"navigation"'
          }, {
            value: '"navigate_next"'
          }, {
            value: '"navigate_before"'
          }, {
            value: '"nature_people"'
          }, {
            value: '"nature"'
          }, {
            value: '"nat"'
          }, {
            value: '"outlined_flag"'
          }, {
            value: '"outlet"'
          }, {
            value: '"outgoing_mail"'
          }, {
            value: '"outdoor_grill"'
          }, {
            value: '"outbox"'
          }, {
            value: '"outbond"'
          }, {
            value: '"open_with"'
          }, {
            value: '"open_in_new"'
          }, {
            value: '"open_in_full"'
          }, {
            value: '"open_in_browser"'
          }, {
            value: '"opacity"'
          }, {
            value: '"online_prediction"'
          }, {
            value: '"ondemand_video"'
          }, {
            value: '"offline_share"'
          }, {
            value: '"offline_pin"'
          }, {
            value: '"offline_bolt"'
          }, {
            value: '"push_pin"'
          }, {
            value: '"published_with_changes"'
          }, {
            value: '"publish"'
          }, {
            value: '"public_off"'
          }, {
            value: '"public"'
          }, {
            value: '"psychology"'
          }, {
            value: '"privacy_tip"'
          }, {
            value: '"priority_high"'
          }, {
            value: '"print_disabled"'
          }, {
            value: '"print"'
          }, {
            value: '"preview"'
          }, {
            value: '"present_to_all"'
          }, {
            value: '"pregnant_woman"'
          }, {
            value: '"power_settings_new"'
          }, {
            value: '"power_off"'
          }, {
            value: '"power_input"'
          }, {
            value: '"power"'
          }, {
            value: '"post_add"'
          }, {
            value: '"portrait"'
          }, {
            value: '"portable_wifi_off"'
          }, {
            value: '"pool"'
          }, {
            value: '"polymer"'
          }, {
            value: '"poll"'
          }, {
            value: '"policy"'
          }, {
            value: '"point_of_sale"'
          }, {
            value: '"plus_one"'
          }, {
            value: '"plumbing"'
          }, {
            value: '"playlist_play"'
          }, {
            value: '"playlist_add_check"'
          }, {
            value: '"playlist_add"'
          }, {
            value: '"play_for_work"'
          }, {
            value: '"play_disabled"'
          }, {
            value: '"play_circle_outline"'
          }, {
            value: '"play_circle_filled"'
          }, {
            value: '"play_circle_fill"'
          }, {
            value: '"play_arrow"'
          }, {
            value: '"plagiarism"'
          }, {
            value: '"place"'
          }, {
            value: '"pivot_table_chart"'
          }, {
            value: '"pin_drop"'
          }, {
            value: '"pie_chart_outlined"'
          }, {
            value: '"pie_chart"'
          }, {
            value: '"picture_in_picture_alt"'
          }, {
            value: '"picture_in_picture"'
          }, {
            value: '"picture_as_pdf"'
          }, {
            value: '"photo_size_select_small"'
          }, {
            value: '"photo_size_select_large"'
          }, {
            value: '"photo_size_select_actual"'
          }, {
            value: '"photo_library"'
          }, {
            value: '"photo_filter"'
          }, {
            value: '"photo_camera_front"'
          }, {
            value: '"photo_camera_back"'
          }, {
            value: '"photo_camera"'
          }, {
            value: '"photo_album"'
          }, {
            value: '"photo"'
          }, {
            value: '"phonelink_setup"'
          }, {
            value: '"phonelink_ring"'
          }, {
            value: '"phonelink_off"'
          }, {
            value: '"phonelink_lock"'
          }, {
            value: '"phonelink_erase"'
          }, {
            value: '"phonelink"'
          }, {
            value: '"phone_paused"'
          }, {
            value: '"phone_missed"'
          }, {
            value: '"phone_locked"'
          }, {
            value: '"phone_iphone"'
          }, {
            value: '"phone_in_talk"'
          }, {
            value: '"phone_forwarded"'
          }, {
            value: '"phone_enabled"'
          }, {
            value: '"phone_disabled"'
          }, {
            value: '"phone_callback"'
          }, {
            value: '"phone_bluetooth_speaker"'
          }, {
            value: '"phone_android"'
          }, {
            value: '"phone"'
          }, {
            value: '"pets"'
          }, {
            value: '"pest_control_rodent"'
          }, {
            value: '"pest_control"'
          }, {
            value: '"personal_video"'
          }, {
            value: '"person_search"'
          }, {
            value: '"person_remove_alt_1"'
          }, {
            value: '"person_remove"'
          }, {
            value: '"person_pin_circle"'
          }, {
            value: '"person_pin"'
          }, {
            value: '"person_outline"'
          }, {
            value: '"person_add_disabled"'
          }, {
            value: '"person_add_alt_1"'
          }, {
            value: '"person_add_alt"'
          }, {
            value: '"person_add"'
          }, {
            value: '"person"'
          }, {
            value: '"perm_scan_wifi"'
          }, {
            value: '"perm_phone_msg"'
          }, {
            value: '"perm_media"'
          }, {
            value: '"perm_identity"'
          }, {
            value: '"perm_device_information"'
          }, {
            value: '"perm_device_info"'
          }, {
            value: '"perm_data_setting"'
          }, {
            value: '"perm_contact_calendar"'
          }, {
            value: '"perm_contact_cal"'
          }, {
            value: '"perm_camera_mic"'
          }, {
            value: '"people_outline"'
          }, {
            value: '"people_alt"'
          }, {
            value: '"people"'
          }, {
            value: '"pending_actions"'
          }, {
            value: '"pending"'
          }, {
            value: '"pedal_bike"'
          }, {
            value: '"payment"'
          }, {
            value: '"pause_presentation"'
          }, {
            value: '"pause_circle_outline"'
          }, {
            value: '"pause_circle_filled"'
          }, {
            value: '"pause"'
          }, {
            value: '"party_mode"'
          }, {
            value: '"park"'
          }, {
            value: '"panorama_wide_angle_select"'
          }, {
            value: '"panorama_wide_angle"'
          }, {
            value: '"panorama_vertical_select"'
          }, {
            value: '"panorama_vertical"'
          }, {
            value: '"panorama_photosphere_select"'
          }, {
            value: '"panorama_photosphere"'
          }, {
            value: '"panorama_horizontal_select"'
          }, {
            value: '"panorama_horizontal"'
          }, {
            value: '"panorama_fisheye"'
          }, {
            value: '"panorama_fish_eye"'
          }, {
            value: '"panorama"'
          }, {
            value: '"pan_tool"'
          }, {
            value: '"palette"'
          }, {
            value: '"pageview"'
          }, {
            value: '"pages"'
          }, {
            value: '"padding"'
          }, {
            value: '"quickreply"'
          }, {
            value: '"quick_contacts_mail"'
          }, {
            value: '"quick_contacts_dialer"'
          }, {
            value: '"queue_play_next"'
          }, {
            value: '"queue_music"'
          }, {
            value: '"queue"'
          }, {
            value: '"question_answer"'
          }, {
            value: '"query_builder"'
          }, {
            value: '"qr_code_scanner"'
          }, {
            value: '"qr_code"'
          }, {
            value: '"rv_hookup"'
          }, {
            value: '"run_circle"'
          }, {
            value: '"rule_folder"'
          }, {
            value: '"rule"'
          }, {
            value: '"rtt"'
          }, {
            value: '"rss_feed"'
          }, {
            value: '"rowing"'
          }, {
            value: '"router"'
          }, {
            value: '"rounded_corner"'
          }, {
            value: '"rotate_right"'
          }, {
            value: '"rotate_left"'
          }, {
            value: '"rotate_90_degrees_ccw"'
          }, {
            value: '"room_service"'
          }, {
            value: '"room_preferences"'
          }, {
            value: '"room"'
          }, {
            value: '"roofing"'
          }, {
            value: '"ring_volume"'
          }, {
            value: '"rice_bowl"'
          }, {
            value: '"restore_page"'
          }, {
            value: '"restore_from_trash"'
          }, {
            value: '"restore"'
          }, {
            value: '"restaurant_menu"'
          }, {
            value: '"restaurant"'
          }, {
            value: '"reset_tv"'
          }, {
            value: '"request_quote"'
          }, {
            value: '"request_page"'
          }, {
            value: '"report_problem"'
          }, {
            value: '"report_off"'
          }, {
            value: '"report"'
          }, {
            value: '"reply_all"'
          }, {
            value: '"reply"'
          }, {
            value: '"replay_circle_filled"'
          }, {
            value: '"replay_5"'
          }, {
            value: '"replay_30"'
          }, {
            value: '"replay_10"'
          }, {
            value: '"replay"'
          }, {
            value: '"repeat_one_on"'
          }, {
            value: '"repeat_one"'
          }, {
            value: '"repeat_on"'
          }, {
            value: '"repeat"'
          }, {
            value: '"reorder"'
          }, {
            value: '"remove_shopping_cart"'
          }, {
            value: '"remove_red_eye"'
          }, {
            value: '"remove_moderator"'
          }, {
            value: '"remove_from_queue"'
          }, {
            value: '"remove_done"'
          }, {
            value: '"remove_circle_outline"'
          }, {
            value: '"remove_circle"'
          }, {
            value: '"remove"'
          }, {
            value: '"refresh"'
          }, {
            value: '"reduce_capacity"'
          }, {
            value: '"redo"'
          }, {
            value: '"redeem"'
          }, {
            value: '"record_voice_over"'
          }, {
            value: '"recommend"'
          }, {
            value: '"recent_actors"'
          }, {
            value: '"receipt_long"'
          }, {
            value: '"receipt"'
          }, {
            value: '"read_more"'
          }, {
            value: '"rate_review"'
          }, {
            value: '"ramen_dining"'
          }, {
            value: '"railway_alert"'
          }, {
            value: '"radio_button_unchecked"'
          }, {
            value: '"radio_button_on"'
          }, {
            value: '"radio_button_off"'
          }, {
            value: '"radio_button_checked"'
          }, {
            value: '"radio"'
          }, {
            value: '"system_update_tv"'
          }, {
            value: '"system_update_alt"'
          }, {
            value: '"system_update"'
          }, {
            value: '"sync_problem"'
          }, {
            value: '"sync_disabled"'
          }, {
            value: '"sync_alt"'
          }, {
            value: '"sync"'
          }, {
            value: '"switch_video"'
          }, {
            value: '"switch_right"'
          }, {
            value: '"switch_left"'
          }, {
            value: '"switch_camera"'
          }, {
            value: '"switch_account"'
          }, {
            value: '"swipe"'
          }, {
            value: '"swap_vertical_circle"'
          }, {
            value: '"swap_vert_circle"'
          }, {
            value: '"swap_vert"'
          }, {
            value: '"swap_horizontal_circle"'
          }, {
            value: '"swap_horiz"'
          }, {
            value: '"swap_calls"'
          }, {
            value: '"surround_sound"'
          }, {
            value: '"support_agent"'
          }, {
            value: '"support"'
          }, {
            value: '"supervisor_account"'
          }, {
            value: '"supervised_user_circle"'
          }, {
            value: '"superscript"'
          }, {
            value: '"subway"'
          }, {
            value: '"subtitles_off"'
          }, {
            value: '"subtitles"'
          }, {
            value: '"subscriptions"'
          }, {
            value: '"subscript"'
          }, {
            value: '"subject"'
          }, {
            value: '"subdirectory_arrow_right"'
          }, {
            value: '"subdirectory_arrow_left"'
          }, {
            value: '"stroller"'
          }, {
            value: '"strikethrough_s"'
          }, {
            value: '"streetview"'
          }, {
            value: '"stream"'
          }, {
            value: '"straighten"'
          }, {
            value: '"storefront"'
          }, {
            value: '"store_mall_directory"'
          }, {
            value: '"store"'
          }, {
            value: '"storage"'
          }, {
            value: '"stop_screen_share"'
          }, {
            value: '"stop_circle"'
          }, {
            value: '"sticky_note_2"'
          }, {
            value: '"stay_primary_portrait"'
          }, {
            value: '"stay_primary_landscape"'
          }, {
            value: '"stay_current_portrait"'
          }, {
            value: '"stay_current_landscape"'
          }, {
            value: '"stars"'
          }, {
            value: '"star_rate"'
          }, {
            value: '"star_outline"'
          }, {
            value: '"star_half"'
          }, {
            value: '"star_border"'
          }, {
            value: '"star"'
          }, {
            value: '"stairs"'
          }, {
            value: '"stacked_line_chart"'
          }, {
            value: '"stacked_bar_chart"'
          }, {
            value: '"square_foot"'
          }, {
            value: '"sports_volleyball"'
          }, {
            value: '"sports_tennis"'
          }, {
            value: '"sports_soccer"'
          }, {
            value: '"sports_rugby"'
          }, {
            value: '"sports_motorsports"'
          }, {
            value: '"sports_mma"'
          }, {
            value: '"sports_kabaddi"'
          }, {
            value: '"sports_hockey"'
          }, {
            value: '"sports_handball"'
          }, {
            value: '"sports_golf"'
          }, {
            value: '"sports_football"'
          }, {
            value: '"sports_esports"'
          }, {
            value: '"sports_cricket"'
          }, {
            value: '"sports_basketball"'
          }, {
            value: '"sports_baseball"'
          }, {
            value: '"sports_bar"'
          }, {
            value: '"sports"'
          }, {
            value: '"spellcheck"'
          }, {
            value: '"speed"'
          }, {
            value: '"speaker_phone"'
          }, {
            value: '"speaker_notes_off"'
          }, {
            value: '"speaker_notes"'
          }, {
            value: '"speaker_group"'
          }, {
            value: '"speaker"'
          }, {
            value: '"space_bar"'
          }, {
            value: '"spa"'
          }, {
            value: '"south_west"'
          }, {
            value: '"south_east"'
          }, {
            value: '"south"'
          }, {
            value: '"sort_by_alpha"'
          }, {
            value: '"sort"'
          }, {
            value: '"soap"'
          }, {
            value: '"snooze"'
          }, {
            value: '"snippet_folder"'
          }, {
            value: '"sms_failed"'
          }, {
            value: '"sms"'
          }, {
            value: '"smoking_rooms"'
          }, {
            value: '"smoke_free"'
          }, {
            value: '"smartphone"'
          }, {
            value: '"smart_button"'
          }, {
            value: '"slow_motion_video"'
          }, {
            value: '"slideshow"'
          }, {
            value: '"skip_previous"'
          }, {
            value: '"skip_next"'
          }, {
            value: '"single_bed"'
          }, {
            value: '"sim_card_alert"'
          }, {
            value: '"sim_card"'
          }, {
            value: '"signal_wifi_off"'
          }, {
            value: '"signal_wifi_4_bar_lock"'
          }, {
            value: '"signal_wifi_4_bar"'
          }, {
            value: '"signal_wifi_0_bar"'
          }, {
            value: '"signal_cellular_off"'
          }, {
            value: '"signal_cellular_null"'
          }, {
            value: '"signal_cellular_no_sim"'
          }, {
            value: '"signal_cellular_connected_no_internet_4_bar"'
          }, {
            value: '"signal_cellular_alt"'
          }, {
            value: '"signal_cellular_4_bar"'
          }, {
            value: '"signal_cellular_0_bar"'
          }, {
            value: '"sick"'
          }, {
            value: '"shutter_speed"'
          }, {
            value: '"shuffle_on"'
          }, {
            value: '"shuffle"'
          }, {
            value: '"show_chart"'
          }, {
            value: '"short_text"'
          }, {
            value: '"shopping_cart"'
          }, {
            value: '"shopping_basket"'
          }, {
            value: '"shopping_bag"'
          }, {
            value: '"shop_two"'
          }, {
            value: '"shop"'
          }, {
            value: '"shield"'
          }, {
            value: '"share"'
          }, {
            value: '"settings_voice"'
          }, {
            value: '"settings_system_daydream"'
          }, {
            value: '"settings_remote"'
          }, {
            value: '"settings_power"'
          }, {
            value: '"settings_phone"'
          }, {
            value: '"settings_overscan"'
          }, {
            value: '"settings_input_svideo"'
          }, {
            value: '"settings_input_hdmi"'
          }, {
            value: '"settings_input_composite"'
          }, {
            value: '"settings_input_component"'
          }, {
            value: '"settings_input_antenna"'
          }, {
            value: '"settings_ethernet"'
          }, {
            value: '"settings_display"'
          }, {
            value: '"settings_cell"'
          }, {
            value: '"settings_brightness"'
          }, {
            value: '"settings_bluetooth"'
          }, {
            value: '"settings_backup_restore"'
          }, {
            value: '"settings_applications"'
          }, {
            value: '"settings"'
          }, {
            value: '"set_meal"'
          }, {
            value: '"sentiment_very_satisfied"'
          }, {
            value: '"sentiment_very_dissatisfied"'
          }, {
            value: '"sentiment_satisfied_alt"'
          }, {
            value: '"sentiment_satisfied"'
          }, {
            value: '"sentiment_neutral"'
          }, {
            value: '"sentiment_dissatisfied"'
          }, {
            value: '"sensor_window"'
          }, {
            value: '"sensor_door"'
          }, {
            value: '"send_to_mobile"'
          }, {
            value: '"send_and_archive"'
          }, {
            value: '"send"'
          }, {
            value: '"self_improvement"'
          }, {
            value: '"select_all"'
          }, {
            value: '"segment"'
          }, {
            value: '"search_off"'
          }, {
            value: '"sd_storage"'
          }, {
            value: '"sd_card"'
          }, {
            value: '"sd"'
          }, {
            value: '"screen_share"'
          }, {
            value: '"screen_search_desktop"'
          }, {
            value: '"screen_rotation"'
          }, {
            value: '"screen_lock_rotation"'
          }, {
            value: '"screen_lock_portrait"'
          }, {
            value: '"screen_lock_landscape"'
          }, {
            value: '"score"'
          }, {
            value: '"science"'
          }, {
            value: '"school"'
          }, {
            value: '"schedule_send"'
          }, {
            value: '"schedule"'
          }, {
            value: '"scatter_plot"'
          }, {
            value: '"scanner"'
          }, {
            value: '"saved_search"'
          }, {
            value: '"save_alt"'
          }, {
            value: '"save"'
          }, {
            value: '"satellite"'
          }, {
            value: '"sanitizer"'
          }, {
            value: '"two_wheeler"'
          }, {
            value: '"tv_off"'
          }, {
            value: '"tv"'
          }, {
            value: '"turned_in_not"'
          }, {
            value: '"turned_in"'
          }, {
            value: '"tune"'
          }, {
            value: '"tty"'
          }, {
            value: '"trip_origin"'
          }, {
            value: '"trending_up"'
          }, {
            value: '"trending_neutral"'
          }, {
            value: '"trending_flat"'
          }, {
            value: '"trending_down"'
          }, {
            value: '"transit_enterexit"'
          }, {
            value: '"transform"'
          }, {
            value: '"transfer_within_a_station"'
          }, {
            value: '"tram"'
          }, {
            value: '"train"'
          }, {
            value: '"traffic"'
          }, {
            value: '"track_changes"'
          }, {
            value: '"toys"'
          }, {
            value: '"tour"'
          }, {
            value: '"touch_app"'
          }, {
            value: '"topic"'
          }, {
            value: '"tonality"'
          }, {
            value: '"toll"'
          }, {
            value: '"toggle_on"'
          }, {
            value: '"toggle_off"'
          }, {
            value: '"today"'
          }, {
            value: '"toc"'
          }, {
            value: '"timer_off"'
          }, {
            value: '"timer_3"'
          }, {
            value: '"timer_10"'
          }, {
            value: '"timer"'
          }, {
            value: '"timeline"'
          }, {
            value: '"timelapse"'
          }, {
            value: '"time_to_leave"'
          }, {
            value: '"thumbs_up_down"'
          }, {
            value: '"thumb_up_off_alt"'
          }, {
            value: '"thumb_up_alt"'
          }, {
            value: '"thumb_up"'
          }, {
            value: '"thumb_down_off_alt"'
          }, {
            value: '"thumb_down_alt"'
          }, {
            value: '"thumb_down"'
          }, {
            value: '"theaters"'
          }, {
            value: '"theater_comedy"'
          }, {
            value: '"texture"'
          }, {
            value: '"textsms"'
          }, {
            value: '"text_snippet"'
          }, {
            value: '"text_rotation_none"'
          }, {
            value: '"text_rotation_down"'
          }, {
            value: '"text_rotation_angleup"'
          }, {
            value: '"text_rotation_angledown"'
          }, {
            value: '"text_rotate_vertical"'
          }, {
            value: '"text_rotate_up"'
          }, {
            value: '"text_format"'
          }, {
            value: '"text_fields"'
          }, {
            value: '"terrain"'
          }, {
            value: '"taxi_alert"'
          }, {
            value: '"tapas"'
          }, {
            value: '"tap_and_play"'
          }, {
            value: '"takeout_dining"'
          }, {
            value: '"tag_faces"'
          }, {
            value: '"tag"'
          }, {
            value: '"tablet_mac"'
          }, {
            value: '"tablet_android"'
          }, {
            value: '"tablet"'
          }, {
            value: '"table_view"'
          }, {
            value: '"table_rows"'
          }, {
            value: '"table_chart"'
          }, {
            value: '"tab_unselected"'
          }, {
            value: '"tab"'
          }, {
            value: '"usb"'
          }, {
            value: '"upload_file"'
          }, {
            value: '"upgrade"'
          }, {
            value: '"update"'
          }, {
            value: '"unsubscribe"'
          }, {
            value: '"unpublished"'
          }, {
            value: '"unfold_more"'
          }, {
            value: '"unfold_less"'
          }, {
            value: '"undo"'
          }, {
            value: '"unarchive"'
          }, {
            value: '"umbrella"'
          }, {
            value: '"vpn_lock"'
          }, {
            value: '"vpn_key"'
          }, {
            value: '"volunteer_activism"'
          }, {
            value: '"volume_up"'
          }, {
            value: '"volume_off"'
          }, {
            value: '"volume_mute"'
          }, {
            value: '"volume_down"'
          }, {
            value: '"voicemail"'
          }, {
            value: '"voice_over_off"'
          }, {
            value: '"voice_chat"'
          }, {
            value: '"visibility_off"'
          }, {
            value: '"visibility"'
          }, {
            value: '"vignette"'
          }, {
            value: '"view_week"'
          }, {
            value: '"view_stream"'
          }, {
            value: '"view_sidebar"'
          }, {
            value: '"view_quilt"'
          }, {
            value: '"view_module"'
          }, {
            value: '"view_list"'
          }, {
            value: '"view_in_ar"'
          }, {
            value: '"view_headline"'
          }, {
            value: '"view_day"'
          }, {
            value: '"view_compact"'
          }, {
            value: '"view_comfy"'
          }, {
            value: '"view_comfortable"'
          }, {
            value: '"view_column"'
          }, {
            value: '"view_carousel"'
          }, {
            value: '"view_array"'
          }, {
            value: '"view_agenda"'
          }, {
            value: '"videogame_asset"'
          }, {
            value: '"videocam_off"'
          }, {
            value: '"videocam"'
          }, {
            value: '"video_settings"'
          }, {
            value: '"video_library"'
          }, {
            value: '"video_label"'
          }, {
            value: '"video_collection"'
          }, {
            value: '"video_call"'
          }, {
            value: '"vibration"'
          }, {
            value: '"vertical_split"'
          }, {
            value: '"vertical_align_top"'
          }, {
            value: '"vertical_align_center"'
          }, {
            value: '"vertical_align_bottom"'
          }, {
            value: '"verified_user"'
          }, {
            value: '"verified"'
          }, {
            value: '"wysiwyg"'
          }, {
            value: '"wrong_location"'
          }, {
            value: '"wrap_text"'
          }, {
            value: '"workspaces_outline"'
          }, {
            value: '"workspaces_filled"'
          }, {
            value: '"work_outline"'
          }, {
            value: '"work_off"'
          }, {
            value: '"work"'
          }, {
            value: '"wine_bar"'
          }, {
            value: '"wifi_tethering"'
          }, {
            value: '"wifi_protected_setup"'
          }, {
            value: '"wifi_off"'
          }, {
            value: '"wifi_lock"'
          }, {
            value: '"wifi_calling"'
          }, {
            value: '"wifi"'
          }, {
            value: '"widgets"'
          }, {
            value: '"where_to_vote"'
          }, {
            value: '"wheelchair_pickup"'
          }, {
            value: '"whatshot"'
          }, {
            value: '"west"'
          }, {
            value: '"weekend"'
          }, {
            value: '"web_asset"'
          }, {
            value: '"web"'
          }, {
            value: '"wc"'
          }, {
            value: '"wb_twighlight"'
          }, {
            value: '"wb_sunny"'
          }, {
            value: '"wb_shade"'
          }, {
            value: '"wb_iridescent"'
          }, {
            value: '"wb_incandescent"'
          }, {
            value: '"wb_cloudy"'
          }, {
            value: '"wb_auto"'
          }, {
            value: '"waves"'
          }, {
            value: '"waterfall_chart"'
          }, {
            value: '"water_damage"'
          }, {
            value: '"watch_later"'
          }, {
            value: '"watch"'
          }, {
            value: '"wash"'
          }, {
            value: '"wallpaper"'
          }, {
            value: '"wallet_travel"'
          }, {
            value: '"wallet_membership"'
          }, {
            value: '"wallet_giftcard"'
          }, {
            value: '"youtube_searched_for"'
          }, {
            value: '"zoom_out_map"'
          }, {
            value: '"zoom_out"'
          }, {
            value: '"zoom_in"'
          }, {
            value: '"do_disturb_alt"'
          }, {
            value: '"do_disturb_off"'
          }, {
            value: '"do_disturb_on"'
          }, {
            value: '"download_done"'
          }, {
            value: '"do_disturb"'
          }, {
            value: '"download"'
          }, {
            value: '"report_gmailerrorred"'
          }, {
            value: '"thermostat"'
          }, {
            value: '"attribution"'
          }, {
            value: '"no_encryption_gmailerrorred"'
          }, {
            value: '"face_unlock"'
          }, {
            value: '"precision_manufacturing"'
          }, {
            value: '"pie_chart_outline"'
          }, {
            value: '"paste"'
          }, {
            value: '"copy"'
          }, {
            value: '"cut"'
          }, {
            value: '"upload"'
          }, {
            value: '"star_border_purple500"'
          }, {
            value: '"star_purple500"'
          }, {
            value: '"sd_card_alert"'
          }, {
            value: '"money_off_csred"'
          }, {
            value: '"mode"'
          }, {
            value: '"warning_amber"'
          }]
        }
      },
      endIcon: {
        defaultValue: null,
        description: '',
        name: 'endIcon',
        required: false,
        type: {
          name: 'enum',
          raw: 'AllIconsType',
          value: [{
            value: '"style"'
          }, {
            value: '"title"'
          }, {
            value: '"translate"'
          }, {
            value: '"security"'
          }, {
            value: '"message"'
          }, {
            value: '"info"'
          }, {
            value: '"warning"'
          }, {
            value: '"error"'
          }, {
            value: '"label"'
          }, {
            value: '"article"'
          }, {
            value: '"code"'
          }, {
            value: '"details"'
          }, {
            value: '"input"'
          }, {
            value: '"link"'
          }, {
            value: '"map"'
          }, {
            value: '"menu"'
          }, {
            value: '"source"'
          }, {
            value: '"table"'
          }, {
            value: '"template"'
          }, {
            value: '"circle"'
          }, {
            value: '"filter"'
          }, {
            value: '"image"'
          }, {
            value: '"stop"'
          }, {
            value: '"email"'
          }, {
            value: '"search"'
          }, {
            value: '"drag_corner"'
          }, {
            value: '"exclude"'
          }, {
            value: '"export"'
          }, {
            value: '"filter_filled"'
          }, {
            value: '"import"'
          }, {
            value: '"list_remove"'
          }, {
            value: '"pin"'
          }, {
            value: '"pin_left"'
          }, {
            value: '"pin_right"'
          }, {
            value: '"unpin"'
          }, {
            value: '"bar_chart"'
          }, {
            value: '"donut_chart"'
          }, {
            value: '"line_chart"'
          }, {
            value: '"stacked_chart"'
          }, {
            value: '"file_chart"'
          }, {
            value: '"file_code"'
          }, {
            value: '"file_pdf"'
          }, {
            value: '"file_table"'
          }, {
            value: '"tree_view"'
          }, {
            value: '"advanced_search"'
          }, {
            value: '"asterisk"'
          }, {
            value: '"currency_exchange"'
          }, {
            value: '"import_export"'
          }, {
            value: '"knowledgebase"'
          }, {
            value: '"move_down"'
          }, {
            value: '"move_up"'
          }, {
            value: '"collapse_column"'
          }, {
            value: '"collapse_down"'
          }, {
            value: '"collapse_horiz"'
          }, {
            value: '"collapse_left"'
          }, {
            value: '"collapse_right"'
          }, {
            value: '"collapse_up"'
          }, {
            value: '"collapse_vert"'
          }, {
            value: '"expand_row"'
          }, {
            value: '"move"'
          }, {
            value: '"analytics"'
          }, {
            value: '"billing"'
          }, {
            value: '"collect"'
          }, {
            value: '"commerce"'
          }, {
            value: '"customers"'
          }, {
            value: '"finance"'
          }, {
            value: '"home"'
          }, {
            value: '"insights"'
          }, {
            value: '"marketplace"'
          }, {
            value: '"payments"'
          }, {
            value: '"platform"'
          }, {
            value: '"products"'
          }, {
            value: '"profile"'
          }, {
            value: '"reporting"'
          }, {
            value: '"revpro"'
          }, {
            value: '"workflow"'
          }, {
            value: '"column_group"'
          }, {
            value: '"fields"'
          }, {
            value: '"insert_row"'
          }, {
            value: '"remove_row"'
          }, {
            value: '"row_group"'
          }, {
            value: '"1k_plus"'
          }, {
            value: '"19mp"'
          }, {
            value: '"18mp"'
          }, {
            value: '"17mp"'
          }, {
            value: '"16mp"'
          }, {
            value: '"15mp"'
          }, {
            value: '"14mp"'
          }, {
            value: '"13mp"'
          }, {
            value: '"12mp"'
          }, {
            value: '"11mp"'
          }, {
            value: '"10mp"'
          }, {
            value: '"10k"'
          }, {
            value: '"1k"'
          }, {
            value: '"2k_plus"'
          }, {
            value: '"24mp"'
          }, {
            value: '"23mp"'
          }, {
            value: '"22mp"'
          }, {
            value: '"21mp"'
          }, {
            value: '"20mp"'
          }, {
            value: '"2mp"'
          }, {
            value: '"2k"'
          }, {
            value: '"3d_rotation"'
          }, {
            value: '"3k_plus"'
          }, {
            value: '"3mp"'
          }, {
            value: '"360"'
          }, {
            value: '"3k"'
          }, {
            value: '"4k_plus"'
          }, {
            value: '"4mp"'
          }, {
            value: '"4k"'
          }, {
            value: '"5mp"'
          }, {
            value: '"5k_plus"'
          }, {
            value: '"5k"'
          }, {
            value: '"5g"'
          }, {
            value: '"6mp"'
          }, {
            value: '"6k_plus"'
          }, {
            value: '"6k"'
          }, {
            value: '"6_ft_apart"'
          }, {
            value: '"7k_plus"'
          }, {
            value: '"7mp"'
          }, {
            value: '"7k"'
          }, {
            value: '"8k_plus"'
          }, {
            value: '"8mp"'
          }, {
            value: '"8k"'
          }, {
            value: '"9k_plus"'
          }, {
            value: '"9mp"'
          }, {
            value: '"9k"'
          }, {
            value: '"av_timer"'
          }, {
            value: '"autorenew"'
          }, {
            value: '"auto_stories"'
          }, {
            value: '"auto_fix_off"'
          }, {
            value: '"auto_fix_normal"'
          }, {
            value: '"auto_fix_high"'
          }, {
            value: '"auto_delete"'
          }, {
            value: '"auto_awesome_motion"'
          }, {
            value: '"auto_awesome_mosaic"'
          }, {
            value: '"auto_awesome"'
          }, {
            value: '"audiotrack"'
          }, {
            value: '"attractions"'
          }, {
            value: '"attachment"'
          }, {
            value: '"attach_money"'
          }, {
            value: '"attach_file"'
          }, {
            value: '"attach_email"'
          }, {
            value: '"atm"'
          }, {
            value: '"assistant_photo"'
          }, {
            value: '"assistant_navigation"'
          }, {
            value: '"assistant_direction"'
          }, {
            value: '"assistant"'
          }, {
            value: '"assignment_turned_in"'
          }, {
            value: '"assignment_returned"'
          }, {
            value: '"assignment_return"'
          }, {
            value: '"assignment_late"'
          }, {
            value: '"assignment_ind"'
          }, {
            value: '"assignment"'
          }, {
            value: '"assessment"'
          }, {
            value: '"aspect_ratio"'
          }, {
            value: '"art_track"'
          }, {
            value: '"arrow_upward"'
          }, {
            value: '"arrow_right_alt"'
          }, {
            value: '"arrow_right"'
          }, {
            value: '"arrow_left"'
          }, {
            value: '"arrow_forward_ios"'
          }, {
            value: '"arrow_forward"'
          }, {
            value: '"arrow_drop_up"'
          }, {
            value: '"arrow_drop_down_circle"'
          }, {
            value: '"arrow_drop_down"'
          }, {
            value: '"arrow_downward"'
          }, {
            value: '"arrow_circle_up"'
          }, {
            value: '"arrow_circle_down"'
          }, {
            value: '"arrow_back_ios"'
          }, {
            value: '"arrow_back"'
          }, {
            value: '"archive"'
          }, {
            value: '"architecture"'
          }, {
            value: '"apps"'
          }, {
            value: '"approval"'
          }, {
            value: '"app_settings_alt"'
          }, {
            value: '"app_registration"'
          }, {
            value: '"app_blocking"'
          }, {
            value: '"api"'
          }, {
            value: '"apartment"'
          }, {
            value: '"announcement"'
          }, {
            value: '"animation"'
          }, {
            value: '"android"'
          }, {
            value: '"anchor"'
          }, {
            value: '"amp_stories"'
          }, {
            value: '"alternate_email"'
          }, {
            value: '"alt_route"'
          }, {
            value: '"all_out"'
          }, {
            value: '"all_inclusive"'
          }, {
            value: '"all_inbox"'
          }, {
            value: '"album"'
          }, {
            value: '"alarm_on"'
          }, {
            value: '"alarm_off"'
          }, {
            value: '"alarm_add"'
          }, {
            value: '"alarm"'
          }, {
            value: '"airport_shuttle"'
          }, {
            value: '"airplay"'
          }, {
            value: '"airplanemode_on"'
          }, {
            value: '"airplanemode_off"'
          }, {
            value: '"airplanemode_inactive"'
          }, {
            value: '"airplanemode_active"'
          }, {
            value: '"airline_seat_recline_normal"'
          }, {
            value: '"airline_seat_recline_extra"'
          }, {
            value: '"airline_seat_legroom_reduced"'
          }, {
            value: '"airline_seat_legroom_normal"'
          }, {
            value: '"airline_seat_legroom_extra"'
          }, {
            value: '"airline_seat_individual_suite"'
          }, {
            value: '"airline_seat_flat_angled"'
          }, {
            value: '"airline_seat_flat"'
          }, {
            value: '"agriculture"'
          }, {
            value: '"admin_panel_settings"'
          }, {
            value: '"adjust"'
          }, {
            value: '"addchart"'
          }, {
            value: '"add_to_queue"'
          }, {
            value: '"add_to_photos"'
          }, {
            value: '"add_to_home_screen"'
          }, {
            value: '"add_to_drive"'
          }, {
            value: '"add_task"'
          }, {
            value: '"add_shopping_cart"'
          }, {
            value: '"add_road"'
          }, {
            value: '"add_photo_alternate"'
          }, {
            value: '"add_moderator"'
          }, {
            value: '"add_location_alt"'
          }, {
            value: '"add_location"'
          }, {
            value: '"add_link"'
          }, {
            value: '"add_ic_call"'
          }, {
            value: '"add_comment"'
          }, {
            value: '"add_circle_outline"'
          }, {
            value: '"add_circle"'
          }, {
            value: '"add_chart"'
          }, {
            value: '"add_call"'
          }, {
            value: '"add_business"'
          }, {
            value: '"add_box"'
          }, {
            value: '"add_alert"'
          }, {
            value: '"add_alarm"'
          }, {
            value: '"add_a_photo"'
          }, {
            value: '"add"'
          }, {
            value: '"adb"'
          }, {
            value: '"ad_units"'
          }, {
            value: '"account_tree"'
          }, {
            value: '"account_circle"'
          }, {
            value: '"account_box"'
          }, {
            value: '"account_balance_wallet"'
          }, {
            value: '"account_balance"'
          }, {
            value: '"accessible_forward"'
          }, {
            value: '"accessible"'
          }, {
            value: '"accessibility_new"'
          }, {
            value: '"accessibility"'
          }, {
            value: '"access_time"'
          }, {
            value: '"access_alarms"'
          }, {
            value: '"access_alarm"'
          }, {
            value: '"ac_unit"'
          }, {
            value: '"business_center"'
          }, {
            value: '"business"'
          }, {
            value: '"bus_alert"'
          }, {
            value: '"burst_mode"'
          }, {
            value: '"build_circle"'
          }, {
            value: '"build"'
          }, {
            value: '"bug_report"'
          }, {
            value: '"bubble_chart"'
          }, {
            value: '"brush"'
          }, {
            value: '"brunch_dining"'
          }, {
            value: '"browser_not_supported"'
          }, {
            value: '"broken_image"'
          }, {
            value: '"brightness_medium"'
          }, {
            value: '"brightness_low"'
          }, {
            value: '"brightness_high"'
          }, {
            value: '"brightness_auto"'
          }, {
            value: '"brightness_7"'
          }, {
            value: '"brightness_6"'
          }, {
            value: '"brightness_5"'
          }, {
            value: '"brightness_4"'
          }, {
            value: '"brightness_3"'
          }, {
            value: '"brightness_2"'
          }, {
            value: '"brightness_1"'
          }, {
            value: '"breakfast_dining"'
          }, {
            value: '"branding_watermark"'
          }, {
            value: '"border_vertical"'
          }, {
            value: '"border_top"'
          }, {
            value: '"border_style"'
          }, {
            value: '"border_right"'
          }, {
            value: '"border_outer"'
          }, {
            value: '"border_left"'
          }, {
            value: '"border_inner"'
          }, {
            value: '"border_horizontal"'
          }, {
            value: '"border_color"'
          }, {
            value: '"border_clear"'
          }, {
            value: '"border_bottom"'
          }, {
            value: '"border_all"'
          }, {
            value: '"bookmarks"'
          }, {
            value: '"bookmark_outline"'
          }, {
            value: '"bookmark_border"'
          }, {
            value: '"bookmark"'
          }, {
            value: '"book_online"'
          }, {
            value: '"book"'
          }, {
            value: '"bolt"'
          }, {
            value: '"blur_on"'
          }, {
            value: '"blur_off"'
          }, {
            value: '"blur_linear"'
          }, {
            value: '"blur_circular"'
          }, {
            value: '"bluetooth_searching"'
          }, {
            value: '"bluetooth_disabled"'
          }, {
            value: '"bluetooth_connected"'
          }, {
            value: '"bluetooth_audio"'
          }, {
            value: '"bluetooth"'
          }, {
            value: '"block_flipped"'
          }, {
            value: '"block"'
          }, {
            value: '"biotech"'
          }, {
            value: '"bike_scooter"'
          }, {
            value: '"bento"'
          }, {
            value: '"beenhere"'
          }, {
            value: '"bedtime"'
          }, {
            value: '"beach_access"'
          }, {
            value: '"battery_unknown"'
          }, {
            value: '"battery_std"'
          }, {
            value: '"battery_full"'
          }, {
            value: '"battery_charging_full"'
          }, {
            value: '"battery_alert"'
          }, {
            value: '"bathtub"'
          }, {
            value: '"batch_prediction"'
          }, {
            value: '"ballot"'
          }, {
            value: '"bakery_dining"'
          }, {
            value: '"badge"'
          }, {
            value: '"backup_table"'
          }, {
            value: '"backup"'
          }, {
            value: '"backspace"'
          }, {
            value: '"backpack"'
          }, {
            value: '"baby_changing_station"'
          }, {
            value: '"crop_square"'
          }, {
            value: '"crop_rotate"'
          }, {
            value: '"crop_portrait"'
          }, {
            value: '"crop_original"'
          }, {
            value: '"crop_landscape"'
          }, {
            value: '"crop_free"'
          }, {
            value: '"crop_din"'
          }, {
            value: '"crop_7_5"'
          }, {
            value: '"crop_5_4"'
          }, {
            value: '"crop_3_2"'
          }, {
            value: '"crop_16_9"'
          }, {
            value: '"crop"'
          }, {
            value: '"credit_card"'
          }, {
            value: '"create_new_folder"'
          }, {
            value: '"create"'
          }, {
            value: '"countertops"'
          }, {
            value: '"corporate_fare"'
          }, {
            value: '"coronavirus"'
          }, {
            value: '"copyright"'
          }, {
            value: '"control_point_duplicate"'
          }, {
            value: '"control_point"'
          }, {
            value: '"control_camera"'
          }, {
            value: '"content_paste"'
          }, {
            value: '"content_cut"'
          }, {
            value: '"content_copy"'
          }, {
            value: '"contacts"'
          }, {
            value: '"contactless"'
          }, {
            value: '"contact_support"'
          }, {
            value: '"contact_phone"'
          }, {
            value: '"contact_page"'
          }, {
            value: '"contact_mail"'
          }, {
            value: '"construction"'
          }, {
            value: '"connected_tv"'
          }, {
            value: '"connect_without_contact"'
          }, {
            value: '"confirmation_number"'
          }, {
            value: '"confirmation_num"'
          }, {
            value: '"computer"'
          }, {
            value: '"compress"'
          }, {
            value: '"compass_calibration"'
          }, {
            value: '"compare_arrows"'
          }, {
            value: '"compare"'
          }, {
            value: '"commute"'
          }, {
            value: '"comment_bank"'
          }, {
            value: '"comment"'
          }, {
            value: '"colorize"'
          }, {
            value: '"color_lens"'
          }, {
            value: '"collections_bookmark"'
          }, {
            value: '"collections"'
          }, {
            value: '"cloud_upload"'
          }, {
            value: '"cloud_queue"'
          }, {
            value: '"cloud_off"'
          }, {
            value: '"cloud_download"'
          }, {
            value: '"cloud_done"'
          }, {
            value: '"cloud_circle"'
          }, {
            value: '"cloud"'
          }, {
            value: '"closed_caption_off"'
          }, {
            value: '"closed_caption_disabled"'
          }, {
            value: '"closed_caption"'
          }, {
            value: '"close_fullscreen"'
          }, {
            value: '"close"'
          }, {
            value: '"clear_all"'
          }, {
            value: '"clear"'
          }, {
            value: '"cleaning_services"'
          }, {
            value: '"clean_hands"'
          }, {
            value: '"class"'
          }, {
            value: '"circle_notifications"'
          }, {
            value: '"chrome_reader_mode"'
          }, {
            value: '"child_friendly"'
          }, {
            value: '"child_care"'
          }, {
            value: '"chevron_right"'
          }, {
            value: '"chevron_left"'
          }, {
            value: '"checkroom"'
          }, {
            value: '"check_circle_outline"'
          }, {
            value: '"check_circle"'
          }, {
            value: '"check_box_outline_blank"'
          }, {
            value: '"check_box"'
          }, {
            value: '"check"'
          }, {
            value: '"chat_bubble_outline"'
          }, {
            value: '"chat_bubble"'
          }, {
            value: '"chat"'
          }, {
            value: '"charging_station"'
          }, {
            value: '"change_history"'
          }, {
            value: '"center_focus_weak"'
          }, {
            value: '"center_focus_strong"'
          }, {
            value: '"cell_wifi"'
          }, {
            value: '"celebration"'
          }, {
            value: '"category"'
          }, {
            value: '"cast_for_education"'
          }, {
            value: '"cast_connected"'
          }, {
            value: '"cast"'
          }, {
            value: '"casino"'
          }, {
            value: '"cases"'
          }, {
            value: '"carpenter"'
          }, {
            value: '"card_travel"'
          }, {
            value: '"card_membership"'
          }, {
            value: '"card_giftcard"'
          }, {
            value: '"car_repair"'
          }, {
            value: '"car_rental"'
          }, {
            value: '"cancel_schedule_send"'
          }, {
            value: '"cancel_presentation"'
          }, {
            value: '"cancel"'
          }, {
            value: '"campaign"'
          }, {
            value: '"camera_roll"'
          }, {
            value: '"camera_rear"'
          }, {
            value: '"camera_front"'
          }, {
            value: '"camera_enhance"'
          }, {
            value: '"camera_alt"'
          }, {
            value: '"camera"'
          }, {
            value: '"call_to_action"'
          }, {
            value: '"call_split"'
          }, {
            value: '"call_received"'
          }, {
            value: '"call_missed_outgoing"'
          }, {
            value: '"call_missed"'
          }, {
            value: '"call_merge"'
          }, {
            value: '"call_made"'
          }, {
            value: '"call_end"'
          }, {
            value: '"call"'
          }, {
            value: '"calendar_view_day"'
          }, {
            value: '"calendar_today"'
          }, {
            value: '"calculate"'
          }, {
            value: '"cake"'
          }, {
            value: '"cached"'
          }, {
            value: '"dynamic_form"'
          }, {
            value: '"dynamic_feed"'
          }, {
            value: '"dvr"'
          }, {
            value: '"duo"'
          }, {
            value: '"dry_cleaning"'
          }, {
            value: '"dry"'
          }, {
            value: '"drive_folder_upload"'
          }, {
            value: '"drive_file_rename_outline"'
          }, {
            value: '"drive_file_move_outline"'
          }, {
            value: '"drive_file_move"'
          }, {
            value: '"drive_eta"'
          }, {
            value: '"drag_indicator"'
          }, {
            value: '"drag_handle"'
          }, {
            value: '"drafts"'
          }, {
            value: '"double_arrow"'
          }, {
            value: '"donut_small"'
          }, {
            value: '"donut_large"'
          }, {
            value: '"done_outline"'
          }, {
            value: '"done_all"'
          }, {
            value: '"done"'
          }, {
            value: '"domain_verification"'
          }, {
            value: '"domain_disabled"'
          }, {
            value: '"domain"'
          }, {
            value: '"dock"'
          }, {
            value: '"do_not_touch"'
          }, {
            value: '"do_not_step"'
          }, {
            value: '"do_not_disturb_on"'
          }, {
            value: '"do_not_disturb_off"'
          }, {
            value: '"do_not_disturb_alt"'
          }, {
            value: '"do_not_disturb"'
          }, {
            value: '"dns"'
          }, {
            value: '"dnd_forwardslash"'
          }, {
            value: '"disc_full"'
          }, {
            value: '"disabled_by_default"'
          }, {
            value: '"dirty_lens"'
          }, {
            value: '"directions_walk"'
          }, {
            value: '"directions_transit"'
          }, {
            value: '"directions_train"'
          }, {
            value: '"directions_subway"'
          }, {
            value: '"directions_run"'
          }, {
            value: '"directions_railway"'
          }, {
            value: '"directions_off"'
          }, {
            value: '"directions_ferry"'
          }, {
            value: '"directions_car"'
          }, {
            value: '"directions_bus"'
          }, {
            value: '"directions_boat"'
          }, {
            value: '"directions_bike"'
          }, {
            value: '"directions"'
          }, {
            value: '"dinner_dining"'
          }, {
            value: '"dialpad"'
          }, {
            value: '"dialer_sip"'
          }, {
            value: '"devices_other"'
          }, {
            value: '"devices"'
          }, {
            value: '"device_unknown"'
          }, {
            value: '"device_thermostat"'
          }, {
            value: '"device_hub"'
          }, {
            value: '"developer_mode"'
          }, {
            value: '"developer_board"'
          }, {
            value: '"desktop_windows"'
          }, {
            value: '"desktop_mac"'
          }, {
            value: '"desktop_access_disabled"'
          }, {
            value: '"design_services"'
          }, {
            value: '"description"'
          }, {
            value: '"departure_board"'
          }, {
            value: '"delivery_dining"'
          }, {
            value: '"delete_sweep"'
          }, {
            value: '"delete_outline"'
          }, {
            value: '"delete_forever"'
          }, {
            value: '"delete"'
          }, {
            value: '"dehaze"'
          }, {
            value: '"deck"'
          }, {
            value: '"date_range"'
          }, {
            value: '"data_usage"'
          }, {
            value: '"dashboard_customize"'
          }, {
            value: '"dashboard"'
          }, {
            value: '"dangerous"'
          }, {
            value: '"extension"'
          }, {
            value: '"exposure_zero"'
          }, {
            value: '"exposure_plus_2"'
          }, {
            value: '"exposure_plus_1"'
          }, {
            value: '"exposure_neg_2"'
          }, {
            value: '"exposure_neg_1"'
          }, {
            value: '"exposure_minus_2"'
          }, {
            value: '"exposure_minus_1"'
          }, {
            value: '"exposure"'
          }, {
            value: '"explore_off"'
          }, {
            value: '"explore"'
          }, {
            value: '"explicit"'
          }, {
            value: '"expand_more"'
          }, {
            value: '"expand_less"'
          }, {
            value: '"expand"'
          }, {
            value: '"exit_to_app"'
          }, {
            value: '"event_seat"'
          }, {
            value: '"event_note"'
          }, {
            value: '"event_busy"'
          }, {
            value: '"event_available"'
          }, {
            value: '"event"'
          }, {
            value: '"ev_station"'
          }, {
            value: '"euro_symbol"'
          }, {
            value: '"euro"'
          }, {
            value: '"escalator_warning"'
          }, {
            value: '"escalator"'
          }, {
            value: '"error_outline"'
          }, {
            value: '"equalizer"'
          }, {
            value: '"enhanced_encryption"'
          }, {
            value: '"enhance_photo_translate"'
          }, {
            value: '"engineering"'
          }, {
            value: '"emoji_transportation"'
          }, {
            value: '"emoji_symbols"'
          }, {
            value: '"emoji_people"'
          }, {
            value: '"emoji_objects"'
          }, {
            value: '"emoji_nature"'
          }, {
            value: '"emoji_food_beverage"'
          }, {
            value: '"emoji_flags"'
          }, {
            value: '"emoji_events"'
          }, {
            value: '"emoji_emotions"'
          }, {
            value: '"elevator"'
          }, {
            value: '"electrical_services"'
          }, {
            value: '"electric_scooter"'
          }, {
            value: '"electric_rickshaw"'
          }, {
            value: '"electric_moped"'
          }, {
            value: '"electric_car"'
          }, {
            value: '"electric_bike"'
          }, {
            value: '"elderly"'
          }, {
            value: '"eject"'
          }, {
            value: '"edit_road"'
          }, {
            value: '"edit_off"'
          }, {
            value: '"edit_location"'
          }, {
            value: '"edit_attributes"'
          }, {
            value: '"edit"'
          }, {
            value: '"eco"'
          }, {
            value: '"east"'
          }, {
            value: '"functions"'
          }, {
            value: '"fullscreen_exit"'
          }, {
            value: '"fullscreen"'
          }, {
            value: '"free_breakfast"'
          }, {
            value: '"foundation"'
          }, {
            value: '"forward_to_inbox"'
          }, {
            value: '"forward_5"'
          }, {
            value: '"forward_30"'
          }, {
            value: '"forward_10"'
          }, {
            value: '"forward"'
          }, {
            value: '"forum"'
          }, {
            value: '"format_underlined"'
          }, {
            value: '"format_underline"'
          }, {
            value: '"format_textdirection_r_to_l"'
          }, {
            value: '"format_textdirection_l_to_r"'
          }, {
            value: '"format_strikethrough"'
          }, {
            value: '"format_size"'
          }, {
            value: '"format_shapes"'
          }, {
            value: '"format_quote"'
          }, {
            value: '"format_paint"'
          }, {
            value: '"format_list_numbered_rtl"'
          }, {
            value: '"format_list_numbered"'
          }, {
            value: '"format_list_bulleted"'
          }, {
            value: '"format_line_spacing"'
          }, {
            value: '"format_italic"'
          }, {
            value: '"format_indent_increase"'
          }, {
            value: '"format_indent_decrease"'
          }, {
            value: '"format_color_text"'
          }, {
            value: '"format_color_reset"'
          }, {
            value: '"format_color_fill"'
          }, {
            value: '"format_clear"'
          }, {
            value: '"format_bold"'
          }, {
            value: '"format_align_right"'
          }, {
            value: '"format_align_left"'
          }, {
            value: '"format_align_justify"'
          }, {
            value: '"format_align_center"'
          }, {
            value: '"food_bank"'
          }, {
            value: '"font_download"'
          }, {
            value: '"follow_the_signs"'
          }, {
            value: '"folder_special"'
          }, {
            value: '"folder_shared"'
          }, {
            value: '"folder_open"'
          }, {
            value: '"folder"'
          }, {
            value: '"flip_to_front"'
          }, {
            value: '"flip_to_back"'
          }, {
            value: '"flip_camera_ios"'
          }, {
            value: '"flip_camera_android"'
          }, {
            value: '"flip"'
          }, {
            value: '"flight_takeoff"'
          }, {
            value: '"flight_land"'
          }, {
            value: '"flight"'
          }, {
            value: '"flash_on"'
          }, {
            value: '"flash_off"'
          }, {
            value: '"flash_auto"'
          }, {
            value: '"flare"'
          }, {
            value: '"flaky"'
          }, {
            value: '"flag"'
          }, {
            value: '"fitness_center"'
          }, {
            value: '"fit_screen"'
          }, {
            value: '"first_page"'
          }, {
            value: '"fireplace"'
          }, {
            value: '"fire_hydrant"'
          }, {
            value: '"fire_extinguisher"'
          }, {
            value: '"fingerprint"'
          }, {
            value: '"find_replace"'
          }, {
            value: '"find_in_page"'
          }, {
            value: '"filter_vintage"'
          }, {
            value: '"filter_tilt_shift"'
          }, {
            value: '"filter_none"'
          }, {
            value: '"filter_list_alt"'
          }, {
            value: '"filter_list"'
          }, {
            value: '"filter_hdr"'
          }, {
            value: '"filter_frames"'
          }, {
            value: '"filter_drama"'
          }, {
            value: '"filter_center_focus"'
          }, {
            value: '"filter_b_and_w"'
          }, {
            value: '"filter_alt"'
          }, {
            value: '"filter_9_plus"'
          }, {
            value: '"filter_9"'
          }, {
            value: '"filter_8"'
          }, {
            value: '"filter_7"'
          }, {
            value: '"filter_6"'
          }, {
            value: '"filter_5"'
          }, {
            value: '"filter_4"'
          }, {
            value: '"filter_3"'
          }, {
            value: '"filter_2"'
          }, {
            value: '"filter_1"'
          }, {
            value: '"file_upload"'
          }, {
            value: '"file_present"'
          }, {
            value: '"file_download_done"'
          }, {
            value: '"file_download"'
          }, {
            value: '"file_copy"'
          }, {
            value: '"fiber_smart_record"'
          }, {
            value: '"fiber_pin"'
          }, {
            value: '"fiber_new"'
          }, {
            value: '"fiber_manual_record"'
          }, {
            value: '"fiber_dvr"'
          }, {
            value: '"festival"'
          }, {
            value: '"fence"'
          }, {
            value: '"feedback"'
          }, {
            value: '"featured_video"'
          }, {
            value: '"featured_play_list"'
          }, {
            value: '"favorite_outline"'
          }, {
            value: '"favorite_border"'
          }, {
            value: '"favorite"'
          }, {
            value: '"fastfood"'
          }, {
            value: '"fast_rewind"'
          }, {
            value: '"fast_forward"'
          }, {
            value: '"family_restroom"'
          }, {
            value: '"fact_check"'
          }, {
            value: '"facebook"'
          }, {
            value: '"face_retouching_natural"'
          }, {
            value: '"face"'
          }, {
            value: '"groups"'
          }, {
            value: '"group_work"'
          }, {
            value: '"group_add"'
          }, {
            value: '"group"'
          }, {
            value: '"grid_view"'
          }, {
            value: '"grid_on"'
          }, {
            value: '"grid_off"'
          }, {
            value: '"grass"'
          }, {
            value: '"graphic_eq"'
          }, {
            value: '"grain"'
          }, {
            value: '"grading"'
          }, {
            value: '"gradient"'
          }, {
            value: '"grade"'
          }, {
            value: '"gps_off"'
          }, {
            value: '"gps_not_fixed"'
          }, {
            value: '"gps_fixed"'
          }, {
            value: '"golf_course"'
          }, {
            value: '"goat"'
          }, {
            value: '"gif"'
          }, {
            value: '"get_app"'
          }, {
            value: '"gesture"'
          }, {
            value: '"gavel"'
          }, {
            value: '"games"'
          }, {
            value: '"gamepad"'
          }, {
            value: '"g_translate"'
          }, {
            value: '"hvac"'
          }, {
            value: '"https"'
          }, {
            value: '"http"'
          }, {
            value: '"how_to_vote"'
          }, {
            value: '"how_to_reg"'
          }, {
            value: '"house_siding"'
          }, {
            value: '"house"'
          }, {
            value: '"hourglass_top"'
          }, {
            value: '"hourglass_full"'
          }, {
            value: '"hourglass_empty"'
          }, {
            value: '"hourglass_disabled"'
          }, {
            value: '"hourglass_bottom"'
          }, {
            value: '"hotel"'
          }, {
            value: '"hot_tub"'
          }, {
            value: '"horizontal_split"'
          }, {
            value: '"horizontal_rule"'
          }, {
            value: '"home_work"'
          }, {
            value: '"home_repair_service"'
          }, {
            value: '"home_filled"'
          }, {
            value: '"history_toggle_off"'
          }, {
            value: '"history_edu"'
          }, {
            value: '"history"'
          }, {
            value: '"highlight_remove"'
          }, {
            value: '"highlight_off"'
          }, {
            value: '"highlight_alt"'
          }, {
            value: '"highlight"'
          }, {
            value: '"high_quality"'
          }, {
            value: '"help_outline"'
          }, {
            value: '"help_center"'
          }, {
            value: '"help"'
          }, {
            value: '"height"'
          }, {
            value: '"hearing_disabled"'
          }, {
            value: '"hearing"'
          }, {
            value: '"healing"'
          }, {
            value: '"headset_off"'
          }, {
            value: '"headset_mic"'
          }, {
            value: '"headset"'
          }, {
            value: '"hdr_weak"'
          }, {
            value: '"hdr_strong"'
          }, {
            value: '"hdr_on"'
          }, {
            value: '"hdr_off"'
          }, {
            value: '"hdr_enhanced_select"'
          }, {
            value: '"hd"'
          }, {
            value: '"hardware"'
          }, {
            value: '"handyman"'
          }, {
            value: '"hail"'
          }, {
            value: '"iso"'
          }, {
            value: '"ios_share"'
          }, {
            value: '"invert_colors_on"'
          }, {
            value: '"invert_colors_off"'
          }, {
            value: '"invert_colors"'
          }, {
            value: '"inventory"'
          }, {
            value: '"integration_instructions"'
          }, {
            value: '"insert_photo"'
          }, {
            value: '"insert_link"'
          }, {
            value: '"insert_invitation"'
          }, {
            value: '"insert_emoticon"'
          }, {
            value: '"insert_drive_file"'
          }, {
            value: '"insert_comment"'
          }, {
            value: '"insert_chart_outlined"'
          }, {
            value: '"insert_chart"'
          }, {
            value: '"info_outline"'
          }, {
            value: '"indeterminate_check_box"'
          }, {
            value: '"inbox"'
          }, {
            value: '"important_devices"'
          }, {
            value: '"import_contacts"'
          }, {
            value: '"imagesearch_roller"'
          }, {
            value: '"image_search"'
          }, {
            value: '"image_not_supported"'
          }, {
            value: '"image_aspect_ratio"'
          }, {
            value: '"icecream"'
          }, {
            value: '"keyboard_arrow_right"'
          }, {
            value: '"keyboard_arrow_left"'
          }, {
            value: '"keyboard_arrow_down"'
          }, {
            value: '"keyboard_backspace"'
          }, {
            value: '"keyboard_arrow_up"'
          }, {
            value: '"keyboard_capslock"'
          }, {
            value: '"keyboard_control"'
          }, {
            value: '"keyboard_return"'
          }, {
            value: '"keyboard_voice"'
          }, {
            value: '"keyboard_hide"'
          }, {
            value: '"keyboard_tab"'
          }, {
            value: '"king_bed"'
          }, {
            value: '"keyboard"'
          }, {
            value: '"kitchen"'
          }, {
            value: '"lunch_dining"'
          }, {
            value: '"luggage"'
          }, {
            value: '"loyalty"'
          }, {
            value: '"low_priority"'
          }, {
            value: '"loupe"'
          }, {
            value: '"loop"'
          }, {
            value: '"looks_two"'
          }, {
            value: '"looks_one"'
          }, {
            value: '"looks_6"'
          }, {
            value: '"looks_5"'
          }, {
            value: '"looks_4"'
          }, {
            value: '"looks_3"'
          }, {
            value: '"looks"'
          }, {
            value: '"logout"'
          }, {
            value: '"login"'
          }, {
            value: '"lock_outline"'
          }, {
            value: '"lock_open"'
          }, {
            value: '"lock_clock"'
          }, {
            value: '"lock"'
          }, {
            value: '"location_searching"'
          }, {
            value: '"location_pin"'
          }, {
            value: '"location_on"'
          }, {
            value: '"location_off"'
          }, {
            value: '"location_history"'
          }, {
            value: '"location_disabled"'
          }, {
            value: '"location_city"'
          }, {
            value: '"local_taxi"'
          }, {
            value: '"local_shipping"'
          }, {
            value: '"local_see"'
          }, {
            value: '"local_restaurant"'
          }, {
            value: '"local_printshop"'
          }, {
            value: '"local_print_shop"'
          }, {
            value: '"local_post_office"'
          }, {
            value: '"local_police"'
          }, {
            value: '"local_play"'
          }, {
            value: '"local_pizza"'
          }, {
            value: '"local_phone"'
          }, {
            value: '"local_pharmacy"'
          }, {
            value: '"local_parking"'
          }, {
            value: '"local_offer"'
          }, {
            value: '"local_movies"'
          }, {
            value: '"local_mall"'
          }, {
            value: '"local_library"'
          }, {
            value: '"local_laundry_service"'
          }, {
            value: '"local_hotel"'
          }, {
            value: '"local_hospital"'
          }, {
            value: '"local_grocery_store"'
          }, {
            value: '"local_gas_station"'
          }, {
            value: '"local_florist"'
          }, {
            value: '"local_fire_department"'
          }, {
            value: '"local_drink"'
          }, {
            value: '"local_dining"'
          }, {
            value: '"local_convenience_store"'
          }, {
            value: '"local_car_wash"'
          }, {
            value: '"local_cafe"'
          }, {
            value: '"local_bar"'
          }, {
            value: '"local_attraction"'
          }, {
            value: '"local_atm"'
          }, {
            value: '"local_airport"'
          }, {
            value: '"local_activity"'
          }, {
            value: '"live_tv"'
          }, {
            value: '"live_help"'
          }, {
            value: '"list_alt"'
          }, {
            value: '"list"'
          }, {
            value: '"liquor"'
          }, {
            value: '"linked_camera"'
          }, {
            value: '"link_off"'
          }, {
            value: '"linear_scale"'
          }, {
            value: '"line_weight"'
          }, {
            value: '"line_style"'
          }, {
            value: '"lightbulb_outline"'
          }, {
            value: '"lightbulb"'
          }, {
            value: '"library_music"'
          }, {
            value: '"library_books"'
          }, {
            value: '"library_add_check"'
          }, {
            value: '"library_add"'
          }, {
            value: '"lens"'
          }, {
            value: '"legend_toggle"'
          }, {
            value: '"leave_bags_at_home"'
          }, {
            value: '"leak_remove"'
          }, {
            value: '"leak_add"'
          }, {
            value: '"leaderboard"'
          }, {
            value: '"layers_clear"'
          }, {
            value: '"layers"'
          }, {
            value: '"launch"'
          }, {
            value: '"last_page"'
          }, {
            value: '"laptop_windows"'
          }, {
            value: '"laptop_mac"'
          }, {
            value: '"laptop_chromebook"'
          }, {
            value: '"laptop"'
          }, {
            value: '"language"'
          }, {
            value: '"landscape"'
          }, {
            value: '"label_outline"'
          }, {
            value: '"label_off"'
          }, {
            value: '"label_important_outline"'
          }, {
            value: '"label_important"'
          }, {
            value: '"my_location"'
          }, {
            value: '"my_library_music"'
          }, {
            value: '"my_library_books"'
          }, {
            value: '"my_library_add"'
          }, {
            value: '"music_video"'
          }, {
            value: '"music_off"'
          }, {
            value: '"music_note"'
          }, {
            value: '"museum"'
          }, {
            value: '"multitrack_audio"'
          }, {
            value: '"multiple_stop"'
          }, {
            value: '"multiline_chart"'
          }, {
            value: '"mp"'
          }, {
            value: '"movie_filter"'
          }, {
            value: '"movie_creation"'
          }, {
            value: '"movie"'
          }, {
            value: '"move_to_inbox"'
          }, {
            value: '"mouse"'
          }, {
            value: '"motorcycle"'
          }, {
            value: '"motion_photos_paused"'
          }, {
            value: '"motion_photos_pause"'
          }, {
            value: '"motion_photos_on"'
          }, {
            value: '"motion_photos_off"'
          }, {
            value: '"more_vert"'
          }, {
            value: '"more_time"'
          }, {
            value: '"more_horiz"'
          }, {
            value: '"more"'
          }, {
            value: '"moped"'
          }, {
            value: '"mood_bad"'
          }, {
            value: '"mood"'
          }, {
            value: '"monochrome_photos"'
          }, {
            value: '"monitor"'
          }, {
            value: '"money_off"'
          }, {
            value: '"money"'
          }, {
            value: '"monetization_on"'
          }, {
            value: '"model_training"'
          }, {
            value: '"mode_edit"'
          }, {
            value: '"mode_comment"'
          }, {
            value: '"mobile_screen_share"'
          }, {
            value: '"mobile_off"'
          }, {
            value: '"mobile_friendly"'
          }, {
            value: '"mms"'
          }, {
            value: '"missed_video_call"'
          }, {
            value: '"miscellaneous_services"'
          }, {
            value: '"minimize"'
          }, {
            value: '"military_tech"'
          }, {
            value: '"microwave"'
          }, {
            value: '"mic_off"'
          }, {
            value: '"mic_none"'
          }, {
            value: '"mic_external_on"'
          }, {
            value: '"mic_external_off"'
          }, {
            value: '"mic"'
          }, {
            value: '"messenger_outline"'
          }, {
            value: '"messenger"'
          }, {
            value: '"merge_type"'
          }, {
            value: '"menu_open"'
          }, {
            value: '"menu_book"'
          }, {
            value: '"memory"'
          }, {
            value: '"meeting_room"'
          }, {
            value: '"medical_services"'
          }, {
            value: '"mediation"'
          }, {
            value: '"maximize"'
          }, {
            value: '"masks"'
          }, {
            value: '"markunread_mailbox"'
          }, {
            value: '"markunread"'
          }, {
            value: '"mark_email_unread"'
          }, {
            value: '"mark_email_read"'
          }, {
            value: '"mark_chat_unread"'
          }, {
            value: '"mark_chat_read"'
          }, {
            value: '"mark_as_unread"'
          }, {
            value: '"margin"'
          }, {
            value: '"maps_ugc"'
          }, {
            value: '"mail_outline"'
          }, {
            value: '"mail"'
          }, {
            value: '"now_widgets"'
          }, {
            value: '"now_wallpaper"'
          }, {
            value: '"notifications_paused"'
          }, {
            value: '"notifications_on"'
          }, {
            value: '"notifications_off"'
          }, {
            value: '"notifications_none"'
          }, {
            value: '"notifications_active"'
          }, {
            value: '"notifications"'
          }, {
            value: '"notification_important"'
          }, {
            value: '"notes"'
          }, {
            value: '"note_add"'
          }, {
            value: '"note"'
          }, {
            value: '"not_started"'
          }, {
            value: '"not_listed_location"'
          }, {
            value: '"not_interested"'
          }, {
            value: '"not_accessible"'
          }, {
            value: '"north_west"'
          }, {
            value: '"north_east"'
          }, {
            value: '"north"'
          }, {
            value: '"no_transfer"'
          }, {
            value: '"no_stroller"'
          }, {
            value: '"no_sim"'
          }, {
            value: '"no_photography"'
          }, {
            value: '"no_meeting_room"'
          }, {
            value: '"no_meals_ouline"'
          }, {
            value: '"no_meals"'
          }, {
            value: '"no_luggage"'
          }, {
            value: '"no_food"'
          }, {
            value: '"no_flash"'
          }, {
            value: '"no_encryption"'
          }, {
            value: '"no_drinks"'
          }, {
            value: '"no_cell"'
          }, {
            value: '"no_backpack"'
          }, {
            value: '"nights_stay"'
          }, {
            value: '"nightlight_round"'
          }, {
            value: '"nightlife"'
          }, {
            value: '"night_shelter"'
          }, {
            value: '"nfc"'
          }, {
            value: '"next_week"'
          }, {
            value: '"next_plan"'
          }, {
            value: '"new_releases"'
          }, {
            value: '"network_wifi"'
          }, {
            value: '"network_locked"'
          }, {
            value: '"network_check"'
          }, {
            value: '"network_cell"'
          }, {
            value: '"near_me_disabled"'
          }, {
            value: '"near_me"'
          }, {
            value: '"navigation"'
          }, {
            value: '"navigate_next"'
          }, {
            value: '"navigate_before"'
          }, {
            value: '"nature_people"'
          }, {
            value: '"nature"'
          }, {
            value: '"nat"'
          }, {
            value: '"outlined_flag"'
          }, {
            value: '"outlet"'
          }, {
            value: '"outgoing_mail"'
          }, {
            value: '"outdoor_grill"'
          }, {
            value: '"outbox"'
          }, {
            value: '"outbond"'
          }, {
            value: '"open_with"'
          }, {
            value: '"open_in_new"'
          }, {
            value: '"open_in_full"'
          }, {
            value: '"open_in_browser"'
          }, {
            value: '"opacity"'
          }, {
            value: '"online_prediction"'
          }, {
            value: '"ondemand_video"'
          }, {
            value: '"offline_share"'
          }, {
            value: '"offline_pin"'
          }, {
            value: '"offline_bolt"'
          }, {
            value: '"push_pin"'
          }, {
            value: '"published_with_changes"'
          }, {
            value: '"publish"'
          }, {
            value: '"public_off"'
          }, {
            value: '"public"'
          }, {
            value: '"psychology"'
          }, {
            value: '"privacy_tip"'
          }, {
            value: '"priority_high"'
          }, {
            value: '"print_disabled"'
          }, {
            value: '"print"'
          }, {
            value: '"preview"'
          }, {
            value: '"present_to_all"'
          }, {
            value: '"pregnant_woman"'
          }, {
            value: '"power_settings_new"'
          }, {
            value: '"power_off"'
          }, {
            value: '"power_input"'
          }, {
            value: '"power"'
          }, {
            value: '"post_add"'
          }, {
            value: '"portrait"'
          }, {
            value: '"portable_wifi_off"'
          }, {
            value: '"pool"'
          }, {
            value: '"polymer"'
          }, {
            value: '"poll"'
          }, {
            value: '"policy"'
          }, {
            value: '"point_of_sale"'
          }, {
            value: '"plus_one"'
          }, {
            value: '"plumbing"'
          }, {
            value: '"playlist_play"'
          }, {
            value: '"playlist_add_check"'
          }, {
            value: '"playlist_add"'
          }, {
            value: '"play_for_work"'
          }, {
            value: '"play_disabled"'
          }, {
            value: '"play_circle_outline"'
          }, {
            value: '"play_circle_filled"'
          }, {
            value: '"play_circle_fill"'
          }, {
            value: '"play_arrow"'
          }, {
            value: '"plagiarism"'
          }, {
            value: '"place"'
          }, {
            value: '"pivot_table_chart"'
          }, {
            value: '"pin_drop"'
          }, {
            value: '"pie_chart_outlined"'
          }, {
            value: '"pie_chart"'
          }, {
            value: '"picture_in_picture_alt"'
          }, {
            value: '"picture_in_picture"'
          }, {
            value: '"picture_as_pdf"'
          }, {
            value: '"photo_size_select_small"'
          }, {
            value: '"photo_size_select_large"'
          }, {
            value: '"photo_size_select_actual"'
          }, {
            value: '"photo_library"'
          }, {
            value: '"photo_filter"'
          }, {
            value: '"photo_camera_front"'
          }, {
            value: '"photo_camera_back"'
          }, {
            value: '"photo_camera"'
          }, {
            value: '"photo_album"'
          }, {
            value: '"photo"'
          }, {
            value: '"phonelink_setup"'
          }, {
            value: '"phonelink_ring"'
          }, {
            value: '"phonelink_off"'
          }, {
            value: '"phonelink_lock"'
          }, {
            value: '"phonelink_erase"'
          }, {
            value: '"phonelink"'
          }, {
            value: '"phone_paused"'
          }, {
            value: '"phone_missed"'
          }, {
            value: '"phone_locked"'
          }, {
            value: '"phone_iphone"'
          }, {
            value: '"phone_in_talk"'
          }, {
            value: '"phone_forwarded"'
          }, {
            value: '"phone_enabled"'
          }, {
            value: '"phone_disabled"'
          }, {
            value: '"phone_callback"'
          }, {
            value: '"phone_bluetooth_speaker"'
          }, {
            value: '"phone_android"'
          }, {
            value: '"phone"'
          }, {
            value: '"pets"'
          }, {
            value: '"pest_control_rodent"'
          }, {
            value: '"pest_control"'
          }, {
            value: '"personal_video"'
          }, {
            value: '"person_search"'
          }, {
            value: '"person_remove_alt_1"'
          }, {
            value: '"person_remove"'
          }, {
            value: '"person_pin_circle"'
          }, {
            value: '"person_pin"'
          }, {
            value: '"person_outline"'
          }, {
            value: '"person_add_disabled"'
          }, {
            value: '"person_add_alt_1"'
          }, {
            value: '"person_add_alt"'
          }, {
            value: '"person_add"'
          }, {
            value: '"person"'
          }, {
            value: '"perm_scan_wifi"'
          }, {
            value: '"perm_phone_msg"'
          }, {
            value: '"perm_media"'
          }, {
            value: '"perm_identity"'
          }, {
            value: '"perm_device_information"'
          }, {
            value: '"perm_device_info"'
          }, {
            value: '"perm_data_setting"'
          }, {
            value: '"perm_contact_calendar"'
          }, {
            value: '"perm_contact_cal"'
          }, {
            value: '"perm_camera_mic"'
          }, {
            value: '"people_outline"'
          }, {
            value: '"people_alt"'
          }, {
            value: '"people"'
          }, {
            value: '"pending_actions"'
          }, {
            value: '"pending"'
          }, {
            value: '"pedal_bike"'
          }, {
            value: '"payment"'
          }, {
            value: '"pause_presentation"'
          }, {
            value: '"pause_circle_outline"'
          }, {
            value: '"pause_circle_filled"'
          }, {
            value: '"pause"'
          }, {
            value: '"party_mode"'
          }, {
            value: '"park"'
          }, {
            value: '"panorama_wide_angle_select"'
          }, {
            value: '"panorama_wide_angle"'
          }, {
            value: '"panorama_vertical_select"'
          }, {
            value: '"panorama_vertical"'
          }, {
            value: '"panorama_photosphere_select"'
          }, {
            value: '"panorama_photosphere"'
          }, {
            value: '"panorama_horizontal_select"'
          }, {
            value: '"panorama_horizontal"'
          }, {
            value: '"panorama_fisheye"'
          }, {
            value: '"panorama_fish_eye"'
          }, {
            value: '"panorama"'
          }, {
            value: '"pan_tool"'
          }, {
            value: '"palette"'
          }, {
            value: '"pageview"'
          }, {
            value: '"pages"'
          }, {
            value: '"padding"'
          }, {
            value: '"quickreply"'
          }, {
            value: '"quick_contacts_mail"'
          }, {
            value: '"quick_contacts_dialer"'
          }, {
            value: '"queue_play_next"'
          }, {
            value: '"queue_music"'
          }, {
            value: '"queue"'
          }, {
            value: '"question_answer"'
          }, {
            value: '"query_builder"'
          }, {
            value: '"qr_code_scanner"'
          }, {
            value: '"qr_code"'
          }, {
            value: '"rv_hookup"'
          }, {
            value: '"run_circle"'
          }, {
            value: '"rule_folder"'
          }, {
            value: '"rule"'
          }, {
            value: '"rtt"'
          }, {
            value: '"rss_feed"'
          }, {
            value: '"rowing"'
          }, {
            value: '"router"'
          }, {
            value: '"rounded_corner"'
          }, {
            value: '"rotate_right"'
          }, {
            value: '"rotate_left"'
          }, {
            value: '"rotate_90_degrees_ccw"'
          }, {
            value: '"room_service"'
          }, {
            value: '"room_preferences"'
          }, {
            value: '"room"'
          }, {
            value: '"roofing"'
          }, {
            value: '"ring_volume"'
          }, {
            value: '"rice_bowl"'
          }, {
            value: '"restore_page"'
          }, {
            value: '"restore_from_trash"'
          }, {
            value: '"restore"'
          }, {
            value: '"restaurant_menu"'
          }, {
            value: '"restaurant"'
          }, {
            value: '"reset_tv"'
          }, {
            value: '"request_quote"'
          }, {
            value: '"request_page"'
          }, {
            value: '"report_problem"'
          }, {
            value: '"report_off"'
          }, {
            value: '"report"'
          }, {
            value: '"reply_all"'
          }, {
            value: '"reply"'
          }, {
            value: '"replay_circle_filled"'
          }, {
            value: '"replay_5"'
          }, {
            value: '"replay_30"'
          }, {
            value: '"replay_10"'
          }, {
            value: '"replay"'
          }, {
            value: '"repeat_one_on"'
          }, {
            value: '"repeat_one"'
          }, {
            value: '"repeat_on"'
          }, {
            value: '"repeat"'
          }, {
            value: '"reorder"'
          }, {
            value: '"remove_shopping_cart"'
          }, {
            value: '"remove_red_eye"'
          }, {
            value: '"remove_moderator"'
          }, {
            value: '"remove_from_queue"'
          }, {
            value: '"remove_done"'
          }, {
            value: '"remove_circle_outline"'
          }, {
            value: '"remove_circle"'
          }, {
            value: '"remove"'
          }, {
            value: '"refresh"'
          }, {
            value: '"reduce_capacity"'
          }, {
            value: '"redo"'
          }, {
            value: '"redeem"'
          }, {
            value: '"record_voice_over"'
          }, {
            value: '"recommend"'
          }, {
            value: '"recent_actors"'
          }, {
            value: '"receipt_long"'
          }, {
            value: '"receipt"'
          }, {
            value: '"read_more"'
          }, {
            value: '"rate_review"'
          }, {
            value: '"ramen_dining"'
          }, {
            value: '"railway_alert"'
          }, {
            value: '"radio_button_unchecked"'
          }, {
            value: '"radio_button_on"'
          }, {
            value: '"radio_button_off"'
          }, {
            value: '"radio_button_checked"'
          }, {
            value: '"radio"'
          }, {
            value: '"system_update_tv"'
          }, {
            value: '"system_update_alt"'
          }, {
            value: '"system_update"'
          }, {
            value: '"sync_problem"'
          }, {
            value: '"sync_disabled"'
          }, {
            value: '"sync_alt"'
          }, {
            value: '"sync"'
          }, {
            value: '"switch_video"'
          }, {
            value: '"switch_right"'
          }, {
            value: '"switch_left"'
          }, {
            value: '"switch_camera"'
          }, {
            value: '"switch_account"'
          }, {
            value: '"swipe"'
          }, {
            value: '"swap_vertical_circle"'
          }, {
            value: '"swap_vert_circle"'
          }, {
            value: '"swap_vert"'
          }, {
            value: '"swap_horizontal_circle"'
          }, {
            value: '"swap_horiz"'
          }, {
            value: '"swap_calls"'
          }, {
            value: '"surround_sound"'
          }, {
            value: '"support_agent"'
          }, {
            value: '"support"'
          }, {
            value: '"supervisor_account"'
          }, {
            value: '"supervised_user_circle"'
          }, {
            value: '"superscript"'
          }, {
            value: '"subway"'
          }, {
            value: '"subtitles_off"'
          }, {
            value: '"subtitles"'
          }, {
            value: '"subscriptions"'
          }, {
            value: '"subscript"'
          }, {
            value: '"subject"'
          }, {
            value: '"subdirectory_arrow_right"'
          }, {
            value: '"subdirectory_arrow_left"'
          }, {
            value: '"stroller"'
          }, {
            value: '"strikethrough_s"'
          }, {
            value: '"streetview"'
          }, {
            value: '"stream"'
          }, {
            value: '"straighten"'
          }, {
            value: '"storefront"'
          }, {
            value: '"store_mall_directory"'
          }, {
            value: '"store"'
          }, {
            value: '"storage"'
          }, {
            value: '"stop_screen_share"'
          }, {
            value: '"stop_circle"'
          }, {
            value: '"sticky_note_2"'
          }, {
            value: '"stay_primary_portrait"'
          }, {
            value: '"stay_primary_landscape"'
          }, {
            value: '"stay_current_portrait"'
          }, {
            value: '"stay_current_landscape"'
          }, {
            value: '"stars"'
          }, {
            value: '"star_rate"'
          }, {
            value: '"star_outline"'
          }, {
            value: '"star_half"'
          }, {
            value: '"star_border"'
          }, {
            value: '"star"'
          }, {
            value: '"stairs"'
          }, {
            value: '"stacked_line_chart"'
          }, {
            value: '"stacked_bar_chart"'
          }, {
            value: '"square_foot"'
          }, {
            value: '"sports_volleyball"'
          }, {
            value: '"sports_tennis"'
          }, {
            value: '"sports_soccer"'
          }, {
            value: '"sports_rugby"'
          }, {
            value: '"sports_motorsports"'
          }, {
            value: '"sports_mma"'
          }, {
            value: '"sports_kabaddi"'
          }, {
            value: '"sports_hockey"'
          }, {
            value: '"sports_handball"'
          }, {
            value: '"sports_golf"'
          }, {
            value: '"sports_football"'
          }, {
            value: '"sports_esports"'
          }, {
            value: '"sports_cricket"'
          }, {
            value: '"sports_basketball"'
          }, {
            value: '"sports_baseball"'
          }, {
            value: '"sports_bar"'
          }, {
            value: '"sports"'
          }, {
            value: '"spellcheck"'
          }, {
            value: '"speed"'
          }, {
            value: '"speaker_phone"'
          }, {
            value: '"speaker_notes_off"'
          }, {
            value: '"speaker_notes"'
          }, {
            value: '"speaker_group"'
          }, {
            value: '"speaker"'
          }, {
            value: '"space_bar"'
          }, {
            value: '"spa"'
          }, {
            value: '"south_west"'
          }, {
            value: '"south_east"'
          }, {
            value: '"south"'
          }, {
            value: '"sort_by_alpha"'
          }, {
            value: '"sort"'
          }, {
            value: '"soap"'
          }, {
            value: '"snooze"'
          }, {
            value: '"snippet_folder"'
          }, {
            value: '"sms_failed"'
          }, {
            value: '"sms"'
          }, {
            value: '"smoking_rooms"'
          }, {
            value: '"smoke_free"'
          }, {
            value: '"smartphone"'
          }, {
            value: '"smart_button"'
          }, {
            value: '"slow_motion_video"'
          }, {
            value: '"slideshow"'
          }, {
            value: '"skip_previous"'
          }, {
            value: '"skip_next"'
          }, {
            value: '"single_bed"'
          }, {
            value: '"sim_card_alert"'
          }, {
            value: '"sim_card"'
          }, {
            value: '"signal_wifi_off"'
          }, {
            value: '"signal_wifi_4_bar_lock"'
          }, {
            value: '"signal_wifi_4_bar"'
          }, {
            value: '"signal_wifi_0_bar"'
          }, {
            value: '"signal_cellular_off"'
          }, {
            value: '"signal_cellular_null"'
          }, {
            value: '"signal_cellular_no_sim"'
          }, {
            value: '"signal_cellular_connected_no_internet_4_bar"'
          }, {
            value: '"signal_cellular_alt"'
          }, {
            value: '"signal_cellular_4_bar"'
          }, {
            value: '"signal_cellular_0_bar"'
          }, {
            value: '"sick"'
          }, {
            value: '"shutter_speed"'
          }, {
            value: '"shuffle_on"'
          }, {
            value: '"shuffle"'
          }, {
            value: '"show_chart"'
          }, {
            value: '"short_text"'
          }, {
            value: '"shopping_cart"'
          }, {
            value: '"shopping_basket"'
          }, {
            value: '"shopping_bag"'
          }, {
            value: '"shop_two"'
          }, {
            value: '"shop"'
          }, {
            value: '"shield"'
          }, {
            value: '"share"'
          }, {
            value: '"settings_voice"'
          }, {
            value: '"settings_system_daydream"'
          }, {
            value: '"settings_remote"'
          }, {
            value: '"settings_power"'
          }, {
            value: '"settings_phone"'
          }, {
            value: '"settings_overscan"'
          }, {
            value: '"settings_input_svideo"'
          }, {
            value: '"settings_input_hdmi"'
          }, {
            value: '"settings_input_composite"'
          }, {
            value: '"settings_input_component"'
          }, {
            value: '"settings_input_antenna"'
          }, {
            value: '"settings_ethernet"'
          }, {
            value: '"settings_display"'
          }, {
            value: '"settings_cell"'
          }, {
            value: '"settings_brightness"'
          }, {
            value: '"settings_bluetooth"'
          }, {
            value: '"settings_backup_restore"'
          }, {
            value: '"settings_applications"'
          }, {
            value: '"settings"'
          }, {
            value: '"set_meal"'
          }, {
            value: '"sentiment_very_satisfied"'
          }, {
            value: '"sentiment_very_dissatisfied"'
          }, {
            value: '"sentiment_satisfied_alt"'
          }, {
            value: '"sentiment_satisfied"'
          }, {
            value: '"sentiment_neutral"'
          }, {
            value: '"sentiment_dissatisfied"'
          }, {
            value: '"sensor_window"'
          }, {
            value: '"sensor_door"'
          }, {
            value: '"send_to_mobile"'
          }, {
            value: '"send_and_archive"'
          }, {
            value: '"send"'
          }, {
            value: '"self_improvement"'
          }, {
            value: '"select_all"'
          }, {
            value: '"segment"'
          }, {
            value: '"search_off"'
          }, {
            value: '"sd_storage"'
          }, {
            value: '"sd_card"'
          }, {
            value: '"sd"'
          }, {
            value: '"screen_share"'
          }, {
            value: '"screen_search_desktop"'
          }, {
            value: '"screen_rotation"'
          }, {
            value: '"screen_lock_rotation"'
          }, {
            value: '"screen_lock_portrait"'
          }, {
            value: '"screen_lock_landscape"'
          }, {
            value: '"score"'
          }, {
            value: '"science"'
          }, {
            value: '"school"'
          }, {
            value: '"schedule_send"'
          }, {
            value: '"schedule"'
          }, {
            value: '"scatter_plot"'
          }, {
            value: '"scanner"'
          }, {
            value: '"saved_search"'
          }, {
            value: '"save_alt"'
          }, {
            value: '"save"'
          }, {
            value: '"satellite"'
          }, {
            value: '"sanitizer"'
          }, {
            value: '"two_wheeler"'
          }, {
            value: '"tv_off"'
          }, {
            value: '"tv"'
          }, {
            value: '"turned_in_not"'
          }, {
            value: '"turned_in"'
          }, {
            value: '"tune"'
          }, {
            value: '"tty"'
          }, {
            value: '"trip_origin"'
          }, {
            value: '"trending_up"'
          }, {
            value: '"trending_neutral"'
          }, {
            value: '"trending_flat"'
          }, {
            value: '"trending_down"'
          }, {
            value: '"transit_enterexit"'
          }, {
            value: '"transform"'
          }, {
            value: '"transfer_within_a_station"'
          }, {
            value: '"tram"'
          }, {
            value: '"train"'
          }, {
            value: '"traffic"'
          }, {
            value: '"track_changes"'
          }, {
            value: '"toys"'
          }, {
            value: '"tour"'
          }, {
            value: '"touch_app"'
          }, {
            value: '"topic"'
          }, {
            value: '"tonality"'
          }, {
            value: '"toll"'
          }, {
            value: '"toggle_on"'
          }, {
            value: '"toggle_off"'
          }, {
            value: '"today"'
          }, {
            value: '"toc"'
          }, {
            value: '"timer_off"'
          }, {
            value: '"timer_3"'
          }, {
            value: '"timer_10"'
          }, {
            value: '"timer"'
          }, {
            value: '"timeline"'
          }, {
            value: '"timelapse"'
          }, {
            value: '"time_to_leave"'
          }, {
            value: '"thumbs_up_down"'
          }, {
            value: '"thumb_up_off_alt"'
          }, {
            value: '"thumb_up_alt"'
          }, {
            value: '"thumb_up"'
          }, {
            value: '"thumb_down_off_alt"'
          }, {
            value: '"thumb_down_alt"'
          }, {
            value: '"thumb_down"'
          }, {
            value: '"theaters"'
          }, {
            value: '"theater_comedy"'
          }, {
            value: '"texture"'
          }, {
            value: '"textsms"'
          }, {
            value: '"text_snippet"'
          }, {
            value: '"text_rotation_none"'
          }, {
            value: '"text_rotation_down"'
          }, {
            value: '"text_rotation_angleup"'
          }, {
            value: '"text_rotation_angledown"'
          }, {
            value: '"text_rotate_vertical"'
          }, {
            value: '"text_rotate_up"'
          }, {
            value: '"text_format"'
          }, {
            value: '"text_fields"'
          }, {
            value: '"terrain"'
          }, {
            value: '"taxi_alert"'
          }, {
            value: '"tapas"'
          }, {
            value: '"tap_and_play"'
          }, {
            value: '"takeout_dining"'
          }, {
            value: '"tag_faces"'
          }, {
            value: '"tag"'
          }, {
            value: '"tablet_mac"'
          }, {
            value: '"tablet_android"'
          }, {
            value: '"tablet"'
          }, {
            value: '"table_view"'
          }, {
            value: '"table_rows"'
          }, {
            value: '"table_chart"'
          }, {
            value: '"tab_unselected"'
          }, {
            value: '"tab"'
          }, {
            value: '"usb"'
          }, {
            value: '"upload_file"'
          }, {
            value: '"upgrade"'
          }, {
            value: '"update"'
          }, {
            value: '"unsubscribe"'
          }, {
            value: '"unpublished"'
          }, {
            value: '"unfold_more"'
          }, {
            value: '"unfold_less"'
          }, {
            value: '"undo"'
          }, {
            value: '"unarchive"'
          }, {
            value: '"umbrella"'
          }, {
            value: '"vpn_lock"'
          }, {
            value: '"vpn_key"'
          }, {
            value: '"volunteer_activism"'
          }, {
            value: '"volume_up"'
          }, {
            value: '"volume_off"'
          }, {
            value: '"volume_mute"'
          }, {
            value: '"volume_down"'
          }, {
            value: '"voicemail"'
          }, {
            value: '"voice_over_off"'
          }, {
            value: '"voice_chat"'
          }, {
            value: '"visibility_off"'
          }, {
            value: '"visibility"'
          }, {
            value: '"vignette"'
          }, {
            value: '"view_week"'
          }, {
            value: '"view_stream"'
          }, {
            value: '"view_sidebar"'
          }, {
            value: '"view_quilt"'
          }, {
            value: '"view_module"'
          }, {
            value: '"view_list"'
          }, {
            value: '"view_in_ar"'
          }, {
            value: '"view_headline"'
          }, {
            value: '"view_day"'
          }, {
            value: '"view_compact"'
          }, {
            value: '"view_comfy"'
          }, {
            value: '"view_comfortable"'
          }, {
            value: '"view_column"'
          }, {
            value: '"view_carousel"'
          }, {
            value: '"view_array"'
          }, {
            value: '"view_agenda"'
          }, {
            value: '"videogame_asset"'
          }, {
            value: '"videocam_off"'
          }, {
            value: '"videocam"'
          }, {
            value: '"video_settings"'
          }, {
            value: '"video_library"'
          }, {
            value: '"video_label"'
          }, {
            value: '"video_collection"'
          }, {
            value: '"video_call"'
          }, {
            value: '"vibration"'
          }, {
            value: '"vertical_split"'
          }, {
            value: '"vertical_align_top"'
          }, {
            value: '"vertical_align_center"'
          }, {
            value: '"vertical_align_bottom"'
          }, {
            value: '"verified_user"'
          }, {
            value: '"verified"'
          }, {
            value: '"wysiwyg"'
          }, {
            value: '"wrong_location"'
          }, {
            value: '"wrap_text"'
          }, {
            value: '"workspaces_outline"'
          }, {
            value: '"workspaces_filled"'
          }, {
            value: '"work_outline"'
          }, {
            value: '"work_off"'
          }, {
            value: '"work"'
          }, {
            value: '"wine_bar"'
          }, {
            value: '"wifi_tethering"'
          }, {
            value: '"wifi_protected_setup"'
          }, {
            value: '"wifi_off"'
          }, {
            value: '"wifi_lock"'
          }, {
            value: '"wifi_calling"'
          }, {
            value: '"wifi"'
          }, {
            value: '"widgets"'
          }, {
            value: '"where_to_vote"'
          }, {
            value: '"wheelchair_pickup"'
          }, {
            value: '"whatshot"'
          }, {
            value: '"west"'
          }, {
            value: '"weekend"'
          }, {
            value: '"web_asset"'
          }, {
            value: '"web"'
          }, {
            value: '"wc"'
          }, {
            value: '"wb_twighlight"'
          }, {
            value: '"wb_sunny"'
          }, {
            value: '"wb_shade"'
          }, {
            value: '"wb_iridescent"'
          }, {
            value: '"wb_incandescent"'
          }, {
            value: '"wb_cloudy"'
          }, {
            value: '"wb_auto"'
          }, {
            value: '"waves"'
          }, {
            value: '"waterfall_chart"'
          }, {
            value: '"water_damage"'
          }, {
            value: '"watch_later"'
          }, {
            value: '"watch"'
          }, {
            value: '"wash"'
          }, {
            value: '"wallpaper"'
          }, {
            value: '"wallet_travel"'
          }, {
            value: '"wallet_membership"'
          }, {
            value: '"wallet_giftcard"'
          }, {
            value: '"youtube_searched_for"'
          }, {
            value: '"zoom_out_map"'
          }, {
            value: '"zoom_out"'
          }, {
            value: '"zoom_in"'
          }, {
            value: '"do_disturb_alt"'
          }, {
            value: '"do_disturb_off"'
          }, {
            value: '"do_disturb_on"'
          }, {
            value: '"download_done"'
          }, {
            value: '"do_disturb"'
          }, {
            value: '"download"'
          }, {
            value: '"report_gmailerrorred"'
          }, {
            value: '"thermostat"'
          }, {
            value: '"attribution"'
          }, {
            value: '"no_encryption_gmailerrorred"'
          }, {
            value: '"face_unlock"'
          }, {
            value: '"precision_manufacturing"'
          }, {
            value: '"pie_chart_outline"'
          }, {
            value: '"paste"'
          }, {
            value: '"copy"'
          }, {
            value: '"cut"'
          }, {
            value: '"upload"'
          }, {
            value: '"star_border_purple500"'
          }, {
            value: '"star_purple500"'
          }, {
            value: '"sd_card_alert"'
          }, {
            value: '"money_off_csred"'
          }, {
            value: '"mode"'
          }, {
            value: '"warning_amber"'
          }]
        }
      },
      icon: {
        defaultValue: null,
        description: '',
        name: 'icon',
        required: false,
        type: {
          name: 'enum',
          raw: 'AllIconsType',
          value: [{
            value: '"style"'
          }, {
            value: '"title"'
          }, {
            value: '"translate"'
          }, {
            value: '"security"'
          }, {
            value: '"message"'
          }, {
            value: '"info"'
          }, {
            value: '"warning"'
          }, {
            value: '"error"'
          }, {
            value: '"label"'
          }, {
            value: '"article"'
          }, {
            value: '"code"'
          }, {
            value: '"details"'
          }, {
            value: '"input"'
          }, {
            value: '"link"'
          }, {
            value: '"map"'
          }, {
            value: '"menu"'
          }, {
            value: '"source"'
          }, {
            value: '"table"'
          }, {
            value: '"template"'
          }, {
            value: '"circle"'
          }, {
            value: '"filter"'
          }, {
            value: '"image"'
          }, {
            value: '"stop"'
          }, {
            value: '"email"'
          }, {
            value: '"search"'
          }, {
            value: '"drag_corner"'
          }, {
            value: '"exclude"'
          }, {
            value: '"export"'
          }, {
            value: '"filter_filled"'
          }, {
            value: '"import"'
          }, {
            value: '"list_remove"'
          }, {
            value: '"pin"'
          }, {
            value: '"pin_left"'
          }, {
            value: '"pin_right"'
          }, {
            value: '"unpin"'
          }, {
            value: '"bar_chart"'
          }, {
            value: '"donut_chart"'
          }, {
            value: '"line_chart"'
          }, {
            value: '"stacked_chart"'
          }, {
            value: '"file_chart"'
          }, {
            value: '"file_code"'
          }, {
            value: '"file_pdf"'
          }, {
            value: '"file_table"'
          }, {
            value: '"tree_view"'
          }, {
            value: '"advanced_search"'
          }, {
            value: '"asterisk"'
          }, {
            value: '"currency_exchange"'
          }, {
            value: '"import_export"'
          }, {
            value: '"knowledgebase"'
          }, {
            value: '"move_down"'
          }, {
            value: '"move_up"'
          }, {
            value: '"collapse_column"'
          }, {
            value: '"collapse_down"'
          }, {
            value: '"collapse_horiz"'
          }, {
            value: '"collapse_left"'
          }, {
            value: '"collapse_right"'
          }, {
            value: '"collapse_up"'
          }, {
            value: '"collapse_vert"'
          }, {
            value: '"expand_row"'
          }, {
            value: '"move"'
          }, {
            value: '"analytics"'
          }, {
            value: '"billing"'
          }, {
            value: '"collect"'
          }, {
            value: '"commerce"'
          }, {
            value: '"customers"'
          }, {
            value: '"finance"'
          }, {
            value: '"home"'
          }, {
            value: '"insights"'
          }, {
            value: '"marketplace"'
          }, {
            value: '"payments"'
          }, {
            value: '"platform"'
          }, {
            value: '"products"'
          }, {
            value: '"profile"'
          }, {
            value: '"reporting"'
          }, {
            value: '"revpro"'
          }, {
            value: '"workflow"'
          }, {
            value: '"column_group"'
          }, {
            value: '"fields"'
          }, {
            value: '"insert_row"'
          }, {
            value: '"remove_row"'
          }, {
            value: '"row_group"'
          }, {
            value: '"1k_plus"'
          }, {
            value: '"19mp"'
          }, {
            value: '"18mp"'
          }, {
            value: '"17mp"'
          }, {
            value: '"16mp"'
          }, {
            value: '"15mp"'
          }, {
            value: '"14mp"'
          }, {
            value: '"13mp"'
          }, {
            value: '"12mp"'
          }, {
            value: '"11mp"'
          }, {
            value: '"10mp"'
          }, {
            value: '"10k"'
          }, {
            value: '"1k"'
          }, {
            value: '"2k_plus"'
          }, {
            value: '"24mp"'
          }, {
            value: '"23mp"'
          }, {
            value: '"22mp"'
          }, {
            value: '"21mp"'
          }, {
            value: '"20mp"'
          }, {
            value: '"2mp"'
          }, {
            value: '"2k"'
          }, {
            value: '"3d_rotation"'
          }, {
            value: '"3k_plus"'
          }, {
            value: '"3mp"'
          }, {
            value: '"360"'
          }, {
            value: '"3k"'
          }, {
            value: '"4k_plus"'
          }, {
            value: '"4mp"'
          }, {
            value: '"4k"'
          }, {
            value: '"5mp"'
          }, {
            value: '"5k_plus"'
          }, {
            value: '"5k"'
          }, {
            value: '"5g"'
          }, {
            value: '"6mp"'
          }, {
            value: '"6k_plus"'
          }, {
            value: '"6k"'
          }, {
            value: '"6_ft_apart"'
          }, {
            value: '"7k_plus"'
          }, {
            value: '"7mp"'
          }, {
            value: '"7k"'
          }, {
            value: '"8k_plus"'
          }, {
            value: '"8mp"'
          }, {
            value: '"8k"'
          }, {
            value: '"9k_plus"'
          }, {
            value: '"9mp"'
          }, {
            value: '"9k"'
          }, {
            value: '"av_timer"'
          }, {
            value: '"autorenew"'
          }, {
            value: '"auto_stories"'
          }, {
            value: '"auto_fix_off"'
          }, {
            value: '"auto_fix_normal"'
          }, {
            value: '"auto_fix_high"'
          }, {
            value: '"auto_delete"'
          }, {
            value: '"auto_awesome_motion"'
          }, {
            value: '"auto_awesome_mosaic"'
          }, {
            value: '"auto_awesome"'
          }, {
            value: '"audiotrack"'
          }, {
            value: '"attractions"'
          }, {
            value: '"attachment"'
          }, {
            value: '"attach_money"'
          }, {
            value: '"attach_file"'
          }, {
            value: '"attach_email"'
          }, {
            value: '"atm"'
          }, {
            value: '"assistant_photo"'
          }, {
            value: '"assistant_navigation"'
          }, {
            value: '"assistant_direction"'
          }, {
            value: '"assistant"'
          }, {
            value: '"assignment_turned_in"'
          }, {
            value: '"assignment_returned"'
          }, {
            value: '"assignment_return"'
          }, {
            value: '"assignment_late"'
          }, {
            value: '"assignment_ind"'
          }, {
            value: '"assignment"'
          }, {
            value: '"assessment"'
          }, {
            value: '"aspect_ratio"'
          }, {
            value: '"art_track"'
          }, {
            value: '"arrow_upward"'
          }, {
            value: '"arrow_right_alt"'
          }, {
            value: '"arrow_right"'
          }, {
            value: '"arrow_left"'
          }, {
            value: '"arrow_forward_ios"'
          }, {
            value: '"arrow_forward"'
          }, {
            value: '"arrow_drop_up"'
          }, {
            value: '"arrow_drop_down_circle"'
          }, {
            value: '"arrow_drop_down"'
          }, {
            value: '"arrow_downward"'
          }, {
            value: '"arrow_circle_up"'
          }, {
            value: '"arrow_circle_down"'
          }, {
            value: '"arrow_back_ios"'
          }, {
            value: '"arrow_back"'
          }, {
            value: '"archive"'
          }, {
            value: '"architecture"'
          }, {
            value: '"apps"'
          }, {
            value: '"approval"'
          }, {
            value: '"app_settings_alt"'
          }, {
            value: '"app_registration"'
          }, {
            value: '"app_blocking"'
          }, {
            value: '"api"'
          }, {
            value: '"apartment"'
          }, {
            value: '"announcement"'
          }, {
            value: '"animation"'
          }, {
            value: '"android"'
          }, {
            value: '"anchor"'
          }, {
            value: '"amp_stories"'
          }, {
            value: '"alternate_email"'
          }, {
            value: '"alt_route"'
          }, {
            value: '"all_out"'
          }, {
            value: '"all_inclusive"'
          }, {
            value: '"all_inbox"'
          }, {
            value: '"album"'
          }, {
            value: '"alarm_on"'
          }, {
            value: '"alarm_off"'
          }, {
            value: '"alarm_add"'
          }, {
            value: '"alarm"'
          }, {
            value: '"airport_shuttle"'
          }, {
            value: '"airplay"'
          }, {
            value: '"airplanemode_on"'
          }, {
            value: '"airplanemode_off"'
          }, {
            value: '"airplanemode_inactive"'
          }, {
            value: '"airplanemode_active"'
          }, {
            value: '"airline_seat_recline_normal"'
          }, {
            value: '"airline_seat_recline_extra"'
          }, {
            value: '"airline_seat_legroom_reduced"'
          }, {
            value: '"airline_seat_legroom_normal"'
          }, {
            value: '"airline_seat_legroom_extra"'
          }, {
            value: '"airline_seat_individual_suite"'
          }, {
            value: '"airline_seat_flat_angled"'
          }, {
            value: '"airline_seat_flat"'
          }, {
            value: '"agriculture"'
          }, {
            value: '"admin_panel_settings"'
          }, {
            value: '"adjust"'
          }, {
            value: '"addchart"'
          }, {
            value: '"add_to_queue"'
          }, {
            value: '"add_to_photos"'
          }, {
            value: '"add_to_home_screen"'
          }, {
            value: '"add_to_drive"'
          }, {
            value: '"add_task"'
          }, {
            value: '"add_shopping_cart"'
          }, {
            value: '"add_road"'
          }, {
            value: '"add_photo_alternate"'
          }, {
            value: '"add_moderator"'
          }, {
            value: '"add_location_alt"'
          }, {
            value: '"add_location"'
          }, {
            value: '"add_link"'
          }, {
            value: '"add_ic_call"'
          }, {
            value: '"add_comment"'
          }, {
            value: '"add_circle_outline"'
          }, {
            value: '"add_circle"'
          }, {
            value: '"add_chart"'
          }, {
            value: '"add_call"'
          }, {
            value: '"add_business"'
          }, {
            value: '"add_box"'
          }, {
            value: '"add_alert"'
          }, {
            value: '"add_alarm"'
          }, {
            value: '"add_a_photo"'
          }, {
            value: '"add"'
          }, {
            value: '"adb"'
          }, {
            value: '"ad_units"'
          }, {
            value: '"account_tree"'
          }, {
            value: '"account_circle"'
          }, {
            value: '"account_box"'
          }, {
            value: '"account_balance_wallet"'
          }, {
            value: '"account_balance"'
          }, {
            value: '"accessible_forward"'
          }, {
            value: '"accessible"'
          }, {
            value: '"accessibility_new"'
          }, {
            value: '"accessibility"'
          }, {
            value: '"access_time"'
          }, {
            value: '"access_alarms"'
          }, {
            value: '"access_alarm"'
          }, {
            value: '"ac_unit"'
          }, {
            value: '"business_center"'
          }, {
            value: '"business"'
          }, {
            value: '"bus_alert"'
          }, {
            value: '"burst_mode"'
          }, {
            value: '"build_circle"'
          }, {
            value: '"build"'
          }, {
            value: '"bug_report"'
          }, {
            value: '"bubble_chart"'
          }, {
            value: '"brush"'
          }, {
            value: '"brunch_dining"'
          }, {
            value: '"browser_not_supported"'
          }, {
            value: '"broken_image"'
          }, {
            value: '"brightness_medium"'
          }, {
            value: '"brightness_low"'
          }, {
            value: '"brightness_high"'
          }, {
            value: '"brightness_auto"'
          }, {
            value: '"brightness_7"'
          }, {
            value: '"brightness_6"'
          }, {
            value: '"brightness_5"'
          }, {
            value: '"brightness_4"'
          }, {
            value: '"brightness_3"'
          }, {
            value: '"brightness_2"'
          }, {
            value: '"brightness_1"'
          }, {
            value: '"breakfast_dining"'
          }, {
            value: '"branding_watermark"'
          }, {
            value: '"border_vertical"'
          }, {
            value: '"border_top"'
          }, {
            value: '"border_style"'
          }, {
            value: '"border_right"'
          }, {
            value: '"border_outer"'
          }, {
            value: '"border_left"'
          }, {
            value: '"border_inner"'
          }, {
            value: '"border_horizontal"'
          }, {
            value: '"border_color"'
          }, {
            value: '"border_clear"'
          }, {
            value: '"border_bottom"'
          }, {
            value: '"border_all"'
          }, {
            value: '"bookmarks"'
          }, {
            value: '"bookmark_outline"'
          }, {
            value: '"bookmark_border"'
          }, {
            value: '"bookmark"'
          }, {
            value: '"book_online"'
          }, {
            value: '"book"'
          }, {
            value: '"bolt"'
          }, {
            value: '"blur_on"'
          }, {
            value: '"blur_off"'
          }, {
            value: '"blur_linear"'
          }, {
            value: '"blur_circular"'
          }, {
            value: '"bluetooth_searching"'
          }, {
            value: '"bluetooth_disabled"'
          }, {
            value: '"bluetooth_connected"'
          }, {
            value: '"bluetooth_audio"'
          }, {
            value: '"bluetooth"'
          }, {
            value: '"block_flipped"'
          }, {
            value: '"block"'
          }, {
            value: '"biotech"'
          }, {
            value: '"bike_scooter"'
          }, {
            value: '"bento"'
          }, {
            value: '"beenhere"'
          }, {
            value: '"bedtime"'
          }, {
            value: '"beach_access"'
          }, {
            value: '"battery_unknown"'
          }, {
            value: '"battery_std"'
          }, {
            value: '"battery_full"'
          }, {
            value: '"battery_charging_full"'
          }, {
            value: '"battery_alert"'
          }, {
            value: '"bathtub"'
          }, {
            value: '"batch_prediction"'
          }, {
            value: '"ballot"'
          }, {
            value: '"bakery_dining"'
          }, {
            value: '"badge"'
          }, {
            value: '"backup_table"'
          }, {
            value: '"backup"'
          }, {
            value: '"backspace"'
          }, {
            value: '"backpack"'
          }, {
            value: '"baby_changing_station"'
          }, {
            value: '"crop_square"'
          }, {
            value: '"crop_rotate"'
          }, {
            value: '"crop_portrait"'
          }, {
            value: '"crop_original"'
          }, {
            value: '"crop_landscape"'
          }, {
            value: '"crop_free"'
          }, {
            value: '"crop_din"'
          }, {
            value: '"crop_7_5"'
          }, {
            value: '"crop_5_4"'
          }, {
            value: '"crop_3_2"'
          }, {
            value: '"crop_16_9"'
          }, {
            value: '"crop"'
          }, {
            value: '"credit_card"'
          }, {
            value: '"create_new_folder"'
          }, {
            value: '"create"'
          }, {
            value: '"countertops"'
          }, {
            value: '"corporate_fare"'
          }, {
            value: '"coronavirus"'
          }, {
            value: '"copyright"'
          }, {
            value: '"control_point_duplicate"'
          }, {
            value: '"control_point"'
          }, {
            value: '"control_camera"'
          }, {
            value: '"content_paste"'
          }, {
            value: '"content_cut"'
          }, {
            value: '"content_copy"'
          }, {
            value: '"contacts"'
          }, {
            value: '"contactless"'
          }, {
            value: '"contact_support"'
          }, {
            value: '"contact_phone"'
          }, {
            value: '"contact_page"'
          }, {
            value: '"contact_mail"'
          }, {
            value: '"construction"'
          }, {
            value: '"connected_tv"'
          }, {
            value: '"connect_without_contact"'
          }, {
            value: '"confirmation_number"'
          }, {
            value: '"confirmation_num"'
          }, {
            value: '"computer"'
          }, {
            value: '"compress"'
          }, {
            value: '"compass_calibration"'
          }, {
            value: '"compare_arrows"'
          }, {
            value: '"compare"'
          }, {
            value: '"commute"'
          }, {
            value: '"comment_bank"'
          }, {
            value: '"comment"'
          }, {
            value: '"colorize"'
          }, {
            value: '"color_lens"'
          }, {
            value: '"collections_bookmark"'
          }, {
            value: '"collections"'
          }, {
            value: '"cloud_upload"'
          }, {
            value: '"cloud_queue"'
          }, {
            value: '"cloud_off"'
          }, {
            value: '"cloud_download"'
          }, {
            value: '"cloud_done"'
          }, {
            value: '"cloud_circle"'
          }, {
            value: '"cloud"'
          }, {
            value: '"closed_caption_off"'
          }, {
            value: '"closed_caption_disabled"'
          }, {
            value: '"closed_caption"'
          }, {
            value: '"close_fullscreen"'
          }, {
            value: '"close"'
          }, {
            value: '"clear_all"'
          }, {
            value: '"clear"'
          }, {
            value: '"cleaning_services"'
          }, {
            value: '"clean_hands"'
          }, {
            value: '"class"'
          }, {
            value: '"circle_notifications"'
          }, {
            value: '"chrome_reader_mode"'
          }, {
            value: '"child_friendly"'
          }, {
            value: '"child_care"'
          }, {
            value: '"chevron_right"'
          }, {
            value: '"chevron_left"'
          }, {
            value: '"checkroom"'
          }, {
            value: '"check_circle_outline"'
          }, {
            value: '"check_circle"'
          }, {
            value: '"check_box_outline_blank"'
          }, {
            value: '"check_box"'
          }, {
            value: '"check"'
          }, {
            value: '"chat_bubble_outline"'
          }, {
            value: '"chat_bubble"'
          }, {
            value: '"chat"'
          }, {
            value: '"charging_station"'
          }, {
            value: '"change_history"'
          }, {
            value: '"center_focus_weak"'
          }, {
            value: '"center_focus_strong"'
          }, {
            value: '"cell_wifi"'
          }, {
            value: '"celebration"'
          }, {
            value: '"category"'
          }, {
            value: '"cast_for_education"'
          }, {
            value: '"cast_connected"'
          }, {
            value: '"cast"'
          }, {
            value: '"casino"'
          }, {
            value: '"cases"'
          }, {
            value: '"carpenter"'
          }, {
            value: '"card_travel"'
          }, {
            value: '"card_membership"'
          }, {
            value: '"card_giftcard"'
          }, {
            value: '"car_repair"'
          }, {
            value: '"car_rental"'
          }, {
            value: '"cancel_schedule_send"'
          }, {
            value: '"cancel_presentation"'
          }, {
            value: '"cancel"'
          }, {
            value: '"campaign"'
          }, {
            value: '"camera_roll"'
          }, {
            value: '"camera_rear"'
          }, {
            value: '"camera_front"'
          }, {
            value: '"camera_enhance"'
          }, {
            value: '"camera_alt"'
          }, {
            value: '"camera"'
          }, {
            value: '"call_to_action"'
          }, {
            value: '"call_split"'
          }, {
            value: '"call_received"'
          }, {
            value: '"call_missed_outgoing"'
          }, {
            value: '"call_missed"'
          }, {
            value: '"call_merge"'
          }, {
            value: '"call_made"'
          }, {
            value: '"call_end"'
          }, {
            value: '"call"'
          }, {
            value: '"calendar_view_day"'
          }, {
            value: '"calendar_today"'
          }, {
            value: '"calculate"'
          }, {
            value: '"cake"'
          }, {
            value: '"cached"'
          }, {
            value: '"dynamic_form"'
          }, {
            value: '"dynamic_feed"'
          }, {
            value: '"dvr"'
          }, {
            value: '"duo"'
          }, {
            value: '"dry_cleaning"'
          }, {
            value: '"dry"'
          }, {
            value: '"drive_folder_upload"'
          }, {
            value: '"drive_file_rename_outline"'
          }, {
            value: '"drive_file_move_outline"'
          }, {
            value: '"drive_file_move"'
          }, {
            value: '"drive_eta"'
          }, {
            value: '"drag_indicator"'
          }, {
            value: '"drag_handle"'
          }, {
            value: '"drafts"'
          }, {
            value: '"double_arrow"'
          }, {
            value: '"donut_small"'
          }, {
            value: '"donut_large"'
          }, {
            value: '"done_outline"'
          }, {
            value: '"done_all"'
          }, {
            value: '"done"'
          }, {
            value: '"domain_verification"'
          }, {
            value: '"domain_disabled"'
          }, {
            value: '"domain"'
          }, {
            value: '"dock"'
          }, {
            value: '"do_not_touch"'
          }, {
            value: '"do_not_step"'
          }, {
            value: '"do_not_disturb_on"'
          }, {
            value: '"do_not_disturb_off"'
          }, {
            value: '"do_not_disturb_alt"'
          }, {
            value: '"do_not_disturb"'
          }, {
            value: '"dns"'
          }, {
            value: '"dnd_forwardslash"'
          }, {
            value: '"disc_full"'
          }, {
            value: '"disabled_by_default"'
          }, {
            value: '"dirty_lens"'
          }, {
            value: '"directions_walk"'
          }, {
            value: '"directions_transit"'
          }, {
            value: '"directions_train"'
          }, {
            value: '"directions_subway"'
          }, {
            value: '"directions_run"'
          }, {
            value: '"directions_railway"'
          }, {
            value: '"directions_off"'
          }, {
            value: '"directions_ferry"'
          }, {
            value: '"directions_car"'
          }, {
            value: '"directions_bus"'
          }, {
            value: '"directions_boat"'
          }, {
            value: '"directions_bike"'
          }, {
            value: '"directions"'
          }, {
            value: '"dinner_dining"'
          }, {
            value: '"dialpad"'
          }, {
            value: '"dialer_sip"'
          }, {
            value: '"devices_other"'
          }, {
            value: '"devices"'
          }, {
            value: '"device_unknown"'
          }, {
            value: '"device_thermostat"'
          }, {
            value: '"device_hub"'
          }, {
            value: '"developer_mode"'
          }, {
            value: '"developer_board"'
          }, {
            value: '"desktop_windows"'
          }, {
            value: '"desktop_mac"'
          }, {
            value: '"desktop_access_disabled"'
          }, {
            value: '"design_services"'
          }, {
            value: '"description"'
          }, {
            value: '"departure_board"'
          }, {
            value: '"delivery_dining"'
          }, {
            value: '"delete_sweep"'
          }, {
            value: '"delete_outline"'
          }, {
            value: '"delete_forever"'
          }, {
            value: '"delete"'
          }, {
            value: '"dehaze"'
          }, {
            value: '"deck"'
          }, {
            value: '"date_range"'
          }, {
            value: '"data_usage"'
          }, {
            value: '"dashboard_customize"'
          }, {
            value: '"dashboard"'
          }, {
            value: '"dangerous"'
          }, {
            value: '"extension"'
          }, {
            value: '"exposure_zero"'
          }, {
            value: '"exposure_plus_2"'
          }, {
            value: '"exposure_plus_1"'
          }, {
            value: '"exposure_neg_2"'
          }, {
            value: '"exposure_neg_1"'
          }, {
            value: '"exposure_minus_2"'
          }, {
            value: '"exposure_minus_1"'
          }, {
            value: '"exposure"'
          }, {
            value: '"explore_off"'
          }, {
            value: '"explore"'
          }, {
            value: '"explicit"'
          }, {
            value: '"expand_more"'
          }, {
            value: '"expand_less"'
          }, {
            value: '"expand"'
          }, {
            value: '"exit_to_app"'
          }, {
            value: '"event_seat"'
          }, {
            value: '"event_note"'
          }, {
            value: '"event_busy"'
          }, {
            value: '"event_available"'
          }, {
            value: '"event"'
          }, {
            value: '"ev_station"'
          }, {
            value: '"euro_symbol"'
          }, {
            value: '"euro"'
          }, {
            value: '"escalator_warning"'
          }, {
            value: '"escalator"'
          }, {
            value: '"error_outline"'
          }, {
            value: '"equalizer"'
          }, {
            value: '"enhanced_encryption"'
          }, {
            value: '"enhance_photo_translate"'
          }, {
            value: '"engineering"'
          }, {
            value: '"emoji_transportation"'
          }, {
            value: '"emoji_symbols"'
          }, {
            value: '"emoji_people"'
          }, {
            value: '"emoji_objects"'
          }, {
            value: '"emoji_nature"'
          }, {
            value: '"emoji_food_beverage"'
          }, {
            value: '"emoji_flags"'
          }, {
            value: '"emoji_events"'
          }, {
            value: '"emoji_emotions"'
          }, {
            value: '"elevator"'
          }, {
            value: '"electrical_services"'
          }, {
            value: '"electric_scooter"'
          }, {
            value: '"electric_rickshaw"'
          }, {
            value: '"electric_moped"'
          }, {
            value: '"electric_car"'
          }, {
            value: '"electric_bike"'
          }, {
            value: '"elderly"'
          }, {
            value: '"eject"'
          }, {
            value: '"edit_road"'
          }, {
            value: '"edit_off"'
          }, {
            value: '"edit_location"'
          }, {
            value: '"edit_attributes"'
          }, {
            value: '"edit"'
          }, {
            value: '"eco"'
          }, {
            value: '"east"'
          }, {
            value: '"functions"'
          }, {
            value: '"fullscreen_exit"'
          }, {
            value: '"fullscreen"'
          }, {
            value: '"free_breakfast"'
          }, {
            value: '"foundation"'
          }, {
            value: '"forward_to_inbox"'
          }, {
            value: '"forward_5"'
          }, {
            value: '"forward_30"'
          }, {
            value: '"forward_10"'
          }, {
            value: '"forward"'
          }, {
            value: '"forum"'
          }, {
            value: '"format_underlined"'
          }, {
            value: '"format_underline"'
          }, {
            value: '"format_textdirection_r_to_l"'
          }, {
            value: '"format_textdirection_l_to_r"'
          }, {
            value: '"format_strikethrough"'
          }, {
            value: '"format_size"'
          }, {
            value: '"format_shapes"'
          }, {
            value: '"format_quote"'
          }, {
            value: '"format_paint"'
          }, {
            value: '"format_list_numbered_rtl"'
          }, {
            value: '"format_list_numbered"'
          }, {
            value: '"format_list_bulleted"'
          }, {
            value: '"format_line_spacing"'
          }, {
            value: '"format_italic"'
          }, {
            value: '"format_indent_increase"'
          }, {
            value: '"format_indent_decrease"'
          }, {
            value: '"format_color_text"'
          }, {
            value: '"format_color_reset"'
          }, {
            value: '"format_color_fill"'
          }, {
            value: '"format_clear"'
          }, {
            value: '"format_bold"'
          }, {
            value: '"format_align_right"'
          }, {
            value: '"format_align_left"'
          }, {
            value: '"format_align_justify"'
          }, {
            value: '"format_align_center"'
          }, {
            value: '"food_bank"'
          }, {
            value: '"font_download"'
          }, {
            value: '"follow_the_signs"'
          }, {
            value: '"folder_special"'
          }, {
            value: '"folder_shared"'
          }, {
            value: '"folder_open"'
          }, {
            value: '"folder"'
          }, {
            value: '"flip_to_front"'
          }, {
            value: '"flip_to_back"'
          }, {
            value: '"flip_camera_ios"'
          }, {
            value: '"flip_camera_android"'
          }, {
            value: '"flip"'
          }, {
            value: '"flight_takeoff"'
          }, {
            value: '"flight_land"'
          }, {
            value: '"flight"'
          }, {
            value: '"flash_on"'
          }, {
            value: '"flash_off"'
          }, {
            value: '"flash_auto"'
          }, {
            value: '"flare"'
          }, {
            value: '"flaky"'
          }, {
            value: '"flag"'
          }, {
            value: '"fitness_center"'
          }, {
            value: '"fit_screen"'
          }, {
            value: '"first_page"'
          }, {
            value: '"fireplace"'
          }, {
            value: '"fire_hydrant"'
          }, {
            value: '"fire_extinguisher"'
          }, {
            value: '"fingerprint"'
          }, {
            value: '"find_replace"'
          }, {
            value: '"find_in_page"'
          }, {
            value: '"filter_vintage"'
          }, {
            value: '"filter_tilt_shift"'
          }, {
            value: '"filter_none"'
          }, {
            value: '"filter_list_alt"'
          }, {
            value: '"filter_list"'
          }, {
            value: '"filter_hdr"'
          }, {
            value: '"filter_frames"'
          }, {
            value: '"filter_drama"'
          }, {
            value: '"filter_center_focus"'
          }, {
            value: '"filter_b_and_w"'
          }, {
            value: '"filter_alt"'
          }, {
            value: '"filter_9_plus"'
          }, {
            value: '"filter_9"'
          }, {
            value: '"filter_8"'
          }, {
            value: '"filter_7"'
          }, {
            value: '"filter_6"'
          }, {
            value: '"filter_5"'
          }, {
            value: '"filter_4"'
          }, {
            value: '"filter_3"'
          }, {
            value: '"filter_2"'
          }, {
            value: '"filter_1"'
          }, {
            value: '"file_upload"'
          }, {
            value: '"file_present"'
          }, {
            value: '"file_download_done"'
          }, {
            value: '"file_download"'
          }, {
            value: '"file_copy"'
          }, {
            value: '"fiber_smart_record"'
          }, {
            value: '"fiber_pin"'
          }, {
            value: '"fiber_new"'
          }, {
            value: '"fiber_manual_record"'
          }, {
            value: '"fiber_dvr"'
          }, {
            value: '"festival"'
          }, {
            value: '"fence"'
          }, {
            value: '"feedback"'
          }, {
            value: '"featured_video"'
          }, {
            value: '"featured_play_list"'
          }, {
            value: '"favorite_outline"'
          }, {
            value: '"favorite_border"'
          }, {
            value: '"favorite"'
          }, {
            value: '"fastfood"'
          }, {
            value: '"fast_rewind"'
          }, {
            value: '"fast_forward"'
          }, {
            value: '"family_restroom"'
          }, {
            value: '"fact_check"'
          }, {
            value: '"facebook"'
          }, {
            value: '"face_retouching_natural"'
          }, {
            value: '"face"'
          }, {
            value: '"groups"'
          }, {
            value: '"group_work"'
          }, {
            value: '"group_add"'
          }, {
            value: '"group"'
          }, {
            value: '"grid_view"'
          }, {
            value: '"grid_on"'
          }, {
            value: '"grid_off"'
          }, {
            value: '"grass"'
          }, {
            value: '"graphic_eq"'
          }, {
            value: '"grain"'
          }, {
            value: '"grading"'
          }, {
            value: '"gradient"'
          }, {
            value: '"grade"'
          }, {
            value: '"gps_off"'
          }, {
            value: '"gps_not_fixed"'
          }, {
            value: '"gps_fixed"'
          }, {
            value: '"golf_course"'
          }, {
            value: '"goat"'
          }, {
            value: '"gif"'
          }, {
            value: '"get_app"'
          }, {
            value: '"gesture"'
          }, {
            value: '"gavel"'
          }, {
            value: '"games"'
          }, {
            value: '"gamepad"'
          }, {
            value: '"g_translate"'
          }, {
            value: '"hvac"'
          }, {
            value: '"https"'
          }, {
            value: '"http"'
          }, {
            value: '"how_to_vote"'
          }, {
            value: '"how_to_reg"'
          }, {
            value: '"house_siding"'
          }, {
            value: '"house"'
          }, {
            value: '"hourglass_top"'
          }, {
            value: '"hourglass_full"'
          }, {
            value: '"hourglass_empty"'
          }, {
            value: '"hourglass_disabled"'
          }, {
            value: '"hourglass_bottom"'
          }, {
            value: '"hotel"'
          }, {
            value: '"hot_tub"'
          }, {
            value: '"horizontal_split"'
          }, {
            value: '"horizontal_rule"'
          }, {
            value: '"home_work"'
          }, {
            value: '"home_repair_service"'
          }, {
            value: '"home_filled"'
          }, {
            value: '"history_toggle_off"'
          }, {
            value: '"history_edu"'
          }, {
            value: '"history"'
          }, {
            value: '"highlight_remove"'
          }, {
            value: '"highlight_off"'
          }, {
            value: '"highlight_alt"'
          }, {
            value: '"highlight"'
          }, {
            value: '"high_quality"'
          }, {
            value: '"help_outline"'
          }, {
            value: '"help_center"'
          }, {
            value: '"help"'
          }, {
            value: '"height"'
          }, {
            value: '"hearing_disabled"'
          }, {
            value: '"hearing"'
          }, {
            value: '"healing"'
          }, {
            value: '"headset_off"'
          }, {
            value: '"headset_mic"'
          }, {
            value: '"headset"'
          }, {
            value: '"hdr_weak"'
          }, {
            value: '"hdr_strong"'
          }, {
            value: '"hdr_on"'
          }, {
            value: '"hdr_off"'
          }, {
            value: '"hdr_enhanced_select"'
          }, {
            value: '"hd"'
          }, {
            value: '"hardware"'
          }, {
            value: '"handyman"'
          }, {
            value: '"hail"'
          }, {
            value: '"iso"'
          }, {
            value: '"ios_share"'
          }, {
            value: '"invert_colors_on"'
          }, {
            value: '"invert_colors_off"'
          }, {
            value: '"invert_colors"'
          }, {
            value: '"inventory"'
          }, {
            value: '"integration_instructions"'
          }, {
            value: '"insert_photo"'
          }, {
            value: '"insert_link"'
          }, {
            value: '"insert_invitation"'
          }, {
            value: '"insert_emoticon"'
          }, {
            value: '"insert_drive_file"'
          }, {
            value: '"insert_comment"'
          }, {
            value: '"insert_chart_outlined"'
          }, {
            value: '"insert_chart"'
          }, {
            value: '"info_outline"'
          }, {
            value: '"indeterminate_check_box"'
          }, {
            value: '"inbox"'
          }, {
            value: '"important_devices"'
          }, {
            value: '"import_contacts"'
          }, {
            value: '"imagesearch_roller"'
          }, {
            value: '"image_search"'
          }, {
            value: '"image_not_supported"'
          }, {
            value: '"image_aspect_ratio"'
          }, {
            value: '"icecream"'
          }, {
            value: '"keyboard_arrow_right"'
          }, {
            value: '"keyboard_arrow_left"'
          }, {
            value: '"keyboard_arrow_down"'
          }, {
            value: '"keyboard_backspace"'
          }, {
            value: '"keyboard_arrow_up"'
          }, {
            value: '"keyboard_capslock"'
          }, {
            value: '"keyboard_control"'
          }, {
            value: '"keyboard_return"'
          }, {
            value: '"keyboard_voice"'
          }, {
            value: '"keyboard_hide"'
          }, {
            value: '"keyboard_tab"'
          }, {
            value: '"king_bed"'
          }, {
            value: '"keyboard"'
          }, {
            value: '"kitchen"'
          }, {
            value: '"lunch_dining"'
          }, {
            value: '"luggage"'
          }, {
            value: '"loyalty"'
          }, {
            value: '"low_priority"'
          }, {
            value: '"loupe"'
          }, {
            value: '"loop"'
          }, {
            value: '"looks_two"'
          }, {
            value: '"looks_one"'
          }, {
            value: '"looks_6"'
          }, {
            value: '"looks_5"'
          }, {
            value: '"looks_4"'
          }, {
            value: '"looks_3"'
          }, {
            value: '"looks"'
          }, {
            value: '"logout"'
          }, {
            value: '"login"'
          }, {
            value: '"lock_outline"'
          }, {
            value: '"lock_open"'
          }, {
            value: '"lock_clock"'
          }, {
            value: '"lock"'
          }, {
            value: '"location_searching"'
          }, {
            value: '"location_pin"'
          }, {
            value: '"location_on"'
          }, {
            value: '"location_off"'
          }, {
            value: '"location_history"'
          }, {
            value: '"location_disabled"'
          }, {
            value: '"location_city"'
          }, {
            value: '"local_taxi"'
          }, {
            value: '"local_shipping"'
          }, {
            value: '"local_see"'
          }, {
            value: '"local_restaurant"'
          }, {
            value: '"local_printshop"'
          }, {
            value: '"local_print_shop"'
          }, {
            value: '"local_post_office"'
          }, {
            value: '"local_police"'
          }, {
            value: '"local_play"'
          }, {
            value: '"local_pizza"'
          }, {
            value: '"local_phone"'
          }, {
            value: '"local_pharmacy"'
          }, {
            value: '"local_parking"'
          }, {
            value: '"local_offer"'
          }, {
            value: '"local_movies"'
          }, {
            value: '"local_mall"'
          }, {
            value: '"local_library"'
          }, {
            value: '"local_laundry_service"'
          }, {
            value: '"local_hotel"'
          }, {
            value: '"local_hospital"'
          }, {
            value: '"local_grocery_store"'
          }, {
            value: '"local_gas_station"'
          }, {
            value: '"local_florist"'
          }, {
            value: '"local_fire_department"'
          }, {
            value: '"local_drink"'
          }, {
            value: '"local_dining"'
          }, {
            value: '"local_convenience_store"'
          }, {
            value: '"local_car_wash"'
          }, {
            value: '"local_cafe"'
          }, {
            value: '"local_bar"'
          }, {
            value: '"local_attraction"'
          }, {
            value: '"local_atm"'
          }, {
            value: '"local_airport"'
          }, {
            value: '"local_activity"'
          }, {
            value: '"live_tv"'
          }, {
            value: '"live_help"'
          }, {
            value: '"list_alt"'
          }, {
            value: '"list"'
          }, {
            value: '"liquor"'
          }, {
            value: '"linked_camera"'
          }, {
            value: '"link_off"'
          }, {
            value: '"linear_scale"'
          }, {
            value: '"line_weight"'
          }, {
            value: '"line_style"'
          }, {
            value: '"lightbulb_outline"'
          }, {
            value: '"lightbulb"'
          }, {
            value: '"library_music"'
          }, {
            value: '"library_books"'
          }, {
            value: '"library_add_check"'
          }, {
            value: '"library_add"'
          }, {
            value: '"lens"'
          }, {
            value: '"legend_toggle"'
          }, {
            value: '"leave_bags_at_home"'
          }, {
            value: '"leak_remove"'
          }, {
            value: '"leak_add"'
          }, {
            value: '"leaderboard"'
          }, {
            value: '"layers_clear"'
          }, {
            value: '"layers"'
          }, {
            value: '"launch"'
          }, {
            value: '"last_page"'
          }, {
            value: '"laptop_windows"'
          }, {
            value: '"laptop_mac"'
          }, {
            value: '"laptop_chromebook"'
          }, {
            value: '"laptop"'
          }, {
            value: '"language"'
          }, {
            value: '"landscape"'
          }, {
            value: '"label_outline"'
          }, {
            value: '"label_off"'
          }, {
            value: '"label_important_outline"'
          }, {
            value: '"label_important"'
          }, {
            value: '"my_location"'
          }, {
            value: '"my_library_music"'
          }, {
            value: '"my_library_books"'
          }, {
            value: '"my_library_add"'
          }, {
            value: '"music_video"'
          }, {
            value: '"music_off"'
          }, {
            value: '"music_note"'
          }, {
            value: '"museum"'
          }, {
            value: '"multitrack_audio"'
          }, {
            value: '"multiple_stop"'
          }, {
            value: '"multiline_chart"'
          }, {
            value: '"mp"'
          }, {
            value: '"movie_filter"'
          }, {
            value: '"movie_creation"'
          }, {
            value: '"movie"'
          }, {
            value: '"move_to_inbox"'
          }, {
            value: '"mouse"'
          }, {
            value: '"motorcycle"'
          }, {
            value: '"motion_photos_paused"'
          }, {
            value: '"motion_photos_pause"'
          }, {
            value: '"motion_photos_on"'
          }, {
            value: '"motion_photos_off"'
          }, {
            value: '"more_vert"'
          }, {
            value: '"more_time"'
          }, {
            value: '"more_horiz"'
          }, {
            value: '"more"'
          }, {
            value: '"moped"'
          }, {
            value: '"mood_bad"'
          }, {
            value: '"mood"'
          }, {
            value: '"monochrome_photos"'
          }, {
            value: '"monitor"'
          }, {
            value: '"money_off"'
          }, {
            value: '"money"'
          }, {
            value: '"monetization_on"'
          }, {
            value: '"model_training"'
          }, {
            value: '"mode_edit"'
          }, {
            value: '"mode_comment"'
          }, {
            value: '"mobile_screen_share"'
          }, {
            value: '"mobile_off"'
          }, {
            value: '"mobile_friendly"'
          }, {
            value: '"mms"'
          }, {
            value: '"missed_video_call"'
          }, {
            value: '"miscellaneous_services"'
          }, {
            value: '"minimize"'
          }, {
            value: '"military_tech"'
          }, {
            value: '"microwave"'
          }, {
            value: '"mic_off"'
          }, {
            value: '"mic_none"'
          }, {
            value: '"mic_external_on"'
          }, {
            value: '"mic_external_off"'
          }, {
            value: '"mic"'
          }, {
            value: '"messenger_outline"'
          }, {
            value: '"messenger"'
          }, {
            value: '"merge_type"'
          }, {
            value: '"menu_open"'
          }, {
            value: '"menu_book"'
          }, {
            value: '"memory"'
          }, {
            value: '"meeting_room"'
          }, {
            value: '"medical_services"'
          }, {
            value: '"mediation"'
          }, {
            value: '"maximize"'
          }, {
            value: '"masks"'
          }, {
            value: '"markunread_mailbox"'
          }, {
            value: '"markunread"'
          }, {
            value: '"mark_email_unread"'
          }, {
            value: '"mark_email_read"'
          }, {
            value: '"mark_chat_unread"'
          }, {
            value: '"mark_chat_read"'
          }, {
            value: '"mark_as_unread"'
          }, {
            value: '"margin"'
          }, {
            value: '"maps_ugc"'
          }, {
            value: '"mail_outline"'
          }, {
            value: '"mail"'
          }, {
            value: '"now_widgets"'
          }, {
            value: '"now_wallpaper"'
          }, {
            value: '"notifications_paused"'
          }, {
            value: '"notifications_on"'
          }, {
            value: '"notifications_off"'
          }, {
            value: '"notifications_none"'
          }, {
            value: '"notifications_active"'
          }, {
            value: '"notifications"'
          }, {
            value: '"notification_important"'
          }, {
            value: '"notes"'
          }, {
            value: '"note_add"'
          }, {
            value: '"note"'
          }, {
            value: '"not_started"'
          }, {
            value: '"not_listed_location"'
          }, {
            value: '"not_interested"'
          }, {
            value: '"not_accessible"'
          }, {
            value: '"north_west"'
          }, {
            value: '"north_east"'
          }, {
            value: '"north"'
          }, {
            value: '"no_transfer"'
          }, {
            value: '"no_stroller"'
          }, {
            value: '"no_sim"'
          }, {
            value: '"no_photography"'
          }, {
            value: '"no_meeting_room"'
          }, {
            value: '"no_meals_ouline"'
          }, {
            value: '"no_meals"'
          }, {
            value: '"no_luggage"'
          }, {
            value: '"no_food"'
          }, {
            value: '"no_flash"'
          }, {
            value: '"no_encryption"'
          }, {
            value: '"no_drinks"'
          }, {
            value: '"no_cell"'
          }, {
            value: '"no_backpack"'
          }, {
            value: '"nights_stay"'
          }, {
            value: '"nightlight_round"'
          }, {
            value: '"nightlife"'
          }, {
            value: '"night_shelter"'
          }, {
            value: '"nfc"'
          }, {
            value: '"next_week"'
          }, {
            value: '"next_plan"'
          }, {
            value: '"new_releases"'
          }, {
            value: '"network_wifi"'
          }, {
            value: '"network_locked"'
          }, {
            value: '"network_check"'
          }, {
            value: '"network_cell"'
          }, {
            value: '"near_me_disabled"'
          }, {
            value: '"near_me"'
          }, {
            value: '"navigation"'
          }, {
            value: '"navigate_next"'
          }, {
            value: '"navigate_before"'
          }, {
            value: '"nature_people"'
          }, {
            value: '"nature"'
          }, {
            value: '"nat"'
          }, {
            value: '"outlined_flag"'
          }, {
            value: '"outlet"'
          }, {
            value: '"outgoing_mail"'
          }, {
            value: '"outdoor_grill"'
          }, {
            value: '"outbox"'
          }, {
            value: '"outbond"'
          }, {
            value: '"open_with"'
          }, {
            value: '"open_in_new"'
          }, {
            value: '"open_in_full"'
          }, {
            value: '"open_in_browser"'
          }, {
            value: '"opacity"'
          }, {
            value: '"online_prediction"'
          }, {
            value: '"ondemand_video"'
          }, {
            value: '"offline_share"'
          }, {
            value: '"offline_pin"'
          }, {
            value: '"offline_bolt"'
          }, {
            value: '"push_pin"'
          }, {
            value: '"published_with_changes"'
          }, {
            value: '"publish"'
          }, {
            value: '"public_off"'
          }, {
            value: '"public"'
          }, {
            value: '"psychology"'
          }, {
            value: '"privacy_tip"'
          }, {
            value: '"priority_high"'
          }, {
            value: '"print_disabled"'
          }, {
            value: '"print"'
          }, {
            value: '"preview"'
          }, {
            value: '"present_to_all"'
          }, {
            value: '"pregnant_woman"'
          }, {
            value: '"power_settings_new"'
          }, {
            value: '"power_off"'
          }, {
            value: '"power_input"'
          }, {
            value: '"power"'
          }, {
            value: '"post_add"'
          }, {
            value: '"portrait"'
          }, {
            value: '"portable_wifi_off"'
          }, {
            value: '"pool"'
          }, {
            value: '"polymer"'
          }, {
            value: '"poll"'
          }, {
            value: '"policy"'
          }, {
            value: '"point_of_sale"'
          }, {
            value: '"plus_one"'
          }, {
            value: '"plumbing"'
          }, {
            value: '"playlist_play"'
          }, {
            value: '"playlist_add_check"'
          }, {
            value: '"playlist_add"'
          }, {
            value: '"play_for_work"'
          }, {
            value: '"play_disabled"'
          }, {
            value: '"play_circle_outline"'
          }, {
            value: '"play_circle_filled"'
          }, {
            value: '"play_circle_fill"'
          }, {
            value: '"play_arrow"'
          }, {
            value: '"plagiarism"'
          }, {
            value: '"place"'
          }, {
            value: '"pivot_table_chart"'
          }, {
            value: '"pin_drop"'
          }, {
            value: '"pie_chart_outlined"'
          }, {
            value: '"pie_chart"'
          }, {
            value: '"picture_in_picture_alt"'
          }, {
            value: '"picture_in_picture"'
          }, {
            value: '"picture_as_pdf"'
          }, {
            value: '"photo_size_select_small"'
          }, {
            value: '"photo_size_select_large"'
          }, {
            value: '"photo_size_select_actual"'
          }, {
            value: '"photo_library"'
          }, {
            value: '"photo_filter"'
          }, {
            value: '"photo_camera_front"'
          }, {
            value: '"photo_camera_back"'
          }, {
            value: '"photo_camera"'
          }, {
            value: '"photo_album"'
          }, {
            value: '"photo"'
          }, {
            value: '"phonelink_setup"'
          }, {
            value: '"phonelink_ring"'
          }, {
            value: '"phonelink_off"'
          }, {
            value: '"phonelink_lock"'
          }, {
            value: '"phonelink_erase"'
          }, {
            value: '"phonelink"'
          }, {
            value: '"phone_paused"'
          }, {
            value: '"phone_missed"'
          }, {
            value: '"phone_locked"'
          }, {
            value: '"phone_iphone"'
          }, {
            value: '"phone_in_talk"'
          }, {
            value: '"phone_forwarded"'
          }, {
            value: '"phone_enabled"'
          }, {
            value: '"phone_disabled"'
          }, {
            value: '"phone_callback"'
          }, {
            value: '"phone_bluetooth_speaker"'
          }, {
            value: '"phone_android"'
          }, {
            value: '"phone"'
          }, {
            value: '"pets"'
          }, {
            value: '"pest_control_rodent"'
          }, {
            value: '"pest_control"'
          }, {
            value: '"personal_video"'
          }, {
            value: '"person_search"'
          }, {
            value: '"person_remove_alt_1"'
          }, {
            value: '"person_remove"'
          }, {
            value: '"person_pin_circle"'
          }, {
            value: '"person_pin"'
          }, {
            value: '"person_outline"'
          }, {
            value: '"person_add_disabled"'
          }, {
            value: '"person_add_alt_1"'
          }, {
            value: '"person_add_alt"'
          }, {
            value: '"person_add"'
          }, {
            value: '"person"'
          }, {
            value: '"perm_scan_wifi"'
          }, {
            value: '"perm_phone_msg"'
          }, {
            value: '"perm_media"'
          }, {
            value: '"perm_identity"'
          }, {
            value: '"perm_device_information"'
          }, {
            value: '"perm_device_info"'
          }, {
            value: '"perm_data_setting"'
          }, {
            value: '"perm_contact_calendar"'
          }, {
            value: '"perm_contact_cal"'
          }, {
            value: '"perm_camera_mic"'
          }, {
            value: '"people_outline"'
          }, {
            value: '"people_alt"'
          }, {
            value: '"people"'
          }, {
            value: '"pending_actions"'
          }, {
            value: '"pending"'
          }, {
            value: '"pedal_bike"'
          }, {
            value: '"payment"'
          }, {
            value: '"pause_presentation"'
          }, {
            value: '"pause_circle_outline"'
          }, {
            value: '"pause_circle_filled"'
          }, {
            value: '"pause"'
          }, {
            value: '"party_mode"'
          }, {
            value: '"park"'
          }, {
            value: '"panorama_wide_angle_select"'
          }, {
            value: '"panorama_wide_angle"'
          }, {
            value: '"panorama_vertical_select"'
          }, {
            value: '"panorama_vertical"'
          }, {
            value: '"panorama_photosphere_select"'
          }, {
            value: '"panorama_photosphere"'
          }, {
            value: '"panorama_horizontal_select"'
          }, {
            value: '"panorama_horizontal"'
          }, {
            value: '"panorama_fisheye"'
          }, {
            value: '"panorama_fish_eye"'
          }, {
            value: '"panorama"'
          }, {
            value: '"pan_tool"'
          }, {
            value: '"palette"'
          }, {
            value: '"pageview"'
          }, {
            value: '"pages"'
          }, {
            value: '"padding"'
          }, {
            value: '"quickreply"'
          }, {
            value: '"quick_contacts_mail"'
          }, {
            value: '"quick_contacts_dialer"'
          }, {
            value: '"queue_play_next"'
          }, {
            value: '"queue_music"'
          }, {
            value: '"queue"'
          }, {
            value: '"question_answer"'
          }, {
            value: '"query_builder"'
          }, {
            value: '"qr_code_scanner"'
          }, {
            value: '"qr_code"'
          }, {
            value: '"rv_hookup"'
          }, {
            value: '"run_circle"'
          }, {
            value: '"rule_folder"'
          }, {
            value: '"rule"'
          }, {
            value: '"rtt"'
          }, {
            value: '"rss_feed"'
          }, {
            value: '"rowing"'
          }, {
            value: '"router"'
          }, {
            value: '"rounded_corner"'
          }, {
            value: '"rotate_right"'
          }, {
            value: '"rotate_left"'
          }, {
            value: '"rotate_90_degrees_ccw"'
          }, {
            value: '"room_service"'
          }, {
            value: '"room_preferences"'
          }, {
            value: '"room"'
          }, {
            value: '"roofing"'
          }, {
            value: '"ring_volume"'
          }, {
            value: '"rice_bowl"'
          }, {
            value: '"restore_page"'
          }, {
            value: '"restore_from_trash"'
          }, {
            value: '"restore"'
          }, {
            value: '"restaurant_menu"'
          }, {
            value: '"restaurant"'
          }, {
            value: '"reset_tv"'
          }, {
            value: '"request_quote"'
          }, {
            value: '"request_page"'
          }, {
            value: '"report_problem"'
          }, {
            value: '"report_off"'
          }, {
            value: '"report"'
          }, {
            value: '"reply_all"'
          }, {
            value: '"reply"'
          }, {
            value: '"replay_circle_filled"'
          }, {
            value: '"replay_5"'
          }, {
            value: '"replay_30"'
          }, {
            value: '"replay_10"'
          }, {
            value: '"replay"'
          }, {
            value: '"repeat_one_on"'
          }, {
            value: '"repeat_one"'
          }, {
            value: '"repeat_on"'
          }, {
            value: '"repeat"'
          }, {
            value: '"reorder"'
          }, {
            value: '"remove_shopping_cart"'
          }, {
            value: '"remove_red_eye"'
          }, {
            value: '"remove_moderator"'
          }, {
            value: '"remove_from_queue"'
          }, {
            value: '"remove_done"'
          }, {
            value: '"remove_circle_outline"'
          }, {
            value: '"remove_circle"'
          }, {
            value: '"remove"'
          }, {
            value: '"refresh"'
          }, {
            value: '"reduce_capacity"'
          }, {
            value: '"redo"'
          }, {
            value: '"redeem"'
          }, {
            value: '"record_voice_over"'
          }, {
            value: '"recommend"'
          }, {
            value: '"recent_actors"'
          }, {
            value: '"receipt_long"'
          }, {
            value: '"receipt"'
          }, {
            value: '"read_more"'
          }, {
            value: '"rate_review"'
          }, {
            value: '"ramen_dining"'
          }, {
            value: '"railway_alert"'
          }, {
            value: '"radio_button_unchecked"'
          }, {
            value: '"radio_button_on"'
          }, {
            value: '"radio_button_off"'
          }, {
            value: '"radio_button_checked"'
          }, {
            value: '"radio"'
          }, {
            value: '"system_update_tv"'
          }, {
            value: '"system_update_alt"'
          }, {
            value: '"system_update"'
          }, {
            value: '"sync_problem"'
          }, {
            value: '"sync_disabled"'
          }, {
            value: '"sync_alt"'
          }, {
            value: '"sync"'
          }, {
            value: '"switch_video"'
          }, {
            value: '"switch_right"'
          }, {
            value: '"switch_left"'
          }, {
            value: '"switch_camera"'
          }, {
            value: '"switch_account"'
          }, {
            value: '"swipe"'
          }, {
            value: '"swap_vertical_circle"'
          }, {
            value: '"swap_vert_circle"'
          }, {
            value: '"swap_vert"'
          }, {
            value: '"swap_horizontal_circle"'
          }, {
            value: '"swap_horiz"'
          }, {
            value: '"swap_calls"'
          }, {
            value: '"surround_sound"'
          }, {
            value: '"support_agent"'
          }, {
            value: '"support"'
          }, {
            value: '"supervisor_account"'
          }, {
            value: '"supervised_user_circle"'
          }, {
            value: '"superscript"'
          }, {
            value: '"subway"'
          }, {
            value: '"subtitles_off"'
          }, {
            value: '"subtitles"'
          }, {
            value: '"subscriptions"'
          }, {
            value: '"subscript"'
          }, {
            value: '"subject"'
          }, {
            value: '"subdirectory_arrow_right"'
          }, {
            value: '"subdirectory_arrow_left"'
          }, {
            value: '"stroller"'
          }, {
            value: '"strikethrough_s"'
          }, {
            value: '"streetview"'
          }, {
            value: '"stream"'
          }, {
            value: '"straighten"'
          }, {
            value: '"storefront"'
          }, {
            value: '"store_mall_directory"'
          }, {
            value: '"store"'
          }, {
            value: '"storage"'
          }, {
            value: '"stop_screen_share"'
          }, {
            value: '"stop_circle"'
          }, {
            value: '"sticky_note_2"'
          }, {
            value: '"stay_primary_portrait"'
          }, {
            value: '"stay_primary_landscape"'
          }, {
            value: '"stay_current_portrait"'
          }, {
            value: '"stay_current_landscape"'
          }, {
            value: '"stars"'
          }, {
            value: '"star_rate"'
          }, {
            value: '"star_outline"'
          }, {
            value: '"star_half"'
          }, {
            value: '"star_border"'
          }, {
            value: '"star"'
          }, {
            value: '"stairs"'
          }, {
            value: '"stacked_line_chart"'
          }, {
            value: '"stacked_bar_chart"'
          }, {
            value: '"square_foot"'
          }, {
            value: '"sports_volleyball"'
          }, {
            value: '"sports_tennis"'
          }, {
            value: '"sports_soccer"'
          }, {
            value: '"sports_rugby"'
          }, {
            value: '"sports_motorsports"'
          }, {
            value: '"sports_mma"'
          }, {
            value: '"sports_kabaddi"'
          }, {
            value: '"sports_hockey"'
          }, {
            value: '"sports_handball"'
          }, {
            value: '"sports_golf"'
          }, {
            value: '"sports_football"'
          }, {
            value: '"sports_esports"'
          }, {
            value: '"sports_cricket"'
          }, {
            value: '"sports_basketball"'
          }, {
            value: '"sports_baseball"'
          }, {
            value: '"sports_bar"'
          }, {
            value: '"sports"'
          }, {
            value: '"spellcheck"'
          }, {
            value: '"speed"'
          }, {
            value: '"speaker_phone"'
          }, {
            value: '"speaker_notes_off"'
          }, {
            value: '"speaker_notes"'
          }, {
            value: '"speaker_group"'
          }, {
            value: '"speaker"'
          }, {
            value: '"space_bar"'
          }, {
            value: '"spa"'
          }, {
            value: '"south_west"'
          }, {
            value: '"south_east"'
          }, {
            value: '"south"'
          }, {
            value: '"sort_by_alpha"'
          }, {
            value: '"sort"'
          }, {
            value: '"soap"'
          }, {
            value: '"snooze"'
          }, {
            value: '"snippet_folder"'
          }, {
            value: '"sms_failed"'
          }, {
            value: '"sms"'
          }, {
            value: '"smoking_rooms"'
          }, {
            value: '"smoke_free"'
          }, {
            value: '"smartphone"'
          }, {
            value: '"smart_button"'
          }, {
            value: '"slow_motion_video"'
          }, {
            value: '"slideshow"'
          }, {
            value: '"skip_previous"'
          }, {
            value: '"skip_next"'
          }, {
            value: '"single_bed"'
          }, {
            value: '"sim_card_alert"'
          }, {
            value: '"sim_card"'
          }, {
            value: '"signal_wifi_off"'
          }, {
            value: '"signal_wifi_4_bar_lock"'
          }, {
            value: '"signal_wifi_4_bar"'
          }, {
            value: '"signal_wifi_0_bar"'
          }, {
            value: '"signal_cellular_off"'
          }, {
            value: '"signal_cellular_null"'
          }, {
            value: '"signal_cellular_no_sim"'
          }, {
            value: '"signal_cellular_connected_no_internet_4_bar"'
          }, {
            value: '"signal_cellular_alt"'
          }, {
            value: '"signal_cellular_4_bar"'
          }, {
            value: '"signal_cellular_0_bar"'
          }, {
            value: '"sick"'
          }, {
            value: '"shutter_speed"'
          }, {
            value: '"shuffle_on"'
          }, {
            value: '"shuffle"'
          }, {
            value: '"show_chart"'
          }, {
            value: '"short_text"'
          }, {
            value: '"shopping_cart"'
          }, {
            value: '"shopping_basket"'
          }, {
            value: '"shopping_bag"'
          }, {
            value: '"shop_two"'
          }, {
            value: '"shop"'
          }, {
            value: '"shield"'
          }, {
            value: '"share"'
          }, {
            value: '"settings_voice"'
          }, {
            value: '"settings_system_daydream"'
          }, {
            value: '"settings_remote"'
          }, {
            value: '"settings_power"'
          }, {
            value: '"settings_phone"'
          }, {
            value: '"settings_overscan"'
          }, {
            value: '"settings_input_svideo"'
          }, {
            value: '"settings_input_hdmi"'
          }, {
            value: '"settings_input_composite"'
          }, {
            value: '"settings_input_component"'
          }, {
            value: '"settings_input_antenna"'
          }, {
            value: '"settings_ethernet"'
          }, {
            value: '"settings_display"'
          }, {
            value: '"settings_cell"'
          }, {
            value: '"settings_brightness"'
          }, {
            value: '"settings_bluetooth"'
          }, {
            value: '"settings_backup_restore"'
          }, {
            value: '"settings_applications"'
          }, {
            value: '"settings"'
          }, {
            value: '"set_meal"'
          }, {
            value: '"sentiment_very_satisfied"'
          }, {
            value: '"sentiment_very_dissatisfied"'
          }, {
            value: '"sentiment_satisfied_alt"'
          }, {
            value: '"sentiment_satisfied"'
          }, {
            value: '"sentiment_neutral"'
          }, {
            value: '"sentiment_dissatisfied"'
          }, {
            value: '"sensor_window"'
          }, {
            value: '"sensor_door"'
          }, {
            value: '"send_to_mobile"'
          }, {
            value: '"send_and_archive"'
          }, {
            value: '"send"'
          }, {
            value: '"self_improvement"'
          }, {
            value: '"select_all"'
          }, {
            value: '"segment"'
          }, {
            value: '"search_off"'
          }, {
            value: '"sd_storage"'
          }, {
            value: '"sd_card"'
          }, {
            value: '"sd"'
          }, {
            value: '"screen_share"'
          }, {
            value: '"screen_search_desktop"'
          }, {
            value: '"screen_rotation"'
          }, {
            value: '"screen_lock_rotation"'
          }, {
            value: '"screen_lock_portrait"'
          }, {
            value: '"screen_lock_landscape"'
          }, {
            value: '"score"'
          }, {
            value: '"science"'
          }, {
            value: '"school"'
          }, {
            value: '"schedule_send"'
          }, {
            value: '"schedule"'
          }, {
            value: '"scatter_plot"'
          }, {
            value: '"scanner"'
          }, {
            value: '"saved_search"'
          }, {
            value: '"save_alt"'
          }, {
            value: '"save"'
          }, {
            value: '"satellite"'
          }, {
            value: '"sanitizer"'
          }, {
            value: '"two_wheeler"'
          }, {
            value: '"tv_off"'
          }, {
            value: '"tv"'
          }, {
            value: '"turned_in_not"'
          }, {
            value: '"turned_in"'
          }, {
            value: '"tune"'
          }, {
            value: '"tty"'
          }, {
            value: '"trip_origin"'
          }, {
            value: '"trending_up"'
          }, {
            value: '"trending_neutral"'
          }, {
            value: '"trending_flat"'
          }, {
            value: '"trending_down"'
          }, {
            value: '"transit_enterexit"'
          }, {
            value: '"transform"'
          }, {
            value: '"transfer_within_a_station"'
          }, {
            value: '"tram"'
          }, {
            value: '"train"'
          }, {
            value: '"traffic"'
          }, {
            value: '"track_changes"'
          }, {
            value: '"toys"'
          }, {
            value: '"tour"'
          }, {
            value: '"touch_app"'
          }, {
            value: '"topic"'
          }, {
            value: '"tonality"'
          }, {
            value: '"toll"'
          }, {
            value: '"toggle_on"'
          }, {
            value: '"toggle_off"'
          }, {
            value: '"today"'
          }, {
            value: '"toc"'
          }, {
            value: '"timer_off"'
          }, {
            value: '"timer_3"'
          }, {
            value: '"timer_10"'
          }, {
            value: '"timer"'
          }, {
            value: '"timeline"'
          }, {
            value: '"timelapse"'
          }, {
            value: '"time_to_leave"'
          }, {
            value: '"thumbs_up_down"'
          }, {
            value: '"thumb_up_off_alt"'
          }, {
            value: '"thumb_up_alt"'
          }, {
            value: '"thumb_up"'
          }, {
            value: '"thumb_down_off_alt"'
          }, {
            value: '"thumb_down_alt"'
          }, {
            value: '"thumb_down"'
          }, {
            value: '"theaters"'
          }, {
            value: '"theater_comedy"'
          }, {
            value: '"texture"'
          }, {
            value: '"textsms"'
          }, {
            value: '"text_snippet"'
          }, {
            value: '"text_rotation_none"'
          }, {
            value: '"text_rotation_down"'
          }, {
            value: '"text_rotation_angleup"'
          }, {
            value: '"text_rotation_angledown"'
          }, {
            value: '"text_rotate_vertical"'
          }, {
            value: '"text_rotate_up"'
          }, {
            value: '"text_format"'
          }, {
            value: '"text_fields"'
          }, {
            value: '"terrain"'
          }, {
            value: '"taxi_alert"'
          }, {
            value: '"tapas"'
          }, {
            value: '"tap_and_play"'
          }, {
            value: '"takeout_dining"'
          }, {
            value: '"tag_faces"'
          }, {
            value: '"tag"'
          }, {
            value: '"tablet_mac"'
          }, {
            value: '"tablet_android"'
          }, {
            value: '"tablet"'
          }, {
            value: '"table_view"'
          }, {
            value: '"table_rows"'
          }, {
            value: '"table_chart"'
          }, {
            value: '"tab_unselected"'
          }, {
            value: '"tab"'
          }, {
            value: '"usb"'
          }, {
            value: '"upload_file"'
          }, {
            value: '"upgrade"'
          }, {
            value: '"update"'
          }, {
            value: '"unsubscribe"'
          }, {
            value: '"unpublished"'
          }, {
            value: '"unfold_more"'
          }, {
            value: '"unfold_less"'
          }, {
            value: '"undo"'
          }, {
            value: '"unarchive"'
          }, {
            value: '"umbrella"'
          }, {
            value: '"vpn_lock"'
          }, {
            value: '"vpn_key"'
          }, {
            value: '"volunteer_activism"'
          }, {
            value: '"volume_up"'
          }, {
            value: '"volume_off"'
          }, {
            value: '"volume_mute"'
          }, {
            value: '"volume_down"'
          }, {
            value: '"voicemail"'
          }, {
            value: '"voice_over_off"'
          }, {
            value: '"voice_chat"'
          }, {
            value: '"visibility_off"'
          }, {
            value: '"visibility"'
          }, {
            value: '"vignette"'
          }, {
            value: '"view_week"'
          }, {
            value: '"view_stream"'
          }, {
            value: '"view_sidebar"'
          }, {
            value: '"view_quilt"'
          }, {
            value: '"view_module"'
          }, {
            value: '"view_list"'
          }, {
            value: '"view_in_ar"'
          }, {
            value: '"view_headline"'
          }, {
            value: '"view_day"'
          }, {
            value: '"view_compact"'
          }, {
            value: '"view_comfy"'
          }, {
            value: '"view_comfortable"'
          }, {
            value: '"view_column"'
          }, {
            value: '"view_carousel"'
          }, {
            value: '"view_array"'
          }, {
            value: '"view_agenda"'
          }, {
            value: '"videogame_asset"'
          }, {
            value: '"videocam_off"'
          }, {
            value: '"videocam"'
          }, {
            value: '"video_settings"'
          }, {
            value: '"video_library"'
          }, {
            value: '"video_label"'
          }, {
            value: '"video_collection"'
          }, {
            value: '"video_call"'
          }, {
            value: '"vibration"'
          }, {
            value: '"vertical_split"'
          }, {
            value: '"vertical_align_top"'
          }, {
            value: '"vertical_align_center"'
          }, {
            value: '"vertical_align_bottom"'
          }, {
            value: '"verified_user"'
          }, {
            value: '"verified"'
          }, {
            value: '"wysiwyg"'
          }, {
            value: '"wrong_location"'
          }, {
            value: '"wrap_text"'
          }, {
            value: '"workspaces_outline"'
          }, {
            value: '"workspaces_filled"'
          }, {
            value: '"work_outline"'
          }, {
            value: '"work_off"'
          }, {
            value: '"work"'
          }, {
            value: '"wine_bar"'
          }, {
            value: '"wifi_tethering"'
          }, {
            value: '"wifi_protected_setup"'
          }, {
            value: '"wifi_off"'
          }, {
            value: '"wifi_lock"'
          }, {
            value: '"wifi_calling"'
          }, {
            value: '"wifi"'
          }, {
            value: '"widgets"'
          }, {
            value: '"where_to_vote"'
          }, {
            value: '"wheelchair_pickup"'
          }, {
            value: '"whatshot"'
          }, {
            value: '"west"'
          }, {
            value: '"weekend"'
          }, {
            value: '"web_asset"'
          }, {
            value: '"web"'
          }, {
            value: '"wc"'
          }, {
            value: '"wb_twighlight"'
          }, {
            value: '"wb_sunny"'
          }, {
            value: '"wb_shade"'
          }, {
            value: '"wb_iridescent"'
          }, {
            value: '"wb_incandescent"'
          }, {
            value: '"wb_cloudy"'
          }, {
            value: '"wb_auto"'
          }, {
            value: '"waves"'
          }, {
            value: '"waterfall_chart"'
          }, {
            value: '"water_damage"'
          }, {
            value: '"watch_later"'
          }, {
            value: '"watch"'
          }, {
            value: '"wash"'
          }, {
            value: '"wallpaper"'
          }, {
            value: '"wallet_travel"'
          }, {
            value: '"wallet_membership"'
          }, {
            value: '"wallet_giftcard"'
          }, {
            value: '"youtube_searched_for"'
          }, {
            value: '"zoom_out_map"'
          }, {
            value: '"zoom_out"'
          }, {
            value: '"zoom_in"'
          }, {
            value: '"do_disturb_alt"'
          }, {
            value: '"do_disturb_off"'
          }, {
            value: '"do_disturb_on"'
          }, {
            value: '"download_done"'
          }, {
            value: '"do_disturb"'
          }, {
            value: '"download"'
          }, {
            value: '"report_gmailerrorred"'
          }, {
            value: '"thermostat"'
          }, {
            value: '"attribution"'
          }, {
            value: '"no_encryption_gmailerrorred"'
          }, {
            value: '"face_unlock"'
          }, {
            value: '"precision_manufacturing"'
          }, {
            value: '"pie_chart_outline"'
          }, {
            value: '"paste"'
          }, {
            value: '"copy"'
          }, {
            value: '"cut"'
          }, {
            value: '"upload"'
          }, {
            value: '"star_border_purple500"'
          }, {
            value: '"star_purple500"'
          }, {
            value: '"sd_card_alert"'
          }, {
            value: '"money_off_csred"'
          }, {
            value: '"mode"'
          }, {
            value: '"warning_amber"'
          }]
        }
      },
      size: {
        defaultValue: null,
        description: '',
        name: 'size',
        required: false,
        type: {
          name: 'enum',
          raw: '"small" | "medium"',
          value: [{
            value: '"small"'
          }, {
            value: '"medium"'
          }]
        }
      },
      tooltip: {
        defaultValue: {
          value: ''
        },
        description: '',
        name: 'tooltip',
        required: false,
        type: {
          name: 'string'
        }
      },
      disabled: {
        defaultValue: null,
        description: 'If `true`, the button will be disabled.\n' + 'If `true`, the base button will be disabled.',
        name: 'disabled',
        required: false,
        type: {
          name: 'boolean'
        }
      },
      variant: {
        defaultValue: {
          value: 'contained'
        },
        description: 'The variant to use.',
        name: 'variant',
        required: false,
        type: {
          name: 'enum',
          raw: '"outlined" | "text" | "contained"',
          value: [{
            value: '"outlined"'
          }, {
            value: '"text"'
          }, {
            value: '"contained"'
          }]
        }
      },
      type: {
        defaultValue: null,
        description: '',
        name: 'type',
        required: false,
        type: {
          name: 'enum',
          raw: '"button" | "submit" | "reset"',
          value: [{
            value: '"button"'
          }, {
            value: '"submit"'
          }, {
            value: '"reset"'
          }]
        }
      },
      href: {
        defaultValue: null,
        description: 'The URL to link to when the button is clicked.\n' + 'If defined, an `a` element will be used as the root node.',
        name: 'href',
        required: false,
        type: {
          name: 'string'
        }
      },
      buttonRef: {
        defaultValue: null,
        description: '@ignore Use that prop to pass a ref to the native button component.\n' + '@deprecated Use `ref` instead.',
        name: 'buttonRef',
        required: false,
        type: {
          name: 'Ref<unknown>'
        }
      }
    }
  };
} catch (e) {}
//# sourceMappingURL=index.js.map